import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Portal } from '@mui/material';
import {
  Box,
  LinearProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Popover,
  IconButton,
  Chip,
  Card,
  Grid,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Menu,
  ListItemText,
  Divider,
  Alert,
  Snackbar,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCalculatorData } from '../../context/CalculatorDataContext';
import { useGlobalCalculatorSettings } from '../../context/GlobalCalculatorSettingsContext';
import { useProfile } from '../../context/ProfileContext';
import { useProfileManagerLauncher } from '../../context/ProfileManagerLauncherContext';
import { calculateAssessableIncome } from '../../utils/haircutFormulas';
import {
  SHARED_CALCULATOR_EXAMPLES,
  buildLoadedProfileHistoryLabel,
  useSharedProfileHistory,
  type SharedCalculatorProfile,
  type SharedCalculatorPropertyType,
  type SharedCalculatorHistoryEntry,
} from '../../hooks/useSharedProfileHistory';
import {
  calculateBsd,
  getBsdBreakdown,
  getAbsdRate,
  getJointAbsdRate,
  getEffectiveCitizenshipForAbsd,
  CITIZENSHIP_LABELS,
  type CitizenshipType,
  type PropertyCount,
} from '../../../utils/stampDutyRates';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import SchoolIcon from '@mui/icons-material/School';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PersonIcon from '@mui/icons-material/Person';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import { NumericFormat } from 'react-number-format';
import type { SourceInfo } from 'react-number-format';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  getPurchasePlannerAutoFitZoom,
  getPurchasePlannerFocusedFieldScrollTop,
  getPurchasePlannerResponsiveWidths,
  getPurchasePlannerVisibleTableMaxHeight,
} from './PurchasePlanner.layout';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import {
  exportPurchasePlannerToPDF,
  type PurchasePlannerPdfSection,
  type ProfileRowData,
} from '../../utils/purchasePlannerPdfExport';
import { logPdfExport } from '../../services/analytics';

interface TimelineData {
  date: string;
  age: number;
  monthlySalary: number;
  cashSavingsMonthly: number;
  cpfOAMonthly: number;
  cpfOAAccumulated: number;
  cashSavingsAccumulated: number;
  cpfRefundFromSales: number;
  cashProceedsFromSale: number;
  totalCapital: number;
  targetPurchasePrice: number;
  maxPurchasePrice: number;
  /** Purchase price used for stamp duty, downpayments, and outlay (target, income max, or income max + funds). */
  purchasePriceForCalculation: number;
  /** Income-based max loan (TDSR/MSR); tooltip “capacity”. */
  maxLoan: number;
  /** min(maxLoan, purchasePrice × LTV) — loan amount at the row’s purchase price. */
  loanAtPurchasePrice: number;
  loanTenure: number;
  loanToValueRatio: number;
  downpayment5Cash: number;
  remainingDownpayment: number;
  /** Buyer's Stamp Duty (BSD) only — ABSD is tracked separately in `absd`. */
  stampDuty: number;
  /** Additional Buyer's Stamp Duty (ABSD), derived from buyer profile(s). */
  absd: number;
  totalOutlay: number;
  difference: number;
  affordableToday: number;
}

interface Profile {
  id: string;
  currentAge: number;
  monthlySalary: number;
  monthlyDebts: number;
  cashSavingsMonthly: number;
  cpfOAMonthly: number;
  currentCPFOA: number;
  currentCashSavings: number;
  selfEmployedInd?: number;
}

interface Settings {
  salaryIncrement: number;
  absdEnabled: boolean;
  absdRate: number;
}

// CPF Ordinary Account interest rate: 2.5% per annum (statutory floor).
// Interest is applied to the accumulated OA balance each year in the timeline projection.
// Source: https://www.cpf.gov.sg/member/growing-your-savings/earning-higher-returns/earning-attractive-interest
const CPF_OA_INTEREST_RATE = 0.025;

// Calculate CPF OA Monthly contribution based on salary and age.
// Updated for CPF policy effective 1 January 2026:
// - Ordinary Wage ceiling increased to $8,000
// - Total CPF rates increased for ages above 55 to 65
const calculateCPFOAMonthly = (salary: number, age: number): number => {
  // CPF contribution rate: Total (Employee + Employer) up to Ordinary Wage ceiling of $8,000
  const ordinaryWageCeiling = 8000;
  const contributableSalary = Math.min(salary, ordinaryWageCeiling);
  
  // Total CPF contribution rate (Employee + Employer) based on age
  let totalCPFContributionRate = 0.37; // Default for 55 & below
  if (age <= 55) {
    totalCPFContributionRate = 0.37; // Employee 20% + Employer 17%
  } else if (age <= 60) {
    totalCPFContributionRate = 0.34; // Employee 18% + Employer 16%
  } else if (age <= 65) {
    totalCPFContributionRate = 0.25; // Employee 12.5% + Employer 12.5%
  } else if (age <= 70) {
    totalCPFContributionRate = 0.165; // Employee 7.5% + Employer 9%
  } else {
    totalCPFContributionRate = 0.125; // Employee 5% + Employer 7.5%
  }
  
  const totalCPFContribution = contributableSalary * totalCPFContributionRate;
  
  // OA allocation ratio based on age
  // Source: CPF Allocation Rates from 1 January 2026
  // - 35 & below: 62.17%
  // - Above 35-45: 56.77%
  // - Above 45-50: 51.36%
  // - Above 50-55: 40.55%
  // - Above 55-60: 35.30%
  // - Above 60-65: 14.00%
  // - Above 65-70: 6.07%
  // - Above 70: 8.00%
  let oaRatio = 0.6217; // Default for 35 & below
  if (age <= 35) {
    oaRatio = 0.6217;
  } else if (age <= 45) {
    oaRatio = 0.5677;
  } else if (age <= 50) {
    oaRatio = 0.5136;
  } else if (age <= 55) {
    oaRatio = 0.4055;
  } else if (age <= 60) {
    oaRatio = 0.353;
  } else if (age <= 65) {
    oaRatio = 0.14;
  } else if (age <= 70) {
    oaRatio = 0.0607;
  } else {
    oaRatio = 0.08; // Above 70
  }
  
  return Math.round(totalCPFContribution * oaRatio);
};

const createDefaultPlannerProfile = (): Profile => {
  const monthlyIncome = 5000;
  return {
    id: '1',
    currentAge: 30,
    monthlySalary: calculateAssessableIncome(String(monthlyIncome), '0'),
    monthlyDebts: 0,
    cashSavingsMonthly: 0,
    cpfOAMonthly: calculateCPFOAMonthly(monthlyIncome, 30),
    currentCPFOA: 0,
    currentCashSavings: 0,
  };
};

// Sum of interest-earning months across a year's 12 monthly contributions.
// CPF pays interest on a contribution only from the month AFTER it is received, so the
// contributions earn 11 + 10 + ... + 1 + 0 = 66 month-equivalents of interest in the year.
const CPF_CONTRIBUTION_INTEREST_MONTHS = 66;

// Grow a CPF OA balance by one year, mirroring CPF's interest method:
// interest is computed monthly on the lowest balance and credited once at year-end (compounded annually).
//   - The opening balance is present all 12 months, so it earns a full year of interest.
//   - Each monthly contribution earns interest only from the month after it is received (see constant above).
// Source: https://www.cpf.gov.sg/service/article/how-is-my-cpf-interest-computed-and-credited-into-my-accounts
const growCpfOaOneYear = (openingBalance: number, monthlyContribution: number): number => {
  const annualContribution = monthlyContribution * 12;
  const openingBalanceInterest = openingBalance * CPF_OA_INTEREST_RATE;
  const contributionInterest =
    monthlyContribution * (CPF_OA_INTEREST_RATE / 12) * CPF_CONTRIBUTION_INTEREST_MONTHS;
  return openingBalance + annualContribution + openingBalanceInterest + contributionInterest;
};

/** Profile DB PropertiesOwned (1 / 2 / 3, or 0 legacy) → ABSD property band */
const propertiesOwnedToPropertyCount = (propertiesOwned: number | null | undefined): PropertyCount => {
  if (propertiesOwned === 2) return '2nd';
  if (propertiesOwned === 3) return '3rd+';
  return '1st';
};

/** Display label for an ABSD property band. */
const propertyCountLabel = (pc: PropertyCount): string => (pc === '3rd+' ? '≥3rd' : pc) + ' Property';

interface HeaderInfoTableCellProps {
  children: React.ReactNode;
  infoContent: React.ReactNode;
  sx?: object;
  /** For Purchase Planner tutorial “Show on page” highlights */
  'data-tutorial'?: string;
}

interface InfoPopoverState {
  trigger: HTMLElement;
  /** Visual viewport coordinates captured from the click/keyboard event. */
  clientX: number;
  clientY: number;
}

const buildInfoPopoverState = (
  trigger: HTMLElement,
  clientX: number,
  clientY: number
): InfoPopoverState => ({ trigger, clientX, clientY });

/** Fallback for keyboard events — uses getBoundingClientRect which is accurate when zoom = 1. */
const buildInfoPopoverStateFromElement = (trigger: HTMLElement): InfoPopoverState => {
  const rect = trigger.getBoundingClientRect();
  return { trigger, clientX: rect.left + rect.width / 2, clientY: rect.bottom };
};

interface InfoPopoverProps {
  open: boolean;
  state: InfoPopoverState | null;
  onClose: () => void;
  maxWidth: number | { xs: string; sm: number };
  closeLabel: string;
  /** Align popover under the anchor: left edge (narrow icon) or centered (wider triggers). */
  anchorAlign?: 'left' | 'center';
  children: React.ReactNode;
}

const InfoPopover: React.FC<InfoPopoverProps> = ({
  open,
  state,
  onClose,
  maxWidth,
  closeLabel,
  anchorAlign = 'center',
  children,
}) => {
  // anchorPosition uses raw viewport coordinates (clientX/Y) — immune to CSS zoom on the table.
  const anchorPosition = state
    ? { top: state.clientY + 8, left: state.clientX }
    : undefined;

  // When anchorReference="anchorPosition", MUI ignores anchorOrigin.
  // transformOrigin controls which edge of the popup aligns with the anchor point.
  const transformOrigin =
    anchorAlign === 'left'
      ? ({ vertical: 'top', horizontal: 'left' } as const)
      : ({ vertical: 'top', horizontal: 'center' } as const);

  return (
    <Popover
      open={open && Boolean(state)}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      transformOrigin={transformOrigin}
      marginThreshold={8}
      onClose={onClose}
      disableRestoreFocus
      BackdropProps={{ invisible: false }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            maxWidth,
            mt: 0.5,
          },
        },
      }}
    >
      <Box
        sx={{ pr: 3 }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {children}
      </Box>
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        sx={{ position: 'absolute', top: 8, right: 8 }}
        aria-label={closeLabel}
      >
        <Typography variant="body2">✕</Typography>
      </IconButton>
    </Popover>
  );
};

const HeaderInfoTableCell: React.FC<HeaderInfoTableCellProps> = ({ children, infoContent, sx, 'data-tutorial': dataTutorial }) => {
  const [popoverState, setPopoverState] = useState<InfoPopoverState | null>(null);

  const handleToggle = (next: InfoPopoverState) => {
    setPopoverState((currentState) =>
      currentState?.trigger === next.trigger ? null : next
    );
  };

  const handleClose = () => {
    setPopoverState(null);
  };

  return (
    <TableCell
      sx={{
        fontWeight: 600,
        userSelect: 'none',
        ...sx,
      }}
      role="columnheader"
      {...(dataTutorial ? { 'data-tutorial': dataTutorial } : {})}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          gap: 0.5,
        }}
      >
        {/* Block wrapper: <br /> in labels must not sit in a flex row (breaks line boxes under zoom). */}
        <Box
          component="div"
          sx={{
            width: '100%',
            minWidth: 0,
            textAlign: 'center',
            lineHeight: 1.55,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {children}
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Column information"
          aria-haspopup="dialog"
          aria-expanded={Boolean(popoverState)}
          onClick={(event) => {
            event.stopPropagation();
            handleToggle(buildInfoPopoverState(event.currentTarget, event.clientX, event.clientY));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              handleToggle(buildInfoPopoverStateFromElement(event.currentTarget as HTMLElement));
            }
          }}
          sx={{
            border: 'none',
            background: 'none',
            // Generous padding so the touch target meets the 44×44 pt Apple guideline
            padding: '10px',
            margin: '-10px',
            cursor: 'pointer',
            color: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'center',
            flexShrink: 0,
            minWidth: 44,
            minHeight: 44,
            borderRadius: 0.5,
            '&:hover': { backgroundColor: 'action.hover' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
          }}
        >
          <InfoIcon sx={{ fontSize: 11, display: 'block' }} />
        </Box>
      </Box>
      <InfoPopover
        open={Boolean(popoverState)}
        state={popoverState}
        onClose={handleClose}
        maxWidth={{ xs: 'calc(100vw - 32px)', sm: 420 }}
        closeLabel="Close column info"
        anchorAlign="left"
      >
        {infoContent}
      </InfoPopover>
    </TableCell>
  );
};

interface InfoValueTriggerProps {
  children: React.ReactNode;
  infoContent: React.ReactNode;
}

const InfoValueTrigger: React.FC<InfoValueTriggerProps> = ({ children, infoContent }) => {
  const [popoverState, setPopoverState] = useState<InfoPopoverState | null>(null);

  const handleToggle = (next: InfoPopoverState) => {
    setPopoverState((currentState) =>
      currentState?.trigger === next.trigger ? null : next
    );
  };

  const handleClose = () => {
    setPopoverState(null);
  };

  return (
    <>
      <Box
        sx={{ display: 'inline-block', cursor: 'pointer', width: '100%' }}
        onClick={(event) => {
          event.stopPropagation();
          handleToggle(buildInfoPopoverState(event.currentTarget, event.clientX, event.clientY));
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            handleToggle(buildInfoPopoverStateFromElement(event.currentTarget as HTMLElement));
          }
        }}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={Boolean(popoverState)}
      >
        {children}
      </Box>
      <InfoPopover
        open={Boolean(popoverState)}
        state={popoverState}
        onClose={handleClose}
        maxWidth={{ xs: 'calc(100vw - 32px)', sm: 460 }}
        closeLabel="Close value info"
        anchorAlign="center"
      >
        {infoContent}
      </InfoPopover>
    </>
  );
};

interface InfoInlineTriggerProps {
  children: React.ReactNode;
  infoContent: React.ReactNode;
  iconSx?: object;
  layout?: 'stacked' | 'inline';
}

const InfoInlineTrigger: React.FC<InfoInlineTriggerProps> = ({
  children,
  infoContent,
  iconSx,
  layout = 'stacked',
}) => {
  const [popoverState, setPopoverState] = useState<InfoPopoverState | null>(null);

  const handleToggle = (next: InfoPopoverState) => {
    setPopoverState((currentState) =>
      currentState?.trigger === next.trigger ? null : next
    );
  };

  const handleClose = () => {
    setPopoverState(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: layout === 'inline' ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: layout === 'inline' ? 0.5 : 0,
          lineHeight: 1,
        }}
      >
        {children}
        <Box
          component="button"
          type="button"
          aria-label="Show information"
          aria-haspopup="dialog"
          aria-expanded={Boolean(popoverState)}
          onClick={(event) => {
            event.stopPropagation();
            handleToggle(buildInfoPopoverState(event.currentTarget, event.clientX, event.clientY));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              handleToggle(buildInfoPopoverStateFromElement(event.currentTarget as HTMLElement));
            }
          }}
          sx={{
            border: 'none',
            background: 'none',
            // Stacked layout keeps the large touch target; inline layout stays visually centered.
            padding: layout === 'inline' ? '4px' : '10px',
            margin: layout === 'inline' ? 0 : '-10px',
            marginTop: 0,
            cursor: 'pointer',
            color: 'inherit',
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: layout === 'inline' ? 20 : 44,
            height: layout === 'inline' ? 20 : 44,
            minWidth: layout === 'inline' ? 20 : 44,
            minHeight: layout === 'inline' ? 20 : 44,
            borderRadius: 0.5,
            alignSelf: 'center',
            '&:hover': { backgroundColor: 'action.hover' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
          }}
        >
          <InfoIcon sx={{ fontSize: layout === 'inline' ? 13 : 11, display: 'block', ...iconSx }} />
        </Box>
      </Box>
      <InfoPopover
        open={Boolean(popoverState)}
        state={popoverState}
        onClose={handleClose}
        maxWidth={{ xs: 'calc(100vw - 32px)', sm: 420 }}
        closeLabel="Close info"
        anchorAlign="left"
      >
        {infoContent}
      </InfoPopover>
    </>
  );
};

/** Standalone info icon button — tap anywhere to open a popover. No hover required (iPad-safe). */
interface InfoIconButtonProps {
  children: React.ReactNode;
  iconSx?: object;
  sx?: object;
}
const InfoIconButton: React.FC<InfoIconButtonProps> = ({ children, iconSx, sx }) => {
  const [popoverState, setPopoverState] = useState<InfoPopoverState | null>(null);

  const handleToggle = (next: InfoPopoverState) => {
    setPopoverState((currentState) =>
      currentState?.trigger === next.trigger ? null : next
    );
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Show information"
        aria-haspopup="dialog"
        aria-expanded={Boolean(popoverState)}
        onClick={(event) => {
          event.stopPropagation();
          handleToggle(buildInfoPopoverState(event.currentTarget, event.clientX, event.clientY));
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            handleToggle(buildInfoPopoverStateFromElement(event.currentTarget as HTMLElement));
          }
        }}
        sx={{
          border: 'none',
          background: 'none',
          padding: '10px',
          margin: '-10px',
          cursor: 'pointer',
          color: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 44,
          minHeight: 44,
          verticalAlign: 'middle',
          borderRadius: 0.5,
          '&:hover': { backgroundColor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
          ...sx,
        }}
      >
        <InfoIcon sx={{ fontSize: 14, display: 'block', ...iconSx }} />
      </Box>
      <InfoPopover
        open={Boolean(popoverState)}
        state={popoverState}
        onClose={() => setPopoverState(null)}
        maxWidth={{ xs: 'calc(100vw - 32px)', sm: 460 }}
        closeLabel="Close info"
        anchorAlign="left"
      >
        {children}
      </InfoPopover>
    </>
  );
};

const PurchasePlanner: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesLaptopWidth = useMediaQuery(theme.breakpoints.between('md', 'xl'));
  const hasFinePointer = useMediaQuery('(hover: hover) and (pointer: fine)');
  const isLaptopViewport = matchesLaptopWidth && hasFinePointer;

  // Theme-aware color palette for the table
  const tableColors = useMemo(() => ({
    // Section header backgrounds
    timelineHeader: isDark
      ? 'linear-gradient(135deg, #1a2332, #243447)'
      : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    fundsHeader: isDark
      ? 'linear-gradient(135deg, #1a2332, #1e3a4f)'
      : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    propertyHeader: isDark
      ? 'linear-gradient(135deg, #3d2800, #5a3a00)'
      : `linear-gradient(135deg, ${theme.palette.warning.dark}, ${theme.palette.warning.main})`,
    // Buyer color themes — muted warm tones for dark mode
    buyerHeaderColors: [
      { bg: isDark ? 'rgba(217,119,6,0.15)' : alpha(theme.palette.warning.light, 0.5), text: theme.palette.text.primary },
      { bg: isDark ? 'rgba(217,119,6,0.22)' : alpha(theme.palette.warning.light, 0.75), text: theme.palette.text.primary },
      { bg: isDark ? 'rgba(217,119,6,0.30)' : alpha(theme.palette.warning.main, 0.4), text: theme.palette.text.primary },
    ],
    buyerSectionColors: [
      { bg: isDark ? '#7c4a0a' : theme.palette.warning.main, text: '#fff' },
      { bg: isDark ? '#5a3608' : theme.palette.warning.dark, text: '#fff' },
      { bg: isDark ? '#3d2504' : '#a36400', text: '#fff' },
    ],
    // Row states
    rowHover: isDark ? 'rgba(255,255,255,0.04)' : alpha(theme.palette.primary.main, 0.06),
    rowSelected: isDark ? 'rgba(59,130,246,0.12)' : alpha(theme.palette.primary.main, 0.1),
    rowAlternate: isDark ? 'rgba(255,255,255,0.02)' : alpha(theme.palette.action.hover, 0.03),
    // Status colors
    positive: isDark ? 'rgba(34,197,94,0.10)' : alpha('#4caf50', 0.08),
    positiveText: isDark ? '#4ade80' : '#2e7d32',
    negative: isDark ? 'rgba(239,68,68,0.10)' : alpha('#f44336', 0.08),
    negativeText: isDark ? '#f87171' : '#c62828',
    cashWarning: isDark ? 'rgba(251,191,36,0.10)' : '#fff3e0',
    cashWarningText: isDark ? '#fbbf24' : '#e65100',
    // Column header
    columnHeaderBg: isDark ? '#1a1f2e' : theme.palette.grey[50],
    // Borders
    sectionBorder: isDark ? 'rgba(255,255,255,0.08)' : alpha(theme.palette.divider, 0.5),
    headerBorderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)',
  }), [isDark, theme]);

  const { transferData, setTransferData } = useCalculatorData();
  const { selectedProfile, getSelectedProfiles, getSelectedName, clearSelection } = useProfile();
  const profileManagerLauncher = useProfileManagerLauncher();
  
  const [propertyType, setPropertyType] = useState<'PRIVATE' | 'EC' | 'HDB'>('PRIVATE');
  const [loanType, setLoanType] = useState<'hdb' | 'bank'>('bank');
  const [cpfHousingGrant, setCpfHousingGrant] = useState<string>('0');
  
  // Helper function to get the base property type for calculations
  const getBasePropertyType = (type: string): 'PRIVATE' | 'EC' | 'HDB' => {
    if (type.startsWith('PRIVATE')) return 'PRIVATE';
    if (type.startsWith('EC')) return 'EC';
    if (type.startsWith('HDB')) return 'HDB';
    return 'PRIVATE'; // Default fallback
  };
  
  // Helper function to get display name for property type
  const getPropertyTypeDisplay = (type: string): string => {
    switch (type) {
      case 'PRIVATE': return 'Private';
      case 'EC': return 'EC';
      case 'HDB': return 'HDB';
      default: return type;
    }
  };
  const [targetPurchasePrice, setTargetPurchasePrice] = useState<string>('0');
  /** When true, Max Purchase Price column shows income-based max + Total Capital; when false, income-based max only. */
  const [includeFundsInMaxPurchasePrice, setIncludeFundsInMaxPurchasePrice] = useState(false);

  // Tutorial state
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [completedSteps] = useState<Set<number>>(new Set());
  
  // Selected row state
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  
  // Tutorial progress tracking
  const [tutorialProgress, setTutorialProgress] = useState<{
    lastStep: number;
    completed: boolean;
    started: boolean;
  }>(() => {
    try {
      const saved = localStorage.getItem('tutorialProgress');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading tutorial progress:', error);
    }
    return { lastStep: 0, completed: false, started: false };
  });

  // Get tutorial button color based on progress
  const getTutorialButtonColor = (): 'success' | 'warning' | 'error' => {
    if (tutorialProgress.completed) return 'success'; // Green - completed
    if (tutorialProgress.started) return 'warning'; // Amber - partially done
    return 'error'; // Red - not started
  };

  // Update tutorial progress
  const updateTutorialProgress = (step: number, isCompleted: boolean = false) => {
    const newProgress = {
      lastStep: step,
      completed: isCompleted,
      started: step > 0 || isCompleted,
    };
    setTutorialProgress(newProgress);
    try {
      localStorage.setItem('tutorialProgress', JSON.stringify(newProgress));
    } catch (error) {
      console.error('Error saving tutorial progress:', error);
    }
  };

  // Restart tutorial
  const handleRestartTutorial = () => {
    setTutorialStep(0);
    const resetProgress = {
      lastStep: 0,
      completed: false,
      started: false,
    };
    setTutorialProgress(resetProgress);
    try {
      localStorage.setItem('tutorialProgress', JSON.stringify(resetProgress));
    } catch (error) {
      console.error('Error resetting tutorial progress:', error);
    }
  };
  
  // History tracking
  const {
    historyEntries,
    activeSelection,
    editingHistoryId,
    editingHistoryLabel,
    setEditingHistoryLabel,
    saveToHistory: saveSharedHistory,
    getLastHistoryError,
    loadFromHistory: loadSharedHistory,
    setActiveSelection,
    updateHistoryLabel,
    startEditingLabel,
    cancelEditing,
    deleteHistory,
    formatTimestamp,
  } = useSharedProfileHistory();
  const [selectedExample, setSelectedExample] = useState<string>('');
  const [loadSelectionOpen, setLoadSelectionOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [historyToast, setHistoryToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const showHistoryToast = (message: string, severity: 'success' | 'error' = 'success') =>
    setHistoryToast({ open: true, message, severity });
  const CLEAR_SELECTION_VALUE = '__clear-selection__';
  const displayedSelectionValue =
    selectedExample ||
    (activeSelection ? activeSelection.id : '');
  
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [sourceViewerOpen, setSourceViewerOpen] = useState(false);
  const [sourceViewerUrl, setSourceViewerUrl] = useState<string>('');
  const [pdfExportDialogOpen, setPdfExportDialogOpen] = useState(false);
  const [pdfExportScope, setPdfExportScope] = useState<'current' | 'all'>('current');
  const [pdfExporting, setPdfExporting] = useState(false);
  const currentUser = useCurrentUser();
  const { settings: globalSettings } = useGlobalCalculatorSettings();
  const stressTestRate = globalSettings.stressTestRate;
  const stressTestSourceLabel = 'Super Calculator Settings';
  const [settings, setSettings] = useState<Settings>({
    salaryIncrement: 0,
    absdEnabled: false,
    absdRate: 0,
  });

  const mapPurchasePlannerPropertyTypeToShared = (
    value: 'PRIVATE' | 'EC' | 'HDB'
  ): SharedCalculatorPropertyType => {
    switch (value) {
      case 'HDB':
        return 'hdb';
      case 'EC':
        return 'ec';
      default:
        return 'private';
    }
  };

  const mapSharedPropertyTypeToPurchasePlanner = (
    value?: SharedCalculatorPropertyType
  ): 'PRIVATE' | 'EC' | 'HDB' => {
    switch (value) {
      case 'hdb':
        return 'HDB';
      case 'ec':
        return 'EC';
      default:
        return 'PRIVATE';
    }
  };

  const mapSharedProfilesToPurchasePlanner = useCallback(
    (sharedProfiles: readonly SharedCalculatorProfile[]): Profile[] =>
      sharedProfiles.map((profile, index) => ({
        id: profile.id || String(index + 1),
        currentAge: profile.age,
        monthlySalary: profile.monthlyIncome,
        monthlyDebts: profile.monthlyDebts ?? 0,
        cashSavingsMonthly: profile.cashSavingsMonthly ?? 0,
        cpfOAMonthly:
          profile.cpfOAMonthly ??
          (profile.selfEmployedInd === 1 ? 0 : calculateCPFOAMonthly(profile.monthlyIncome, profile.age)),
        currentCPFOA: profile.currentCPFOA ?? 0,
        currentCashSavings: profile.currentCashSavings ?? 0,
        selfEmployedInd: profile.selfEmployedInd ?? 0,
      })),
    []
  );

  const buildSharedProfilesFromPurchasePlanner = useCallback(
    (inputProfiles: Profile[]): SharedCalculatorProfile[] =>
      inputProfiles.map((profile, index) => ({
        id: profile.id || String(index + 1),
        age: profile.currentAge,
        monthlyIncome: profile.monthlySalary,
        monthlyDebts: profile.monthlyDebts ?? 0,
        cashSavingsMonthly: profile.cashSavingsMonthly ?? 0,
        cpfOAMonthly: profile.cpfOAMonthly ?? 0,
        currentCPFOA: profile.currentCPFOA ?? 0,
        currentCashSavings: profile.currentCashSavings ?? 0,
        selfEmployedInd: profile.selfEmployedInd ?? 0,
        incomeBasis: 'assessable',
      })),
    []
  );

  const applySharedProfiles = useCallback(
    (sharedProfiles: readonly SharedCalculatorProfile[], nextPropertyType?: SharedCalculatorPropertyType) => {
      setProfiles(mapSharedProfilesToPurchasePlanner(sharedProfiles));
      setPropertyType(mapSharedPropertyTypeToPurchasePlanner(nextPropertyType));
      setSelfEmployedCpfOAMonthlyByRow({});
    },
    [mapSharedProfilesToPurchasePlanner]
  );

  // Convert selected profile/group to Profile[] format
  const convertProfilesToTimelineFormat = useCallback((): Profile[] => {
    const selectedProfiles = getSelectedProfiles();
    if (selectedProfiles.length === 0) {
      // Default profile if none selected
      const defaultMonthlyIncome = 5000;
      const defaultAssessableIncome = calculateAssessableIncome(String(defaultMonthlyIncome), '0');
      return [{
        id: '1',
        currentAge: 30,
        monthlySalary: defaultAssessableIncome,
        monthlyDebts: 0,
        cashSavingsMonthly: 0,
        cpfOAMonthly: calculateCPFOAMonthly(defaultMonthlyIncome, 30),
        currentCPFOA: 0,
        currentCashSavings: 0,
      }];
    }
    
    const currentYear = new Date().getFullYear();
      return selectedProfiles.map((profile, index) => {
      const age = profile.BirthYear ? currentYear - profile.BirthYear : 30;
      const monthlyIncome = profile.MonthlyIncome || 0;
      const otherAnnualIncome = profile.OtherAnnualIncome || 0;
      const monthlyDebts = (profile.CarLoan ?? 0) + (profile.CreditCard ?? 0) + (profile.HousingLoan ?? 0) + (profile.OtherLoan ?? 0);
      // Calculate assessable income; self-employed gets 30% haircut on base monthly income
      const assessableIncome = calculateAssessableIncome(
        String(monthlyIncome),
        String(otherAnnualIncome),
        profile.SelfEmployedInd
      );
      // CPF OA monthly: auto-calculate from raw salary for employees; self-employed default $0 (editable).
      const cpfOAMonthly =
        profile.SelfEmployedInd === 1 ? 0 : calculateCPFOAMonthly(monthlyIncome, age);

      return {
        id: `profile-${profile.Id}`,
        currentAge: age,
        monthlySalary: assessableIncome, // Store assessable income in monthlySalary field
        monthlyDebts,
        cashSavingsMonthly: 0,
        cpfOAMonthly: cpfOAMonthly,
        currentCPFOA: profile.Cpf ?? 0,
        currentCashSavings: profile.Saving ?? 0,
        selfEmployedInd: profile.SelfEmployedInd ?? 0,
      };
    });
  }, [getSelectedProfiles]);

  // Multiple profiles support - initialize from selected profile/group
  const [profiles, setProfiles] = useState<Profile[]>(() => {
    const selectedProfiles = getSelectedProfiles();
    if (selectedProfiles.length === 0) {
      return [createDefaultPlannerProfile()];
    }
    
    const currentYear = new Date().getFullYear();
    return selectedProfiles.map((profile) => {
      const age = profile.BirthYear ? currentYear - profile.BirthYear : 30;
      const monthlyIncome = profile.MonthlyIncome || 0;
      const otherAnnualIncome = profile.OtherAnnualIncome || 0;
      const monthlyDebts = (profile.CarLoan ?? 0) + (profile.CreditCard ?? 0) + (profile.HousingLoan ?? 0) + (profile.OtherLoan ?? 0);
      // Calculate assessable income; self-employed gets 30% haircut on base monthly income
      const assessableIncome = calculateAssessableIncome(
        String(monthlyIncome),
        String(otherAnnualIncome),
        profile.SelfEmployedInd
      );
      // CPF OA monthly: auto-calculate from raw salary for employees; self-employed default $0 (editable).
      const cpfOAMonthly =
        profile.SelfEmployedInd === 1 ? 0 : calculateCPFOAMonthly(monthlyIncome, age);

      return {
        id: `profile-${profile.Id}`,
        currentAge: age,
        monthlySalary: assessableIncome, // Store assessable income in monthlySalary field
        monthlyDebts,
        cashSavingsMonthly: 0,
        cpfOAMonthly: cpfOAMonthly,
        currentCPFOA: profile.Cpf ?? 0,
        currentCashSavings: profile.Saving ?? 0,
        selfEmployedInd: profile.SelfEmployedInd ?? 0,
      };
    });
  });
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Auto-load profiles when selected profile/group changes; leave target purchase price at $0 for user entry
  useEffect(() => {
    if (selectedProfile) {
      setSelectedExample('');
      setActiveSelection(null);
      const convertedProfiles = convertProfilesToTimelineFormat();
      setProfiles(convertedProfiles);
      setProfileLoaded(true);
      setManualEdits({});
      setSelfEmployedCpfOAMonthlyByRow({});
      setTargetPurchasePrice('0');
    } else {
      if (activeSelection?.profiles?.length) return;
      // Reset to default if no profile selected
      setProfiles([createDefaultPlannerProfile()]);
      setProfileLoaded(false);
      setManualEdits({});
      setSelfEmployedCpfOAMonthlyByRow({});
    }
  }, [selectedProfile, convertProfilesToTimelineFormat]);

  const lastAppliedSelectionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeSelection?.profiles?.length) {
      lastAppliedSelectionIdRef.current = null;
      return;
    }
    if (activeSelection.id === lastAppliedSelectionIdRef.current) return;
    lastAppliedSelectionIdRef.current = activeSelection.id;
    applySharedProfiles(activeSelection.profiles, activeSelection.propertyType);
    restorePurchasePlannerHistoryState(activeSelection.purchasePlannerState);
    setSelectedExample(activeSelection.id);
  }, [activeSelection, applySharedProfiles]);

  // Zoom state and refs
  const [zoom, setZoom] = useState<number>(1);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const sectionHeaderRowRef = useRef<HTMLTableRowElement>(null);
  const pendingInputVisibilitySyncRef = useRef<number[]>([]);
  const [sectionHeaderRowPx, setSectionHeaderRowPx] = useState(56);
  const [touchViewportTableMaxHeight, setTouchViewportTableMaxHeight] = useState<number | null>(null);
  // Only cap the table height to the visual viewport while the software keyboard
  // is open (an input inside the table is focused). Otherwise the iPad would
  // always size the table to `viewportBottom - containerTop`, which makes the
  // page un-scrollable and leaves only a few rows visible. When no field is
  // focused we fall back to the same responsive clamp desktop uses.
  const [isTableInputFocused, setIsTableInputFocused] = useState(false);
  const responsiveColumnWidths = useCallback(
    (xs: number, sm: number, md: number, lg: number): Record<string, number> =>
      getPurchasePlannerResponsiveWidths({ xs, sm, md, lg }, isLaptopViewport),
    [isLaptopViewport]
  );
  const timelineColumnWidths = useMemo(
    () => (isLaptopViewport ? { xs: 80, sm: 90, md: 84, lg: 84 } : { xs: 80, sm: 90 }),
    [isLaptopViewport]
  );
  const editableCompactColumnWidths = useMemo(
    () => (isLaptopViewport ? { xs: 64, sm: 72, md: 72, lg: 76 } : { xs: 64, sm: 72, md: 80 }),
    [isLaptopViewport]
  );
  const ageColumnWidths = useMemo(
    () => responsiveColumnWidths(86, 94, 98, 102),
    [responsiveColumnWidths],
  );
  /** Full-width inputs make short numbers look lost in wide columns; laptop uses intrinsic width + maxWidth. */
  const laptopAgeFieldSx = useMemo(
    () =>
      isLaptopViewport
        ? ({ width: '100%', minWidth: 72, display: 'block' } as const)
        : ({ width: '100%' } as const),
    [isLaptopViewport],
  );
  const laptopProfileCurrencyFieldSx = useMemo(
    () =>
      isLaptopViewport
        ? ({ width: 112, display: 'block', marginLeft: 'auto' } as const)
        : ({ width: '100%', minWidth: 0 } as const),
    [isLaptopViewport],
  );
  const laptopFundsCurrencyFieldSx = useMemo(
    () => ({ width: '100%', minWidth: 0 } as const),
    [],
  );
  const tableMinWidth = useMemo(
    () => (isLaptopViewport ? { xs: 1180, sm: 1360, md: 1260, lg: 1320 } : { xs: 1440, sm: 1580, md: 1720 }),
    [isLaptopViewport]
  );
  const tableBodyPadding = useMemo(
    () => (isLaptopViewport ? { xs: '6px 5px', sm: '8px 6px', md: '8px 6px' } : { xs: '8px 6px', sm: '10px 8px', md: '12px 10px' }),
    [isLaptopViewport]
  );
  const tableBodyFontSize = useMemo(
    () => (isLaptopViewport ? { xs: '1rem', sm: '1.05rem', md: '1.2rem' } : { xs: '0.95rem', sm: '1rem', md: '1.05rem' }),
    [isLaptopViewport]
  );
  const tableHeaderPadding = useMemo(
    () => (isLaptopViewport ? { xs: '3px 5px', sm: '4px 6px', md: '5px 6px' } : { xs: '4px 6px', sm: '5px 7px', md: '6px 8px' }),
    [isLaptopViewport]
  );
  const tableHeaderFontSize = useMemo(
    () => (isLaptopViewport ? { xs: '0.72rem', sm: '0.78rem', md: '0.88rem' } : { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }),
    [isLaptopViewport]
  );
  const getAutoFitZoomForTable = useCallback(
    (containerWidth: number, tableWidth: number) =>
      getPurchasePlannerAutoFitZoom({
        containerWidth,
        tableWidth,
        isLaptopViewport,
      }),
    [isLaptopViewport]
  );
  const editableInputFontSize = 'inherit';
  const compactEditableInputPadding = '5px';
  const standardEditableInputPadding = '6px 10px';
  const tableContainerMaxHeight = useMemo(
    () =>
      !hasFinePointer && isTableInputFocused && touchViewportTableMaxHeight
        ? `${touchViewportTableMaxHeight}px`
        : ({
            xs: 'clamp(460px, calc(100dvh - 220px), 900px)',
            sm: 'clamp(560px, calc(100dvh - 180px), 960px)',
            md: 'clamp(620px, calc(100dvh - 170px), 1040px)',
            lg: 'clamp(660px, calc(100dvh - 160px), 1100px)',
          } as const),
    [hasFinePointer, isTableInputFocused, touchViewportTableMaxHeight],
  );

  const clearPendingInputVisibilitySyncs = useCallback(() => {
    pendingInputVisibilitySyncRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    pendingInputVisibilitySyncRef.current = [];
  }, []);

  const getVisibleViewportBottom = useCallback(() => {
    const visualViewport = window.visualViewport;
    return visualViewport ? visualViewport.height + visualViewport.offsetTop : window.innerHeight;
  }, []);

  const syncTouchViewportTableMaxHeight = useCallback(() => {
    if (hasFinePointer) {
      setTouchViewportTableMaxHeight(null);
      return;
    }

    const container = tableContainerRef.current;
    if (!container) return;

    const nextMaxHeight = getPurchasePlannerVisibleTableMaxHeight({
      containerTop: container.getBoundingClientRect().top,
      viewportBottom: getVisibleViewportBottom(),
    });

    setTouchViewportTableMaxHeight((current) => (current === nextMaxHeight ? current : nextMaxHeight));
  }, [getVisibleViewportBottom, hasFinePointer]);

  const keepFocusedInputVisible = useCallback((target?: HTMLElement | null) => {
    const container = tableContainerRef.current;
    if (!container) return;

    const activeElement =
      target ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    if (!activeElement || !container.contains(activeElement)) return;

    // On touch, centering helps keep the active cell in the visible band above the
    // software keyboard after the visual viewport shrinks.
    activeElement.scrollIntoView({
      block: hasFinePointer ? 'nearest' : 'center',
      inline: 'nearest',
      behavior: 'auto',
    });

    const containerRect = container.getBoundingClientRect();
    const visibleViewportBottom = getVisibleViewportBottom();
    const visibleContainerHeight = Math.max(
      0,
      Math.min(container.clientHeight, visibleViewportBottom - containerRect.top),
    );
    if (visibleContainerHeight <= 0) return;

    const tableHead = tableRef.current?.querySelector('thead');
    const stickyTopInset =
      tableHead instanceof HTMLElement
        ? tableHead.getBoundingClientRect().height
        : sectionHeaderRowPx * 2;
    const fieldRect = activeElement.getBoundingClientRect();
    const nextScrollTop = getPurchasePlannerFocusedFieldScrollTop({
      containerScrollTop: container.scrollTop,
      containerTop: containerRect.top,
      containerHeight: visibleContainerHeight,
      fieldTop: fieldRect.top,
      fieldBottom: fieldRect.bottom,
      stickyTopInset,
    });

    if (nextScrollTop === null) return;

    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTo({
      top: Math.max(0, Math.min(nextScrollTop, maxScrollTop)),
      behavior: 'auto',
    });
  }, [getVisibleViewportBottom, hasFinePointer, sectionHeaderRowPx]);

  const scheduleFocusedInputVisibilitySync = useCallback((target?: HTMLElement | null) => {
    const activeElement =
      target ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    if (!activeElement || !tableContainerRef.current?.contains(activeElement)) return;

    clearPendingInputVisibilitySyncs();
    [0, 120, 260].forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        syncTouchViewportTableMaxHeight();
        keepFocusedInputVisible(activeElement);
      }, delay);
      pendingInputVisibilitySyncRef.current.push(timeoutId);
    });
  }, [clearPendingInputVisibilitySyncs, keepFocusedInputVisible, syncTouchViewportTableMaxHeight]);

  useLayoutEffect(() => {
    const row = sectionHeaderRowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      setSectionHeaderRowPx(row.offsetHeight);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    measure();
    return () => ro.disconnect();
  }, [zoom, profiles.length, propertyType]);

  useLayoutEffect(() => {
    syncTouchViewportTableMaxHeight();
  }, [syncTouchViewportTableMaxHeight, profiles.length, propertyType, targetPurchasePrice]);

  useEffect(() => {
    const handleViewportChange = () => {
      syncTouchViewportTableMaxHeight();
      keepFocusedInputVisible();
    };

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', handleViewportChange);
    visualViewport?.addEventListener('scroll', handleViewportChange);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      visualViewport?.removeEventListener('resize', handleViewportChange);
      visualViewport?.removeEventListener('scroll', handleViewportChange);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [keepFocusedInputVisible, syncTouchViewportTableMaxHeight]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const isEditableTarget = (target: EventTarget | null): target is HTMLElement =>
      target instanceof HTMLElement &&
      target.matches('input, textarea, [contenteditable="true"]');

    const handleFocusIn = (event: FocusEvent) => {
      if (!isEditableTarget(event.target)) return;
      setIsTableInputFocused(true);
      scheduleFocusedInputVisibilitySync(event.target);
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (!isEditableTarget(event.target)) return;
      // focusout fires before the next focusin; defer so we can detect focus
      // moving between two editable cells without flickering the layout.
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && container.contains(active) && isEditableTarget(active)) {
          return;
        }
        setIsTableInputFocused(false);
      }, 0);
    };

    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);
    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
    };
  }, [scheduleFocusedInputVisibilitySync]);

  useEffect(() => clearPendingInputVisibilitySyncs, [clearPendingInputVisibilitySyncs]);

  // Reset view to fit table
  const handleViewReset = () => {
    if (!tableContainerRef.current || !tableRef.current) return;
    
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      const containerWidth = tableContainerRef.current?.clientWidth || 0;
      const tableWidth = tableRef.current?.scrollWidth || 0;
      
      if (tableWidth === 0 || containerWidth === 0) return;
      const clampedZoom = getAutoFitZoomForTable(containerWidth, tableWidth);
      setZoom(clampedZoom);
    }, 100);
  };

  // Zoom in/out handlers
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(2.0, prev + 0.1));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(0.5, prev - 0.1));
  };

  // Clamp scroll position to prevent over-scrolling into empty spaces
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const clampScroll = () => {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const maxScrollTop = container.scrollHeight - container.clientHeight;

      // Clamp horizontal scroll
      if (container.scrollLeft < 0) {
        container.scrollLeft = 0;
      } else if (container.scrollLeft > maxScrollLeft) {
        container.scrollLeft = maxScrollLeft;
      }

      // Clamp vertical scroll
      if (container.scrollTop < 0) {
        container.scrollTop = 0;
      } else if (container.scrollTop > maxScrollTop) {
        container.scrollTop = maxScrollTop;
      }
    };

    container.addEventListener('scroll', clampScroll, { passive: true });
    return () => container.removeEventListener('scroll', clampScroll);
  }, [zoom]); // Re-run when zoom changes

  // Calculate initial zoom on mount
  useEffect(() => {
    const calculateInitialZoom = () => {
      if (!tableContainerRef.current || !tableRef.current) return;
      
      setTimeout(() => {
        const containerWidth = tableContainerRef.current?.clientWidth || 0;
        const tableWidth = tableRef.current?.scrollWidth || 0;
        
        if (tableWidth === 0 || containerWidth === 0) return;
        const clampedZoom = getAutoFitZoomForTable(containerWidth, tableWidth);
        setZoom(clampedZoom);
      }, 200);
    };

    calculateInitialZoom();
  }, [getAutoFitZoomForTable, profiles.length, targetPurchasePrice]); // Recalculate when table structure changes

  // Recalculate zoom on window resize (only if user hasn't manually zoomed)
  useEffect(() => {
    const handleResize = () => {
      if (!tableContainerRef.current || !tableRef.current) return;
      
      setTimeout(() => {
        const containerWidth = tableContainerRef.current?.clientWidth || 0;
        const tableWidth = tableRef.current?.scrollWidth || 0;
        
        if (tableWidth === 0 || containerWidth === 0) return;
        const clampedZoom = getAutoFitZoomForTable(containerWidth, tableWidth);
        
        // Only auto-adjust if current zoom is close to optimal (user hasn't manually zoomed)
        if (Math.abs(zoom - clampedZoom) < 0.05) {
          setZoom(clampedZoom);
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getAutoFitZoomForTable, zoom]);


  // Helper function to calculate Income Weighted Average Age (IWAA)
  // IWAA = Σ(Age × Assessable Income) / Σ(Assessable Income)
  // Source: MAS guidelines for joint loan applications
  // Note: Rounded UP to next whole number for conservative age calculation (fewer loan years)
  const calculateIWAA = (profiles: Profile[]): { original: number; roundedUp: number } => {
    if (profiles.length === 0) return { original: 0, roundedUp: 0 };
    if (profiles.length === 1) {
      const age = profiles[0].currentAge;
      return { original: age, roundedUp: age };
    }
    
    const totalIncome = profiles.reduce((sum, p) => sum + p.monthlySalary, 0);
    if (totalIncome === 0) {
      const age = profiles[0].currentAge;
      return { original: age, roundedUp: age };
    }
    
    const weightedAgeSum = profiles.reduce((sum, p) => sum + (p.currentAge * p.monthlySalary), 0);
    const original = weightedAgeSum / totalIncome;
    const roundedUp = Math.ceil(original); // Round UP to next whole number for conservative calculation
    
    return { original, roundedUp };
  };

  // Helper function to format IWAA display
  const formatIWAA = (iwaa: { original: number; roundedUp: number }): string => {
    if (iwaa.original === iwaa.roundedUp) {
      // Already a whole number, show only one value
      return `${iwaa.roundedUp}`;
    }
    // Show both values: original (rounded up to roundedUp)
    return `${iwaa.original.toFixed(1)} (rounded up to ${iwaa.roundedUp})`;
  };

  // Helper function to get combined values
  const getCombinedValues = (profiles: Profile[]) => {
    return {
      totalMonthlySalary: profiles.reduce((sum, p) => sum + p.monthlySalary, 0),
      totalMonthlyDebts: profiles.reduce((sum, p) => sum + (p.monthlyDebts ?? 0), 0),
      totalCashSavingsMonthly: profiles.reduce((sum, p) => sum + p.cashSavingsMonthly, 0),
      totalCPFOAMonthly: profiles.reduce((sum, p) => sum + p.cpfOAMonthly, 0),
      totalCurrentCPFOA: profiles.reduce((sum, p) => sum + p.currentCPFOA, 0),
      totalCurrentCashSavings: profiles.reduce((sum, p) => sum + p.currentCashSavings, 0),
      iwaa: calculateIWAA(profiles),
    };
  };

  // Add a new profile
  const addProfile = () => {
    const newProfile: Profile = {
      id: Date.now().toString(),
      currentAge: 30,
      monthlySalary: 0,
      monthlyDebts: 0,
      cashSavingsMonthly: 0,
      cpfOAMonthly: 0,
      currentCPFOA: 0,
      currentCashSavings: 0,
    };
    setProfiles([...profiles, newProfile]);
  };

  // Remove a profile
  const removeProfile = (id: string) => {
    if (profiles.length > 1) {
      setProfiles(profiles.filter(p => p.id !== id));
    }
  };

  // Update a profile
  const updateProfile = (id: string, updates: Partial<Profile>) => {
    setProfiles(profiles.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const commitHistoryRename = async (entryId: string, label: string) => {
    const ok = await updateHistoryLabel(entryId, label);
    if (ok) {
      showHistoryToast('History entry updated');
    }
  };

  const handleDeleteHistoryEntry = async (historyId: string) => {
    const ok = await deleteHistory(historyId);
    if (ok) {
      showHistoryToast('History entry deleted');
    }
  };

  // Save current state to history
  const handleSaveToHistory = async () => {
    const customLabel = buildLoadedProfileHistoryLabel(getSelectedName(), activeSelection);
    const created = await saveSharedHistory(
      {
        profiles: buildSharedProfilesFromPurchasePlanner(profiles),
        propertyType: mapPurchasePlannerPropertyTypeToShared(propertyType),
        purchasePlannerState: buildPurchasePlannerHistoryState(),
      },
      customLabel
    );
    if (created) {
      showHistoryToast('Saved to history');
    } else {
      const reason = getLastHistoryError();
      showHistoryToast(reason ? `Save failed — ${reason}` : 'Save failed — please try again', 'error');
    }
  };


  // Load from history
  const loadFromHistory = (historyId: string) => {
    const entry = loadSharedHistory(historyId);
    if (entry) {
      applySharedProfiles(entry.profiles, entry.propertyType);
      restorePurchasePlannerHistoryState(entry.purchasePlannerState);
    }
  };
  const presetExamples = SHARED_CALCULATOR_EXAMPLES;

  const clearLoadedSelection = () => {
    setSelectedExample('');
    setActiveSelection(null);
  };

  // Load a preset example
  const loadExample = (exampleKey: keyof typeof presetExamples) => {
    clearSelection();
    const example = presetExamples[exampleKey];
    applySharedProfiles(example.profiles, example.propertyType);
    restorePurchasePlannerHistoryState(undefined);
    setSelectedExample(exampleKey);
    setActiveSelection({
      id: exampleKey,
      source: 'example',
      profiles: example.profiles,
      propertyType: example.propertyType,
      purchasePlannerState: undefined,
    });
  };

  // Handle loading from history via dropdown
  const handleLoadFromHistory = (historyId: string) => {
    const entry = loadSharedHistory(historyId);
    if (!entry) return;

    clearSelection();
    applySharedProfiles(entry.profiles, entry.propertyType);
    restorePurchasePlannerHistoryState(entry.purchasePlannerState);
    setSelectedExample(historyId);
    setActiveSelection({
      id: historyId,
      source: 'history',
      profiles: entry.profiles,
      propertyType: entry.propertyType,
      purchasePlannerState: entry.purchasePlannerState,
    });
  };

  // Tutorial pointer state
  const [pointerTarget, setPointerTarget] = useState<{ selector: string; position: 'top' | 'bottom' | 'left' | 'right' } | null>(null);
  const [pointerVisible, setPointerVisible] = useState(false);
  const [dialogMinimized, setDialogMinimized] = useState(false);

  // Show pointer for tutorial step with alternating dialog visibility
  const showPointer = (selector: string | null, position: 'top' | 'bottom' | 'left' | 'right' = 'bottom') => {
    if (!selector) {
      setPointerVisible(false);
      setPointerTarget(null);
      setDialogMinimized(false);
      return;
    }
    
    // Minimize dialog to make pointer visible
    setDialogMinimized(true);
    setPointerTarget({ selector, position });
    
    // Small delay before showing pointer for smooth transition
    setTimeout(() => {
      setPointerVisible(true);
      
      // Auto-hide pointer after 3 seconds
      setTimeout(() => {
        setPointerVisible(false);
        
        // Restore dialog visibility after pointer fades out
        setTimeout(() => {
          setDialogMinimized(false);
          setTimeout(() => setPointerTarget(null), 300); // Clean up after transition
        }, 300); // Wait for fade out animation
      }, 3000);
    }, 300); // Small delay for dialog minimization animation
  };

  // Tutorial steps data
  const tutorialSteps = [
    {
      title: 'Welcome to Super Calculator!',
      content: 'This comprehensive property purchase planner helps you calculate affordability, loan eligibility, and plan your property purchase timeline. Let\'s get started!',
      targetElement: null as string | null,
    },
    {
      title: 'Load Example / History',
      content: 'Use the "Load Example / History" dropdown to quickly start with preset profiles:\n• Example 1: Single buyer, 30 years old, $8k monthly salary\n• Example 2: Single buyer, 45 years old, $8k monthly salary\n• Example 3: Two buyers (30yrs $4k + 35yrs $7k)\n\nYou can also load from your saved history entries.',
      targetElement: '[data-tutorial="load-profile"]',
    },
    {
      title: 'Property Type Selection',
      content: 'Select your property type:\n• Private: Private condominiums (new or resale)\n• EC: Executive Condominiums\n• HDB: HDB resale flats\n\nThe property type affects Loan-to-Value (LTV) ratios and financing rules.',
      targetElement: '[data-tutorial="property-type"]',
    },
    {
      title: 'Target Purchase Price',
      content: 'Enter your target purchase price (optional). If set:\n• Calculations will use this price instead of maximum affordable price\n• Remaining downpayment = Target Price - Max Loan - 5% Cash Downpayment\n• The "Diff" column will show difference from target price',
      targetElement: '[data-tutorial="target-price"]',
    },
    {
      title: 'Current Profile - Single Buyer',
      content: 'For a single buyer, enter:\n• Age: Your current age (editable in first row)\n• Monthly Salary: Your gross monthly income\n• Cash Savings (Monthly): Additional monthly savings\n• CPF OA (Monthly): CPF Ordinary Account contribution (auto-calculated)\n• Current OA $: Your existing CPF OA balance\n• Current Saving $: Your existing cash savings',
      targetElement: '[data-tutorial="current-profile"]',
    },
    {
      title: 'Multiple Buyers - Add Profile',
      content: 'Click the "+" button next to "Current Profile" to add a second buyer:\n• Each buyer has separate Age, Monthly Salary, Cash Savings, and CPF OA\n• The calculator uses Income Weighted Average Age (IWAA) for joint loans\n• Combined funds are automatically calculated\n• Different color themes help distinguish Buyer 1 and Buyer 2',
      targetElement: '[data-tutorial="add-profile"]',
    },
    {
      title: 'Combined Funds (Accumulated)',
      content: 'When multiple profiles exist:\n• "Funds (Accumulated)" becomes "Combined Funds (Accumulated)"\n• Combined Current OA $: Sum of all buyers\' CPF OA\n• Combined Current Saving $: Sum of all buyers\' cash savings\n• You can edit these fields directly, and changes distribute proportionally',
      targetElement: '[data-tutorial="combined-funds"]',
    },
    {
      title: 'Start Month Selection',
      content: 'Select the starting month for your timeline:\n• Timeline calculations begin from this month\n• Cannot select December if current month is December\n• All future calculations are based on this start date',
      targetElement: '[data-tutorial="start-month"]',
    },
    {
      title: 'Timeline Table Overview',
      content: 'The timeline table shows projections year by year:\n• Age progression over time\n• Monthly salary (with increments if set)\n• Accumulated CPF OA and Cash Savings\n• Maximum loan eligibility\n• Purchase affordability calculations',
      targetElement: '[data-tutorial="timeline-table"]',
    },
    {
      title: 'Max Purchase Price & Loan',
      content: 'Key calculations:\n• Max Loan: Based on TDSR (55% for private) or MSR (30% for HDB/EC)\n• Uses stress test rate (default 4.0%) to ensure affordability\n• Max Purchase Price = Max Loan ÷ LTV Ratio\n• LTV reduces when loan tenure exceeds 30 years (MAS regulations)',
      targetElement: '[data-tutorial="max-loan-column"]',
    },
    {
      title: 'Downpayment Breakdown',
      content: 'Understanding downpayments:\n• 5% Cash Downpayment: Must be paid in cash (5% of purchase price)\n• Remaining Downpayment: Additional funds needed\n  - If target price > 0: Target Price - Max Loan - 5% Cash\n  - Otherwise: 20% of Max Purchase Price\n• Total Outlay = 5% Cash + Remaining Downpayment + Stamp Duty (BSD) + ABSD',
      targetElement: '[data-tutorial="downpayment-column"]',
    },
    {
      title: 'Stamp Duty & ABSD Calculations',
      content: 'Stamp duty has two parts, shown in separate columns:\n• Buyer\'s Stamp Duty (BSD): Progressive residential rates (1-6%, on or after 15 Feb 2023)\n• Additional Buyer\'s Stamp Duty (ABSD): Purchase Price × ABSD rate, derived from the buyer profile(s) — citizenship (with FTA remission) and properties owned, incl. married-couple remission\n• Both are added to Total Outlay. Tap any value for the full breakdown.',
      targetElement: '[data-tutorial="stamp-duty-column"]',
    },
    {
      title: 'Zoom Controls',
      content: 'Navigate the timeline table:\n• Zoom Out: Reduce table size\n• View Reset: Fit table to viewport\n• Zoom In: Enlarge table for detail\n• Zoom percentage shown for reference\n• Horizontal scrolling available when zoomed in',
      targetElement: '[data-tutorial="zoom-controls"]',
    },
    {
      title: 'Info & Search Buttons',
      content: 'Additional resources:\n• Info (i) button: View all formulas, calculations, and sources\n• Search button: Quick access to MAS, CPF, and IRAS references\n• Settings button: Configure salary increments and ABSD rates\n• Left menu > Settings: Configure the shared stress test rate\n• Each calculation includes source references',
      targetElement: '[data-tutorial="info-buttons"]',
    },
    {
      title: 'History & Saving Profiles',
      content: 'Save and reload your work:\n• Loading a preset automatically saves current state to history\n• History entries show timestamp and are editable (double-click label)\n• Load from history to restore previous configurations\n• History persists across browser sessions',
      targetElement: '[data-tutorial="load-profile"]',
    },
  ];

  // Download user manual
  const downloadUserManual = () => {
    const manualContent = `SUPER CALCULATOR - USER MANUAL
Generated: ${new Date().toLocaleString('en-SG')}

================================================================================
TABLE OF CONTENTS
================================================================================

1. Introduction
2. Getting Started
3. Property Type Selection
4. Profile Setup
5. Multiple Buyers
6. Timeline Calculations
7. Understanding Results
8. Advanced Features
9. Tips & Best Practices
10. Troubleshooting

================================================================================
1. INTRODUCTION
================================================================================

Super Calculator is a comprehensive property purchase planning tool that helps 
you:
• Calculate property affordability based on your income
• Determine maximum loan eligibility
• Plan your purchase timeline
• Understand downpayment requirements
• Calculate stamp duties and total outlay

================================================================================
2. GETTING STARTED
================================================================================

STEP 1: Load a Preset Profile
Click "Load Profile" button and select:
• Default Profile 1: Single buyer, 30 years old, $5k monthly salary
• Default Profile 2: Two buyers (30yrs $5k + 40yrs $8k) with $2M target

STEP 2: Select Property Type
Choose from:
• Private: Private condominiums (new or resale)
• EC: Executive Condominiums
• HDB: HDB resale flats

STEP 3: Enter Target Purchase Price (Optional)
If you have a specific property in mind, enter the target price. This will 
override the maximum affordable price calculations.

================================================================================
3. PROPERTY TYPE SELECTION
================================================================================

Property type affects:
• Loan-to-Value (LTV) ratios
• Maximum loan tenure
• Financing rules (TDSR vs MSR)

LTV RATIOS:
• Private/EC: 75% for tenure ≤ 30 years, 55% for tenure > 30 years
• HDB: 75% for tenure ≤ 30 years, 55% for tenure > 30 years (MAS guidelines)

Source: MAS Property Financing Guidelines

================================================================================
4. PROFILE SETUP - SINGLE BUYER
================================================================================

Enter the following information:

AGE
• Your current age
• Editable in the first row of the timeline table
• Affects CPF contribution rates

MONTHLY SALARY
• Your gross monthly income
• Used for TDSR/MSR calculations
• Can include salary increments (configure in Settings)

CASH SAVINGS (MONTHLY)
• Additional monthly savings beyond CPF
• Added to accumulated cash savings over time

CPF OA (MONTHLY)
• Automatically calculated based on age and salary
• Uses CPF contribution rates and OA allocation ratios
• Contribution rates vary by age bracket

CURRENT OA $ & CURRENT SAVING $
• Your existing CPF Ordinary Account balance
• Your existing cash savings
• These are your starting funds

================================================================================
5. MULTIPLE BUYERS
================================================================================

ADDING A SECOND BUYER:
• Click the "+" button next to "Current Profile"
• Enter details for the second buyer
• Calculator automatically combines funds

INCOME WEIGHTED AVERAGE AGE (IWAA):
IWAA = Σ(Age × Monthly Salary) / Σ(Monthly Salary)

Used for joint loan applications to determine loan tenure limits.

COMBINED FUNDS:
• Combined Current OA $: Sum of all buyers' CPF OA
• Combined Current Saving $: Sum of all buyers' cash savings
• Editable fields distribute changes proportionally

COLOR THEMES:
• Buyer 1 and Buyer 2 have different background colors
• Helps distinguish between buyers in the timeline table

================================================================================
6. TIMELINE CALCULATIONS
================================================================================

The timeline table shows year-by-year projections:

AGE
• Progresses automatically each year
• Used for CPF contribution rate calculations

MONTHLY SALARY
• Base salary with increments (if configured)
• Used for loan eligibility calculations

ACCUMULATED FUNDS
• CPF OA Accumulated: Grows from monthly contributions plus 2.5% p.a. OA interest (contributions earn interest from the month after each is received)
• Cash Savings Accumulated: Grows monthly based on savings rate
• Total Capital: Sum of CPF OA + Cash Savings + CPF Refund From Sales + Cash Proceeds From Sale

MAX LOAN
• Calculated using TDSR (55% for private) or MSR (30% for HDB/EC)
• Uses stress test rate (default 4.0%) to ensure affordability
• Formula: Max Loan = Max Monthly Payment × [(1+r)^n - 1] / [r × (1+r)^n]
  where r = monthly stress test rate, n = number of payments

MAX PURCHASE PRICE
• Max Purchase Price = Max Loan ÷ LTV Ratio
• Based on loan eligibility and property type

TARGET PURCHASE PRICE
• If set, replaces Max Purchase Price in calculations
• Shows difference from affordable price

================================================================================
7. UNDERSTANDING RESULTS
================================================================================

DOWNPAYMENT BREAKDOWN:

5% CASH DOWNPAYMENT
• Must be paid in cash
• 5% of purchase price
• Cannot use CPF for this portion

REMAINING DOWNPAYMENT
• If target price > 0: Target Price - Max Loan - 5% Cash
• Otherwise: 20% of Max Purchase Price
• Can use CPF OA and cash savings

STAMP DUTY:
Buyer's Stamp Duty (BSD) — residential (on or after 15 Feb 2023):
• First $180,000: 1%
• Next $180,000: 2%
• Next $640,000: 3%
• Next $500,000: 4%
• Next $1,500,000: 5%
• Remaining amount: 6%
(BSD rounded down to the nearest dollar; minimum $1.)

Additional Buyer Stamp Duty (ABSD):
• Shown in its own column; added to Total Outlay
• Rate derived from buyer profile(s): citizenship (with FTA remission) and properties owned, incl. married-couple remission
• A manual rate set in Settings overrides the derived rate

TOTAL OUTLAY:
Total Outlay = 5% Cash + Remaining Downpayment + Stamp Duty (BSD) + ABSD

AFFORDABILITY:
• Affordable Today: Shows if you can afford today (Total Capital ≥ Total Outlay)
• Difference: Total Capital - Total Outlay
• Positive difference means you can afford the purchase

================================================================================
8. ADVANCED FEATURES
================================================================================

ZOOM CONTROLS:
• Zoom Out: Reduce table size
• View Reset: Fit table to viewport
• Zoom In: Enlarge table for detail
• Use when table is too large or small for your screen

SETTINGS:
Access via Settings icon for local planner options, or left menu > Settings for the shared stress test rate:
• Salary Increment: Annual salary increase percentage
• ABSD Enabled: Toggle Additional Buyer Stamp Duty
• ABSD Rate: Set ABSD percentage
• Stress Test Rate: Interest rate for loan calculations (default 4.0%)

INFO BUTTON:
• View all formulas and calculations
• Access source references (MAS, CPF, IRAS)
• Understand calculation methodology

SEARCH BUTTON:
• Quick access to external references
• Search MAS regulations, CPF rules, IRAS guidelines
• Opens relevant documentation

HISTORY:
• Automatically saves when loading presets
• Double-click history entry label to edit
• Load from history to restore previous configurations
• History persists across browser sessions

================================================================================
9. TIPS & BEST PRACTICES
================================================================================

1. START WITH PRESETS
   Use preset profiles to understand the tool, then customize for your situation.

2. ENTER ACCURATE DATA
   Accurate age, salary, and current savings ensure reliable projections.

3. CONSIDER MULTIPLE SCENARIOS
   Try different property types and target prices to see affordability ranges.

4. UNDERSTAND LTV LIMITS
   Loan tenure exceeding 30 years reduces LTV ratios significantly.

5. PLAN FOR STAMP DUTY
   Don't forget BSD and ABSD in your total outlay calculations.

6. USE TARGET PRICE WISELY
   Set target price only if you have a specific property in mind.

7. REVIEW STRESS TEST RATE
   Ensure stress test rate reflects current market conditions.

8. SAVE YOUR WORK
   Use history feature to save different scenarios for comparison.

9. CHECK SOURCES
   Use Info and Search buttons to verify regulations and rates.

10. REGULAR UPDATES
    Update your current savings and CPF OA balances regularly.

================================================================================
10. TROUBLESHOOTING
================================================================================

ISSUE: Timeline not updating
SOLUTION: Check that all required fields are filled. Ensure start month is 
          selected correctly.

ISSUE: Max loan seems too low
SOLUTION: Verify salary entry. Check TDSR/MSR limits. Review stress test rate 
          in Settings.

ISSUE: Cannot edit age
SOLUTION: Age is only editable in the first row of the timeline table.

ISSUE: History not saving
SOLUTION: Check browser localStorage permissions. Clear browser cache if needed.

ISSUE: Calculations seem incorrect
SOLUTION: Verify property type selection. Check LTV ratios match your property 
          type. Review Info dialog for calculation formulas.

ISSUE: Target price not affecting calculations
SOLUTION: Ensure target price is greater than 0. Check that remaining 
          downpayment calculation uses target price.

================================================================================
SOURCES & REFERENCES
================================================================================

MAS Property Financing Guidelines:
https://www.mas.gov.sg/regulation/banking/notices-and-circulars

MAS Loan Tenure and LTV Limits:
https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits

CPF Allocation Rates:
https://www.cpf.gov.sg/service/sfc/servlet.shepherd/document/download/069IW000005qFrJYAU

IRAS Stamp Duty Rates:
https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property

================================================================================
END OF MANUAL
================================================================================

For support or questions, refer to the Info button in the application for 
detailed calculation formulas and source references.

Version: 1.1.0
Last Updated: ${new Date().toLocaleDateString('en-SG')}
`;

    const blob = new Blob([manualContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Super_Calculator_User_Manual_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [timelineData, setTimelineData] = useState<TimelineData[]>([]);
  // Track manual edits to CPF OA and Cash Savings for each row
  const [manualEdits, setManualEdits] = useState<Record<number, { cpfOA?: number | string; cashSavings?: number; cpfRefundFromSales?: number; cashProceedsFromSale?: number }>>({});
  /** Self-employed: CPF OA monthly for timeline rows after the first (row 0 uses Profile.cpfOAMonthly). */
  const [selfEmployedCpfOAMonthlyByRow, setSelfEmployedCpfOAMonthlyByRow] = useState<
    Record<string, Record<number, number>>
  >({});

  const buildPurchasePlannerHistoryState = useCallback((): SharedCalculatorHistoryEntry['purchasePlannerState'] => {
    const normalizedTargetPurchasePrice = parseNumberFromFormatted(targetPurchasePrice).replace(
      /^0+(?=\d)/,
      ''
    );
    const filteredManualEdits: NonNullable<SharedCalculatorHistoryEntry['purchasePlannerState']>['manualEdits'] = {};

    Object.entries(manualEdits).forEach(([rowIndex, edit]) => {
      const savedEdit = {
        cashSavings: edit.cashSavings,
        cpfRefundFromSales: edit.cpfRefundFromSales,
        cashProceedsFromSale: edit.cashProceedsFromSale,
      };

      if (
        savedEdit.cashSavings !== undefined ||
        savedEdit.cpfRefundFromSales !== undefined ||
        savedEdit.cashProceedsFromSale !== undefined
      ) {
        filteredManualEdits[Number(rowIndex)] = savedEdit;
      }
    });

    if (normalizedTargetPurchasePrice || Object.keys(filteredManualEdits).length > 0) {
      return {
        targetPurchasePrice: normalizedTargetPurchasePrice || undefined,
        manualEdits: Object.keys(filteredManualEdits).length > 0 ? filteredManualEdits : undefined,
      };
    }

    return undefined;
  }, [manualEdits, targetPurchasePrice]);

  function restorePurchasePlannerHistoryState(
    state?: SharedCalculatorHistoryEntry['purchasePlannerState']
  ) {
    setManualEdits(state?.manualEdits ?? {});
    setTargetPurchasePrice(state?.targetPurchasePrice ?? '0');
  }

  // Get current month in Singapore timezone (0-11, where 0=Jan, 11=Dec)
  const getCurrentSingaporeMonth = () => {
    const singaporeTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
    return singaporeTime.getMonth();
  };
  
  const [startMonth, setStartMonth] = useState<number>(getCurrentSingaporeMonth());

  /**
   * Calculate CPF OA contribution based on age
   * Source: CPF Allocation Rates from 1 January 2026
   * https://www.cpf.gov.sg/service/sfc/servlet.shepherd/document/download/069IW000005qFrJYAU
   * 
   * Total CPF contribution rates (Employee + Employer):
   * - 55 & below: 37% (Employee: 20%, Employer: 17%)
   * - Above 55-60: 34% (Employee: 18%, Employer: 16%)
   * - Above 60-65: 25% (Employee: 12.5%, Employer: 12.5%)
   * - Above 65-70: 16.5% (Employee: 7.5%, Employer: 9%)
   * - Above 70: 12.5% (Employee: 5%, Employer: 7.5%)
   * 
   * OA allocation rates by age:
   * - 35 & below: 62.17%
   * - Above 35-45: 56.77%
   * - Above 45-50: 51.36%
   * - Above 50-55: 40.55%
   * - Above 55-60: 35.30%
   * - Above 60-65: 14.00%
   * - Above 65-70: 6.07%
   * - Above 70: 8.00%
   */

  // Check for transfer data from individual calculators and auto-populate
  useEffect(() => {
    if (transferData && transferData.source === 'mortgage-loan') {
      const data = transferData.data;
      
      // Estimate monthly salary from max monthly mortgage (assuming TDSR 55% for private)
      const estimatedSalary = data.maxMonthlyMortgage ? Math.round(data.maxMonthlyMortgage / 0.55) : 5000;
      const estimatedAge = 30; // Default age
      
      // Calculate CPF OA monthly based on age and salary
      const cpfOAMonthly = calculateCPFOAMonthly(estimatedSalary, estimatedAge);
      
      // Create profile with estimated/default values
      // CPF and cash savings can be empty/0
      const newProfile: Profile = {
        id: '1',
        currentAge: estimatedAge,
        monthlySalary: estimatedSalary,
        monthlyDebts: 0,
        cashSavingsMonthly: 0,
        cpfOAMonthly: cpfOAMonthly, // Calculated automatically
        currentCPFOA: 0, // Can be empty/0
        currentCashSavings: 0, // Can be empty/0
      };

      // Infer property type from LTV
      let inferredPropertyType: 'PRIVATE' | 'EC' | 'HDB' = 'PRIVATE';
      if (data.loanToValue) {
        if (data.loanToValue >= 90) {
          inferredPropertyType = 'HDB';
        } else if (data.loanToValue >= 75) {
          inferredPropertyType = 'PRIVATE';
        } else {
          inferredPropertyType = 'PRIVATE';
        }
      }

      // Set target purchase price from max property price
      const targetPrice = data.maxTotalPropertyPrice ? data.maxTotalPropertyPrice.toString() : '0';

      saveSharedHistory(
        {
          profiles: buildSharedProfilesFromPurchasePlanner([newProfile]),
          propertyType: mapPurchasePlannerPropertyTypeToShared(inferredPropertyType),
          purchasePlannerState: {
            targetPurchasePrice: targetPrice,
          },
        },
        `Mortgage Loan Calculator - ${formatTimestamp(new Date())}`
      );

      // Load the newly created history entry
      setProfiles([newProfile]);
      setTargetPurchasePrice(targetPrice);
      setPropertyType(inferredPropertyType);

      // Clear transfer data
      setTransferData(null);
    } else if (transferData && transferData.source === 'sales-proceed') {
      const data = transferData.data;
      const cpfRefundFromSales = data.cpfRefundFromSales ?? 0;
      const cashProceedsFromSale = data.cashProceedsFromSale ?? 0;
      setManualEdits((prev) => ({
        ...prev,
        0: {
          ...prev[0],
          cpfRefundFromSales,
          cashProceedsFromSale,
        },
      }));
      setTransferData(null);
    }
  }, [transferData]);

  const clearPlanner = () => {
    setPropertyType('PRIVATE');
    setLoanType('bank');
    setCpfHousingGrant('0');
    setTargetPurchasePrice('0');
    setIncludeFundsInMaxPurchasePrice(false);
    setProfiles([createDefaultPlannerProfile()]);
    setProfileLoaded(false);
    setManualEdits({});
    setSelfEmployedCpfOAMonthlyByRow({});
    setTimelineData([]);
    setStartMonth(getCurrentSingaporeMonth());
    setSelectedRowIndex(null);
    setSettings({ salaryIncrement: 0, absdEnabled: false, absdRate: 0 });
    setZoom(1);
    setTransferData(null);

    lastAppliedSelectionIdRef.current = null;
    setSelectedExample('');
    setActiveSelection(null);
    showHistoryToast('Purchase Planner reset');
    setClearDialogOpen(false);
  };

  // ── ABSD derivation (mirrors AffordabilityCalculator) ──────────────────────
  // Derives the Additional Buyer's Stamp Duty rate from the selected buyer
  // profile(s): per-borrower citizenship (+ FTA remission) and property count,
  // with IRAS married-couple joint-purchase remission for a 2-buyer group.
  // A manual rate set via Settings (ABSD toggle) overrides the derived rate.
  const absdInfo = useMemo(() => {
    if (settings.absdEnabled) {
      return {
        manual: true,
        rate: settings.absdRate,
        remissionApplied: false,
        rateBeforeRemission: settings.absdRate,
        isMarriedCouple: false,
        borrowers: [] as {
          label: string;
          citizenship: CitizenshipType;
          citizenshipLabel: string;
          fta: boolean;
          effective: CitizenshipType;
          propertyCount: PropertyCount;
          rate: number;
        }[],
      };
    }

    const borrowers = getSelectedProfiles().map((p, i) => {
      const citizenship = ((p.Citizenship as CitizenshipType) || 'SC');
      const fta = Number(p.FtaNationalityInd) === 1;
      const effective = getEffectiveCitizenshipForAbsd(citizenship, fta);
      const propertyCount = propertiesOwnedToPropertyCount(p.PropertiesOwned);
      return {
        label: `Borrower ${i + 1}`,
        citizenship,
        citizenshipLabel: CITIZENSHIP_LABELS[citizenship] ?? citizenship,
        fta,
        effective,
        propertyCount,
        rate: getAbsdRate(effective, propertyCount),
      };
    });

    if (borrowers.length === 0) {
      return { manual: false, rate: 0, remissionApplied: false, rateBeforeRemission: 0, isMarriedCouple: false, borrowers };
    }

    if (borrowers.length === 1) {
      const r = borrowers[0].rate;
      return { manual: false, rate: r, remissionApplied: false, rateBeforeRemission: r, isMarriedCouple: false, borrowers };
    }

    if (borrowers.length === 2) {
      const isMarriedCouple =
        selectedProfile?.type === 'group' && (selectedProfile.group.Relationship || 'SPOUSE') === 'SPOUSE';
      const joint = getJointAbsdRate(
        borrowers[0].effective,
        borrowers[0].propertyCount,
        borrowers[1].effective,
        borrowers[1].propertyCount,
        isMarriedCouple,
      );
      return {
        manual: false,
        rate: joint.rate,
        remissionApplied: joint.remissionApplied,
        rateBeforeRemission: joint.rateBeforeRemission,
        isMarriedCouple,
        borrowers,
      };
    }

    // 3+ buyers: IRAS applies the highest applicable rate (no married-couple remission).
    const rate = Math.max(...borrowers.map((b) => b.rate));
    return { manual: false, rate, remissionApplied: false, rateBeforeRemission: rate, isMarriedCouple: false, borrowers };
  }, [settings.absdEnabled, settings.absdRate, selectedProfile, getSelectedProfiles]);

  /**
   * Find the highest whole-dollar property price P satisfying all three
   * conditions simultaneously:
   *
   *   1. P ≤ maxLoan + cashAvailable + cpfAvailable  (absolute resource cap)
   *   2. 0.05 × P ≤ cashAvailable                   (5% cash-only downpayment,
   *                                                   bank loans only)
   *   3. Downpayment(P) + BSD(P) + ABSD(P) ≤ cashAvailable + cpfAvailable
   *      where Downpayment(P) = P − min(maxLoan, ltvRatio × P)
   *
   * For HDB loans there is no mandatory 5% cash tranche, so condition 2 is
   * skipped (requireCashMinimum = false).
   *
   * BSD uses residential rates from 15 Feb 2023 (1–6%), rounded down to
   * nearest whole dollar. ABSD = P × absdRate% (derived from the buyer
   * profile(s); 0 when not applicable).
   *
   * All three cost functions are monotonically increasing in P, so binary
   * search correctly finds the maximum feasible price.
   */
  const calculateAffordableToday = (
    maxLoan: number,
    ltvRatio: number,
    cashAvailable: number,
    cpfAvailable: number,
    requireCashMinimum: boolean,
    absdRate: number,
  ): number => {
    const totalCapital = cashAvailable + cpfAvailable;

    const satisfies = (p: number): boolean => {
      // Condition 2: 5% cash-only tranche (bank loans)
      if (requireCashMinimum && 0.05 * p > cashAvailable) return false;

      // Condition 3: total upfront cost within combined capital
      const loanAmount = Math.min(maxLoan, ltvRatio * p);
      const downpayment = p - loanAmount;
      const bsd = calculateBsd(p);
      const absd = Math.round(p * (absdRate / 100));
      return downpayment + bsd + absd <= totalCapital;
    };

    let lo = 0;
    let hi = maxLoan + totalCapital; // absolute upper bound (condition 1)

    while (lo < hi) {
      const mid = Math.floor((lo + hi + 1) / 2);
      if (satisfies(mid)) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return lo;
  };

  /**
   * Calculate max loan based on MSR/TDSR with stress test rate
   * Source: MAS Guidelines
   * - TDSR (Total Debt Servicing Ratio): 55% for private properties
   *   Reference: MAS Notice 645 - Property Market Measures
   *   https://www.mas.gov.sg/regulation/banking/notices-and-circulars
   * - MSR (Mortgage Servicing Ratio): 30% for HDB/EC
   *   Reference: MAS Property Market Measures
   *   https://www.mas.gov.sg/regulation/banking/notices-and-circulars
   * 
   * Loan calculation uses standard amortization formula with stress test rate:
   * Max Loan = Max Monthly Payment × [(1+r)^n - 1] / [r × (1+r)^n]
   * where r = monthly stress test interest rate, n = number of payments
   * 
   * Stress test rate ensures borrowers can afford loans even if rates rise
   */
  function calculateMaxLoan(
    monthlyIncome: number,
    _age: number,
    loanTenure: number,
    stressTestRate: number = globalSettings.stressTestRate,
    monthlyDebts: number = 0
  ): number {
    const maxTotalDebtTDSR = monthlyIncome * 0.55;
    const maxMortgageTDSR = Math.max(0, maxTotalDebtTDSR - monthlyDebts);
    const maxMortgageMSR = monthlyIncome * 0.30;
    const maxMonthlyPayment =
      getBasePropertyType(propertyType) === 'PRIVATE'
        ? maxMortgageTDSR
        : Math.min(maxMortgageTDSR, maxMortgageMSR);

    const monthlyRate = stressTestRate / 100 / 12;
    const numPayments = loanTenure * 12;

    if (monthlyRate === 0) {
      return maxMonthlyPayment * numPayments;
    }

    const maxLoanAmount =
      maxMonthlyPayment *
      ((Math.pow(1 + monthlyRate, numPayments) - 1) /
        (monthlyRate * Math.pow(1 + monthlyRate, numPayments)));

    return Math.round(maxLoanAmount);
  }

  // Calculate timeline data
  useEffect(() => {
      const generateTimeline = () => {
        const data: TimelineData[] = [];
        // Get current date/time in Singapore timezone (UTC+8)
        const singaporeTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
        const currentYear = singaporeTime.getFullYear();
        // Use selected start month, or current month if December
        const selectedMonth = startMonth;
        const startDate = new Date(currentYear, selectedMonth, 1);
        // Get combined values and IWAA
        const combined = getCombinedValues(profiles);
        const iwaa = combined.iwaa.roundedUp; // Use rounded-up value for calculations
        
        // Calculate years needed to reach age 65 based on IWAA
        const targetAge = 65;
        const years = Math.max(0, targetAge - iwaa); // iwaa is already a whole number

      let currentCPFOA = combined.totalCurrentCPFOA;
      let currentCashSavings = combined.totalCurrentCashSavings;
      let currentSalary = combined.totalMonthlySalary;

      for (let i = 0; i <= years; i++) {
        const date = new Date(startDate);
        date.setFullYear(date.getFullYear() + i);
        // Format date using Singapore timezone
        const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'Asia/Singapore' });
        const year = date.getFullYear();
        const dateStr = `${month} ${year}`;
        // Use IWAA for age calculation, incrementing by year
        const age = iwaa + i; // iwaa is already a whole number (roundedUp)

        if (i > 0) {
          currentSalary = currentSalary * (1 + settings.salaryIncrement / 100);
        }

        // Calculate CPF OA monthly for each profile and sum them
        let totalCPFOAMonthly = 0;
        profiles.forEach(profile => {
          const profileAge = profile.currentAge + i;
          const profileSalary = i === 0 ? profile.monthlySalary : profile.monthlySalary * Math.pow(1 + settings.salaryIncrement / 100, i);
          if (profile.selfEmployedInd === 1) {
            const rowOverride = selfEmployedCpfOAMonthlyByRow[profile.id]?.[i];
            totalCPFOAMonthly += rowOverride !== undefined ? rowOverride : profile.cpfOAMonthly;
          } else {
            totalCPFOAMonthly += calculateCPFOAMonthly(profileSalary, profileAge);
          }
        });
        
        // Check if this row has manual edits
        const manualEdit = manualEdits[i];
        
        // For first row (i === 0), use manual edit if available, otherwise use combined current CPF OA (editable)
        // For subsequent rows, accumulate normally
        if (i === 0) {
          if (manualEdit?.cpfOA !== undefined) {
            // Use manual edit value (handle both number and empty string cases)
            if (manualEdit.cpfOA === '' || manualEdit.cpfOA === null) {
              currentCPFOA = 0;
            } else {
              currentCPFOA = typeof manualEdit.cpfOA === 'number' ? manualEdit.cpfOA : combined.totalCurrentCPFOA;
            }
          } else {
            currentCPFOA = combined.totalCurrentCPFOA;
          }
        } else {
          // Grow by one year: 2.5% p.a. OA interest on the balance + this year's contributions
          // (contributions also earn interest from the month after each is received).
          currentCPFOA = growCpfOaOneYear(currentCPFOA, totalCPFOAMonthly);
        }

        // Check if this row has manual edits for cash savings
        if (manualEdit?.cashSavings !== undefined) {
          // Use manually edited value
          currentCashSavings = manualEdit.cashSavings;
        } else {
          // Normal accumulation - sum all profiles' monthly cash savings
          if (i === 0) {
            currentCashSavings = combined.totalCurrentCashSavings;
          } else {
            currentCashSavings += combined.totalCashSavingsMonthly * 12;
          }
        }

        // Maximum loan tenure: Until age 65, with MAS/CPF maximum tenure cap
        // HDB: Conservative cap at 25 years (applies to both HDB loans and bank loans)
        // HDB loans: Maximum 25 years (per CPF Board guidelines)
        // Bank loans: 30 years for Private/EC, 25 years for HDB (conservative approach)
        const basePropertyType = getBasePropertyType(propertyType);
        let maxTenureCap: number;
        if (propertyType === 'HDB') {
          // HDB: Conservative cap at 25 years for both HDB loans and bank loans
          // HDB Loan: Maximum 25 years per CPF Board guidelines
          // Bank Loan: Also capped at 25 years for conservative calculation
          // Source: https://www.cpf.gov.sg/member/infohub/educational-resources/3-differences-between-hdb-loan-and-bank-loan
          maxTenureCap = 25;
        } else {
          // Bank loans for other property types: MAS guidelines
          maxTenureCap = basePropertyType === 'PRIVATE' ? 30 : basePropertyType === 'EC' ? 30 : 30; // Private and EC capped at 30 years
        }
        const maxLoanTenure = Math.min(65 - age, maxTenureCap);
        // Use stress test rate for max loan calculation; factor in monthly debt from profile (TDSR/MSR)
        const maxLoan = calculateMaxLoan(currentSalary, age, maxLoanTenure, stressTestRate, combined.totalMonthlyDebts);

        // Calculate LTV ratio based on loan tenure, property type, and loan type
        // HDB loans: Fixed at 75% (no reduction for tenure > 30 years per CPF Board guidelines)
        // Bank loans: LTV reduces when loan tenure exceeds 30 years (MAS regulations)
        // Sources:
        // - HDB loans: https://www.cpf.gov.sg/member/infohub/educational-resources/3-differences-between-hdb-loan-and-bank-loan
        // - Bank loans: https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits
        const getLTVRatio = (tenure: number, propertyType: string, loanType: 'hdb' | 'bank'): number => {
          if (propertyType === 'HDB' && loanType === 'hdb') {
            // HDB Loan for HDB Resale: Fixed at 75% (25% downpayment) regardless of tenure
            // Per CPF Board guidelines, HDB loans maintain 75% LTV
            return 0.75;
          } else if (propertyType === 'HDB' && loanType === 'bank') {
            // Bank Loan for HDB: Fixed at 75% since tenure is capped at 25 years (conservative)
            // With 25-year cap, tenure will always be ≤ 30 years, so LTV remains at 75%
            return 0.75;
          } else {
            // Private/EC: 75% for tenure ≤ 30 years, 55% for tenure > 30 years
            return tenure > 30 ? 0.55 : 0.75;
          }
        };
        
        const loanToValueRatio = getLTVRatio(maxLoanTenure, propertyType, loanType);
        
        // Calculate max purchase price based on max loan capacity
        // This is the theoretical maximum based on loan eligibility (TDSR/MSR)
        // Max Purchase Price = Max Loan ÷ LTV Ratio
        const maxPurchasePrice = Math.round(maxLoan / loanToValueRatio);

        // Parse target purchase price from state
        const targetPurchasePriceValue = parseFloat(parseNumberFromFormatted(targetPurchasePrice)) || 0;

        // Row 0 seeds CPF Refund / Cash Proceeds; later rows inherit unless overridden (needed before purchase price when "include funds")
        const cpfRefundFromSales =
          manualEdit?.cpfRefundFromSales !== undefined
            ? manualEdit.cpfRefundFromSales
            : (manualEdits[0]?.cpfRefundFromSales ?? 0);
        const cashProceedsFromSale =
          manualEdit?.cashProceedsFromSale !== undefined
            ? manualEdit.cashProceedsFromSale
            : (manualEdits[0]?.cashProceedsFromSale ?? 0);
        const totalCapitalWithGrant =
          currentCPFOA +
          currentCashSavings +
          cpfRefundFromSales +
          cashProceedsFromSale +
          parseFloat(cpfHousingGrant || '0');

        let purchasePriceForCalculation: number;
        if (targetPurchasePriceValue > 0) {
          purchasePriceForCalculation = targetPurchasePriceValue;
        } else if (includeFundsInMaxPurchasePrice) {
          purchasePriceForCalculation = Math.round(maxPurchasePrice + totalCapitalWithGrant);
        } else {
          purchasePriceForCalculation = maxPurchasePrice;
        }

        // Loan at property price: normally capped by income (TDSR/MSR). When "Include funds" is on without a
        // target price, the purchase price is income max + funds — use full LTV on that total (not min with income max).
        const loanAtPurchasePrice =
          targetPurchasePriceValue > 0
            ? Math.min(maxLoan, Math.round(purchasePriceForCalculation * loanToValueRatio))
            : includeFundsInMaxPurchasePrice
              ? Math.round(purchasePriceForCalculation * loanToValueRatio)
              : Math.min(maxLoan, Math.round(purchasePriceForCalculation * loanToValueRatio));

        // For HDB Resale with HDB Loan: Combined 25% downpayment (can be CPF/Cash)
        // For Bank Loans: Split into 5% cash + 20% remaining (CPF/Cash)
        let downpayment5Cash: number;
        let remainingDownpayment: number;

        if (propertyType === 'HDB' && loanType === 'hdb') {
          // HDB Loan: Combined 25% downpayment (can be paid using CPF OA or cash)
          // Source: https://www.cpf.gov.sg/member/infohub/educational-resources/3-differences-between-hdb-loan-and-bank-loan
          if (targetPurchasePriceValue > 0) {
            const maxLoanForTargetPrice = Math.round(targetPurchasePriceValue * loanToValueRatio);
            const actualLoan = Math.min(maxLoan, maxLoanForTargetPrice);
            const totalDownpayment = Math.round(targetPurchasePriceValue - actualLoan);
            downpayment5Cash = Math.max(0, totalDownpayment); // Combined 25% for HDB loans
            remainingDownpayment = 0; // Not used for HDB loans
          } else if (includeFundsInMaxPurchasePrice) {
            const totalDownpayment = Math.round(purchasePriceForCalculation - loanAtPurchasePrice);
            downpayment5Cash = Math.max(0, totalDownpayment);
            remainingDownpayment = 0;
          } else {
            downpayment5Cash = Math.round(purchasePriceForCalculation * 0.25); // Combined 25%
            remainingDownpayment = 0; // Not used for HDB loans
          }
        } else {
          // Bank loans: Split downpayment structure
          if (targetPurchasePriceValue > 0) {
            downpayment5Cash = Math.round(purchasePriceForCalculation * 0.05);
            const maxLoanForTargetPrice = Math.round(targetPurchasePriceValue * loanToValueRatio);
            const actualLoan = Math.min(maxLoan, maxLoanForTargetPrice);
            remainingDownpayment = Math.round(targetPurchasePriceValue - actualLoan - downpayment5Cash);
            remainingDownpayment = Math.max(0, remainingDownpayment);
          } else if (includeFundsInMaxPurchasePrice) {
            downpayment5Cash = Math.round(purchasePriceForCalculation * 0.05);
            remainingDownpayment = Math.max(
              0,
              Math.round(purchasePriceForCalculation - loanAtPurchasePrice - downpayment5Cash)
            );
          } else {
            downpayment5Cash = Math.round(purchasePriceForCalculation * 0.05);
            remainingDownpayment = Math.round(purchasePriceForCalculation * 0.2);
          }
        }

        const stampDuty = calculateBsd(purchasePriceForCalculation);
        const absd = Math.round(purchasePriceForCalculation * (absdInfo.rate / 100));
        const totalOutlay = downpayment5Cash + remainingDownpayment + stampDuty + absd;
        const difference = totalCapitalWithGrant - totalOutlay;

        // Cash: savings + cash proceeds from prior sale
        // CPF:  OA accumulated + CPF refund from prior sale + CPF housing grant
        const cashAvailableForAffordable = Math.round(currentCashSavings + cashProceedsFromSale);
        const cpfAvailableForAffordable = Math.round(
          currentCPFOA + cpfRefundFromSales + parseFloat(cpfHousingGrant || '0'),
        );
        // 5% cash constraint applies to all bank loans; HDB loans allow full CPF
        const requireCashMin = !(propertyType === 'HDB' && loanType === 'hdb');
        const affordableToday = calculateAffordableToday(
          maxLoan,
          loanToValueRatio,
          cashAvailableForAffordable,
          cpfAvailableForAffordable,
          requireCashMin,
          absdInfo.rate,
        );

        data.push({
          date: dateStr,
          age,
          monthlySalary: Math.round(currentSalary),
          cashSavingsMonthly: combined.totalCashSavingsMonthly,
          cpfOAMonthly: totalCPFOAMonthly,
          cpfOAAccumulated: Math.round(currentCPFOA),
          cashSavingsAccumulated: Math.round(currentCashSavings),
          cpfRefundFromSales: Math.round(cpfRefundFromSales),
          cashProceedsFromSale: Math.round(cashProceedsFromSale),
          totalCapital: Math.round(totalCapitalWithGrant),
          targetPurchasePrice: targetPurchasePriceValue,
          maxPurchasePrice,
          purchasePriceForCalculation,
          maxLoan,
          loanAtPurchasePrice,
          loanTenure: maxLoanTenure,
          loanToValueRatio,
          downpayment5Cash,
          remainingDownpayment,
          stampDuty,
          absd,
          totalOutlay,
          difference: Math.round(difference),
          affordableToday,
        });
      }

      setTimelineData(data);
    };

    generateTimeline();
  }, [
    profiles,
    settings,
    propertyType,
    loanType,
    cpfHousingGrant,
    manualEdits,
    targetPurchasePrice,
    startMonth,
    selfEmployedCpfOAMonthlyByRow,
    includeFundsInMaxPurchasePrice,
    stressTestRate,
    absdInfo.rate,
  ]);

  // Generate timeline data for a specific property type (used for PDF export)
  const generateTimelineForType = useCallback((
    forPropertyType: 'PRIVATE' | 'EC' | 'HDB',
    forLoanType: 'hdb' | 'bank',
  ): { timelineData: TimelineData[]; profileRowData: ProfileRowData[][] } => {
    const data: TimelineData[] = [];
    const profileRowDataArr: ProfileRowData[][] = profiles.map(() => []);

    const singaporeTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
    const currentYear = singaporeTime.getFullYear();
    const startDate = new Date(currentYear, startMonth, 1);
    const combined = getCombinedValues(profiles);
    const iwaa = combined.iwaa.roundedUp;
    const targetAge = 65;
    const years = Math.max(0, targetAge - iwaa);

    let currentCPFOA = combined.totalCurrentCPFOA;
    let currentCashSavings = combined.totalCurrentCashSavings;
    let currentSalary = combined.totalMonthlySalary;

    const localGetBasePropertyType = (type: string): 'PRIVATE' | 'EC' | 'HDB' => {
      if (type.startsWith('PRIVATE')) return 'PRIVATE';
      if (type.startsWith('EC')) return 'EC';
      if (type.startsWith('HDB')) return 'HDB';
      return 'PRIVATE';
    };

    const localCalculateMaxLoan = (
      monthlyIncome: number,
      _age: number,
      loanTenure: number,
      stRate: number,
      monthlyDebts: number,
    ): number => {
      const maxTotalDebtTDSR = monthlyIncome * 0.55;
      const maxMortgageTDSR = Math.max(0, maxTotalDebtTDSR - monthlyDebts);
      const maxMortgageMSR = monthlyIncome * 0.30;
      const maxMonthlyPayment =
        localGetBasePropertyType(forPropertyType) === 'PRIVATE'
          ? maxMortgageTDSR
          : Math.min(maxMortgageTDSR, maxMortgageMSR);
      const monthlyRate = stRate / 100 / 12;
      const numPayments = loanTenure * 12;
      if (monthlyRate === 0) return maxMonthlyPayment * numPayments;
      return Math.round(
        maxMonthlyPayment *
        ((Math.pow(1 + monthlyRate, numPayments) - 1) /
          (monthlyRate * Math.pow(1 + monthlyRate, numPayments))),
      );
    };

    for (let i = 0; i <= years; i++) {
      const date = new Date(startDate);
      date.setFullYear(date.getFullYear() + i);
      const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'Asia/Singapore' });
      const year = date.getFullYear();
      const dateStr = `${month} ${year}`;
      const age = iwaa + i;

      if (i > 0) currentSalary = currentSalary * (1 + settings.salaryIncrement / 100);

      let totalCPFOAMonthly = 0;
      profiles.forEach((profile, pi) => {
        const profileAge = profile.currentAge + i;
        const profileSalary = i === 0
          ? profile.monthlySalary
          : profile.monthlySalary * Math.pow(1 + settings.salaryIncrement / 100, i);
        let profileCPFOAMonthly: number;
        if (profile.selfEmployedInd === 1) {
          const rowOverride = selfEmployedCpfOAMonthlyByRow[profile.id]?.[i];
          profileCPFOAMonthly = rowOverride !== undefined ? rowOverride : profile.cpfOAMonthly;
        } else {
          profileCPFOAMonthly = calculateCPFOAMonthly(profileSalary, profileAge);
        }
        totalCPFOAMonthly += profileCPFOAMonthly;

        profileRowDataArr[pi].push({
          age: profileAge,
          assessableIncome: Math.round(profileSalary),
          cashSavingsMonthly: profile.cashSavingsMonthly,
          cpfOAMonthly: profileCPFOAMonthly,
        });
      });

      const manualEdit = manualEdits[i];
      if (i === 0) {
        if (manualEdit?.cpfOA !== undefined) {
          if (manualEdit.cpfOA === '' || manualEdit.cpfOA === null) {
            currentCPFOA = 0;
          } else {
            currentCPFOA = typeof manualEdit.cpfOA === 'number' ? manualEdit.cpfOA : combined.totalCurrentCPFOA;
          }
        } else {
          currentCPFOA = combined.totalCurrentCPFOA;
        }
      } else {
        // Grow by one year: 2.5% p.a. OA interest on the balance + this year's contributions
        // (contributions also earn interest from the month after each is received).
        currentCPFOA = growCpfOaOneYear(currentCPFOA, totalCPFOAMonthly);
      }

      if (manualEdit?.cashSavings !== undefined) {
        currentCashSavings = manualEdit.cashSavings;
      } else {
        if (i === 0) {
          currentCashSavings = combined.totalCurrentCashSavings;
        } else {
          currentCashSavings += combined.totalCashSavingsMonthly * 12;
        }
      }

      const basePropertyType = localGetBasePropertyType(forPropertyType);
      let maxTenureCap: number;
      if (forPropertyType === 'HDB') {
        maxTenureCap = 25;
      } else {
        maxTenureCap = basePropertyType === 'PRIVATE' ? 30 : 30;
      }
      const maxLoanTenure = Math.min(65 - age, maxTenureCap);
      const maxLoan = localCalculateMaxLoan(currentSalary, age, maxLoanTenure, stressTestRate, combined.totalMonthlyDebts);

      const getLTVRatio = (tenure: number, propType: string, lt: 'hdb' | 'bank'): number => {
        if (propType === 'HDB' && lt === 'hdb') return 0.75;
        if (propType === 'HDB' && lt === 'bank') return 0.75;
        return tenure > 30 ? 0.55 : 0.75;
      };

      const loanToValueRatio = getLTVRatio(maxLoanTenure, forPropertyType, forLoanType);
      const maxPurchasePrice = Math.round(maxLoan / loanToValueRatio);

      const targetPurchasePriceValue = parseFloat(parseNumberFromFormatted(targetPurchasePrice)) || 0;
      const purchasePriceForCalculation = targetPurchasePriceValue > 0 ? targetPurchasePriceValue : maxPurchasePrice;

      let downpayment5Cash: number;
      let remainingDownpayment: number;

      if (forPropertyType === 'HDB' && forLoanType === 'hdb') {
        if (targetPurchasePriceValue > 0) {
          const maxLoanForTargetPrice = Math.round(targetPurchasePriceValue * loanToValueRatio);
          const actualLoan = Math.min(maxLoan, maxLoanForTargetPrice);
          downpayment5Cash = Math.max(0, Math.round(targetPurchasePriceValue - actualLoan));
          remainingDownpayment = 0;
        } else {
          downpayment5Cash = Math.round(purchasePriceForCalculation * 0.25);
          remainingDownpayment = 0;
        }
      } else {
        downpayment5Cash = Math.round(purchasePriceForCalculation * 0.05);
        if (targetPurchasePriceValue > 0) {
          const maxLoanForTargetPrice = Math.round(targetPurchasePriceValue * loanToValueRatio);
          const actualLoan = Math.min(maxLoan, maxLoanForTargetPrice);
          remainingDownpayment = Math.max(0, Math.round(targetPurchasePriceValue - actualLoan - downpayment5Cash));
        } else {
          remainingDownpayment = Math.round(purchasePriceForCalculation * 0.20);
        }
      }

      const stampDuty = calculateBsd(purchasePriceForCalculation);
      const absd = Math.round(purchasePriceForCalculation * (absdInfo.rate / 100));
      const totalOutlay = downpayment5Cash + remainingDownpayment + stampDuty + absd;

      const cpfRefundFromSales =
        manualEdit?.cpfRefundFromSales !== undefined
          ? manualEdit.cpfRefundFromSales
          : (manualEdits[0]?.cpfRefundFromSales ?? 0);
      const cashProceedsFromSale =
        manualEdit?.cashProceedsFromSale !== undefined
          ? manualEdit.cashProceedsFromSale
          : (manualEdits[0]?.cashProceedsFromSale ?? 0);
      const totalCapitalWithGrant =
        currentCPFOA +
        currentCashSavings +
        cpfRefundFromSales +
        cashProceedsFromSale +
        parseFloat(cpfHousingGrant || '0');
      const difference = totalCapitalWithGrant - totalOutlay;

      const cashAvailableForAffordable = Math.round(currentCashSavings + cashProceedsFromSale);
      const cpfAvailableForAffordable = Math.round(
        currentCPFOA + cpfRefundFromSales + parseFloat(cpfHousingGrant || '0'),
      );
      const requireCashMin = !(forPropertyType === 'HDB' && forLoanType === 'hdb');
      const affordableToday = calculateAffordableToday(
        maxLoan,
        loanToValueRatio,
        cashAvailableForAffordable,
        cpfAvailableForAffordable,
        requireCashMin,
        absdInfo.rate,
      );

      data.push({
        date: dateStr,
        age,
        monthlySalary: Math.round(currentSalary),
        cashSavingsMonthly: combined.totalCashSavingsMonthly,
        cpfOAMonthly: totalCPFOAMonthly,
        cpfOAAccumulated: Math.round(currentCPFOA),
        cashSavingsAccumulated: Math.round(currentCashSavings),
        cpfRefundFromSales: Math.round(cpfRefundFromSales),
        cashProceedsFromSale: Math.round(cashProceedsFromSale),
        totalCapital: Math.round(totalCapitalWithGrant),
        targetPurchasePrice: targetPurchasePriceValue,
        maxPurchasePrice,
        purchasePriceForCalculation,
        maxLoan,
        loanAtPurchasePrice: Math.round(Math.min(maxLoan, purchasePriceForCalculation * loanToValueRatio)),
        loanTenure: maxLoanTenure,
        loanToValueRatio,
        downpayment5Cash,
        remainingDownpayment,
        stampDuty,
        absd,
        totalOutlay,
        difference: Math.round(difference),
        affordableToday,
      });
    }

    return { timelineData: data, profileRowData: profileRowDataArr };
  }, [profiles, settings, manualEdits, targetPurchasePrice, cpfHousingGrant, startMonth, selfEmployedCpfOAMonthlyByRow, stressTestRate, absdInfo.rate]);

  // Build profile row data for current timeline (used for PDF export of current type)
  const currentProfileRowData = useMemo((): ProfileRowData[][] => {
    return profiles.map((profile, _pi) => {
      return timelineData.map((_row, index) => {
        const profileAge = profile.currentAge + index;
        const profileSalary = index === 0
          ? profile.monthlySalary
          : profile.monthlySalary * Math.pow(1 + settings.salaryIncrement / 100, index);
        let profileCPFOAMonthly: number;
        if (profile.selfEmployedInd === 1) {
          const rowOverride = selfEmployedCpfOAMonthlyByRow[profile.id]?.[index];
          profileCPFOAMonthly = rowOverride !== undefined ? rowOverride : profile.cpfOAMonthly;
        } else {
          profileCPFOAMonthly = calculateCPFOAMonthly(profileSalary, profileAge);
        }
        return {
          age: profileAge,
          assessableIncome: Math.round(profileSalary),
          cashSavingsMonthly: profile.cashSavingsMonthly,
          cpfOAMonthly: profileCPFOAMonthly,
        };
      });
    });
  }, [profiles, timelineData, settings.salaryIncrement, selfEmployedCpfOAMonthlyByRow]);

  // Handle PDF export
  const handleExportPdf = useCallback(async () => {
    setPdfExporting(true);
    try {
      const selectedProfiles = getSelectedProfiles();
      const profileNamesList = profiles.map((profile, idx) => {
        return selectedProfiles[idx]?.Name || `Profile ${idx + 1}`;
      });
      const clientName = selectedProfile ? getSelectedName() ?? undefined : undefined;

      const buildSection = (
        forPropertyType: 'PRIVATE' | 'EC' | 'HDB',
        forLoanType: 'hdb' | 'bank',
        tlData: typeof timelineData,
        prd: ProfileRowData[][],
      ): PurchasePlannerPdfSection => {
        const displayMap = { PRIVATE: 'Private', EC: 'EC', HDB: 'HDB' };
        return {
          propertyType: forPropertyType,
          propertyTypeDisplay: displayMap[forPropertyType],
          loanType: forLoanType,
          timelineData: tlData,
          profileRowData: prd,
          showRemainingDownpayment: !(forPropertyType === 'HDB' && forLoanType === 'hdb'),
          showAffordableToday: true,
        };
      };

      let sections: PurchasePlannerPdfSection[];

      if (pdfExportScope === 'current') {
        sections = [buildSection(propertyType, loanType, timelineData, currentProfileRowData)];
      } else {
        // Generate for all property types
        const privateResult = generateTimelineForType('PRIVATE', 'bank');
        const ecResult = generateTimelineForType('EC', 'bank');
        const hdbResult = generateTimelineForType('HDB', 'bank');

        sections = [
          buildSection('PRIVATE', 'bank', privateResult.timelineData, privateResult.profileRowData),
          buildSection('EC', 'bank', ecResult.timelineData, ecResult.profileRowData),
          buildSection('HDB', 'bank', hdbResult.timelineData, hdbResult.profileRowData),
        ];
      }

      logPdfExport('purchase_planner', currentUser);
      await exportPurchasePlannerToPDF({
        clientName,
        agentInfo: currentUser ?? undefined,
        profileNames: profileNamesList,
        sections,
      });
    } finally {
      setPdfExporting(false);
      setPdfExportDialogOpen(false);
    }
  }, [
    pdfExportScope, propertyType, loanType, timelineData, currentProfileRowData,
    profiles, selectedProfile, getSelectedProfiles, getSelectedName, currentUser,
    generateTimelineForType,
  ]);

  // Function to manually show arrow for current step
  const handleShowArrow = () => {
    const step = tutorialSteps[tutorialStep];
    if (step.targetElement) {
      showPointer(step.targetElement, 'bottom');
    }
  };

  // Reset pointer when tutorial step changes (but don't auto-show)
  useEffect(() => {
    if (tutorialOpen && tutorialStep < tutorialSteps.length) {
      // Reset pointer state when step changes
      setPointerVisible(false);
      setPointerTarget(null);
      setDialogMinimized(false);
      // Update progress when step changes
      updateTutorialProgress(tutorialStep, false);
    } else {
      // Reset when tutorial closes
      setPointerVisible(false);
      setPointerTarget(null);
      setDialogMinimized(false);
    }
  }, [tutorialStep, tutorialOpen]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  /** NumericFormat also fires onValueChange when the controlled value prop changes (source "prop"); updating state from that causes feedback loops and flicker. */
  const isUserNumericInput = (sourceInfo: SourceInfo) => sourceInfo.source === 'event';

  const getProfileTooltipContent = (
    sourceProfile?: {
      Name?: string;
      MonthlyIncome?: number | null;
      OtherAnnualIncome?: number | null;
      Saving?: number | null;
      Cpf?: number | null;
      CarLoan?: number | null;
      HousingLoan?: number | null;
      CreditCard?: number | null;
      OtherLoan?: number | null;
      SelfEmployedInd?: number | null;
    },
    profile?: Profile
  ) => {
    const monthlyIncome = sourceProfile?.MonthlyIncome ?? 0;
    const otherAnnualIncome = sourceProfile?.OtherAnnualIncome ?? 0;
    const isSelfEmployed = sourceProfile?.SelfEmployedInd === 1;
    const assessableIncome = calculateAssessableIncome(
      String(monthlyIncome),
      String(otherAnnualIncome),
      sourceProfile?.SelfEmployedInd
    );
    const monthlyDebts =
      (sourceProfile?.CarLoan ?? 0) +
      (sourceProfile?.HousingLoan ?? 0) +
      (sourceProfile?.CreditCard ?? 0) +
      (sourceProfile?.OtherLoan ?? 0);

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {sourceProfile?.Name || 'Profile Details'}
          {isSelfEmployed && (
            <span style={{ marginLeft: 6, fontSize: '0.65rem', color: '#D97706', fontWeight: 700 }}>
              [Self Employed — 30% haircut]
            </span>
          )}
        </Typography>
        <Typography variant="caption" component="div">
          Age: {profile?.currentAge ?? '-'}
        </Typography>
        <Typography variant="caption" component="div">
          Monthly Income: {formatCurrency(monthlyIncome)}
          {isSelfEmployed && (
            <span style={{ marginLeft: 4, color: '#D97706' }}>→ {formatCurrency(monthlyIncome * 0.7)} (70% recognised)</span>
          )}
        </Typography>
        <Typography variant="caption" component="div">
          Other Annual Income: {formatCurrency(otherAnnualIncome)}
        </Typography>
        <Typography variant="caption" component="div">
          Assessable Income (Monthly): {formatCurrency(assessableIncome)}
        </Typography>
        <Typography variant="caption" component="div">
          Cash / Saving: {formatCurrency(sourceProfile?.Saving ?? 0)}
        </Typography>
        <Typography variant="caption" component="div">
          CPF OA: {formatCurrency(sourceProfile?.Cpf ?? 0)}
        </Typography>
        <Typography variant="caption" component="div">
          Monthly Debt Obligations: {formatCurrency(monthlyDebts)}
        </Typography>
      </Box>
    );
  };

  // Format number with commas (for input fields)
  const formatNumberWithCommas = (value: string | number): string => {
    if (value === '' || value === null || value === undefined) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(numValue)) return value.toString();
    return numValue.toLocaleString('en-SG');
  };

  // Parse number from formatted string (remove commas)
  const parseNumberFromFormatted = (value: string): string => {
    return value.replace(/,/g, '');
  };

  return (
    <Box sx={{ 
      p: { xs: 0.5, sm: 0.5, md: 0.5 }, 
      width: '100%', 
      maxWidth: '100vw',
      overflowX: 'auto',
      mx: 0,
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
            Purchase Planner
          </Typography>
          <Tooltip title={`Tutorial - Learn how to use Super Calculator${tutorialProgress.completed ? ' (Completed)' : tutorialProgress.started ? ' (In Progress)' : ' (Not Started)'}`}>
            <IconButton
              onClick={() => {
                setTutorialOpen(true);
                setTutorialStep(tutorialProgress.lastStep || 0);
                updateTutorialProgress(tutorialProgress.lastStep || 0, false);
              }}
              color={getTutorialButtonColor()}
              size="small"
              sx={{
                border: '2px solid',
                borderColor: 'currentColor',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <SchoolIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }} data-tutorial="info-buttons">
          <Tooltip title="Search key terms and references">
            <IconButton 
              onClick={() => {
                const searchTerm = prompt('Enter search term (e.g., LTV, TDSR, MSR, CPF, ABSD):', '');
                if (searchTerm) {
                  const searchUrls: Record<string, string> = {
                    'ltv': 'https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits',
                    'loan tenure': 'https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits',
                    'tdsr': 'https://www.mas.gov.sg/regulation/explainers/new-housing-loans/msr-and-tdsr-rules',
                    'msr': 'https://www.mas.gov.sg/regulation/explainers/new-housing-loans/msr-and-tdsr-rules',
                    'cpf': 'https://www.cpf.gov.sg',
                    'cpf oa': 'https://www.cpf.gov.sg/service/sfc/servlet.shepherd/document/download/069IW000005qFrJYAU',
                    'absd': 'https://www.iras.gov.sg/taxes/stamp-duty/stamp-duty-for-property',
                    'stamp duty': 'https://www.iras.gov.sg/taxes/stamp-duty/stamp-duty-for-property',
                    'bsd': 'https://www.iras.gov.sg/taxes/stamp-duty/stamp-duty-for-property',
                  };
                  const lowerTerm = searchTerm.toLowerCase();
                  const url = searchUrls[lowerTerm] || `https://www.google.com/search?q=${encodeURIComponent(searchTerm + ' singapore MAS')}`;
                  window.open(url, '_blank');
                }
              }} 
              color="primary" 
              size={isMobile ? 'small' : 'medium'}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View all formulas and logic">
            <IconButton onClick={() => setInfoOpen(true)} color="primary" size={isMobile ? 'small' : 'medium'}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={() => setSettingsOpen(true)} color="primary" size={isMobile ? 'small' : 'medium'}>
            <SettingsIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Start Month Selector & Load Example */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Start Month:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }} data-tutorial="start-month">
          <InputLabel>Month</InputLabel>
          <Select
            value={startMonth}
            label="Month"
            onChange={(e: any) => setStartMonth(Number(e.target.value))}
            disabled={getCurrentSingaporeMonth() === 11} // Disable if current month is December
          >
            {[
              'January', 'February', 'March', 'April', 'May', 'June',
              'July', 'August', 'September', 'October', 'November', 'December'
            ].map((month, index) => (
              <MenuItem key={index} value={index}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {getCurrentSingaporeMonth() === 11 && (
          <Typography variant="caption" color="text.secondary">
            (Current month is December - cannot change)
          </Typography>
        )}

        {/* Load Example / History Dropdown */}
        <FormControl size="small" sx={{ minWidth: 250 }} data-tutorial="load-profile">
          <InputLabel shrink>Load Example / History</InputLabel>
          <Select
            displayEmpty
            open={loadSelectionOpen}
            onOpen={() => setLoadSelectionOpen(true)}
            onClose={() => setLoadSelectionOpen(false)}
            value={displayedSelectionValue}
            label="Load Example / History"
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Select example or history…
                  </Typography>
                );
              }
              if (value in presetExamples) {
                return presetExamples[value as keyof typeof presetExamples].name;
              }
              const entry = historyEntries.find((item) => item.id === value);
              return entry ? entry.label : value;
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value.startsWith('example')) {
                loadExample(value as keyof typeof presetExamples);
              } else if (value.startsWith('history-')) {
                handleLoadFromHistory(value);
              } else if (value === CLEAR_SELECTION_VALUE) {
                clearLoadedSelection();
              } else {
                clearLoadedSelection();
              }
              setLoadSelectionOpen(false);
            }}
            sx={{ minWidth: 300 }}
          >
            <MenuItem
              value={CLEAR_SELECTION_VALUE}
              sx={{
                my: 0.75,
                mx: 1,
                py: 1.25,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'grey.100',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              Clear Selection
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem value="example1">{presetExamples.example1.name}</MenuItem>
            <MenuItem value="example2">{presetExamples.example2.name}</MenuItem>
            <MenuItem value="example3">{presetExamples.example3.name}</MenuItem>
            {historyEntries.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
                  History
                </Typography>
                {historyEntries.map((entry) => (
                  <MenuItem
                    key={entry.id}
                    value={entry.id}
                    onClick={(e) => {
                      if (editingHistoryId === entry.id) {
                        e.stopPropagation();
                        return;
                      }
                      handleLoadFromHistory(entry.id);
                      setLoadSelectionOpen(false);
                    }}
                    sx={{ py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                  >
                    {editingHistoryId === entry.id ? (
                      <TextField
                        value={editingHistoryLabel}
                        onChange={(e) => setEditingHistoryLabel(e.target.value)}
                        onBlur={() => {
                          if (editingHistoryLabel.trim()) {
                            void commitHistoryRename(entry.id, editingHistoryLabel.trim());
                          } else {
                            cancelEditing();
                          }
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            if (editingHistoryLabel.trim()) {
                              void commitHistoryRename(entry.id, editingHistoryLabel.trim());
                            } else {
                              cancelEditing();
                            }
                          } else if (e.key === 'Escape') {
                            cancelEditing();
                          }
                        }}
                        autoFocus
                        size="small"
                        fullWidth
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {entry.label}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            startEditingLabel(entry.id);
                          }}
                          sx={{
                            opacity: 0.7,
                            '&:hover': { opacity: 1, backgroundColor: 'action.hover' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            void handleDeleteHistoryEntry(entry.id);
                          }}
                          sx={{
                            opacity: 0.7,
                            '&:hover': { opacity: 1, backgroundColor: 'action.hover' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </MenuItem>
                ))}
              </>
            )}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          startIcon={<SaveIcon />}
          onClick={() => void handleSaveToHistory()}
        >
          Save to History
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={() => setClearDialogOpen(true)}
        >
          Clear
        </Button>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={12} md={true}>
            <Typography variant="body2" gutterBottom>
              Property Type
            </Typography>
            <ToggleButtonGroup
              value={propertyType}
              exclusive
              onChange={(_, value) => value && setPropertyType(value)}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              data-tutorial="property-type"
              sx={{ 
                flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' },
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  flex: { xs: '1 1 auto', sm: '1 1 auto', md: '0 1 auto' },
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap',
                }
              }}
            >
              {/* Private left-most and default selection as per new UX */}
              {[
                { value: 'PRIVATE' as const, label: 'Private' },
                { value: 'EC' as const, label: 'EC' },
                { value: 'HDB' as const, label: 'HDB' },
              ].map(option => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
          {(propertyType === 'HDB' || propertyType === 'EC') && (
            <>
              {propertyType === 'HDB' && (
                <Grid item xs={12} sm={6} md="auto">
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    Loan Packages:
                  </FormLabel>
                  <RadioGroup
                    row
                    value={loanType}
                    onChange={(e) => setLoanType(e.target.value as 'hdb' | 'bank')}
                    sx={{ flexWrap: 'wrap', gap: 0.5 }}
                  >
                    <FormControlLabel value="hdb" control={<Radio size="small" />} label="HDB Loan" />
                    <FormControlLabel value="bank" control={<Radio size="small" />} label="Bank Loan" />
                  </RadioGroup>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md="auto">
                <Typography variant="body2" gutterBottom>
                  CPF Housing Grant
                </Typography>
                <TextField
                  type="text"
                  inputMode="numeric"
                  value={cpfHousingGrant ? formatNumberWithCommas(cpfHousingGrant) : ''}
                  onChange={(e) => {
                    const rawValue = parseNumberFromFormatted(e.target.value);
                    const cleanedValue = rawValue.replace(/^0+(?=\d)/, '');
                    setCpfHousingGrant(cleanedValue);
                  }}
                  size="small"
                  placeholder="0"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{
                    width: { xs: '100%', sm: '100%', md: '180px' },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={12} md="auto">
            <Typography variant="body2" gutterBottom>
              Target Purchase Price
            </Typography>
            <TextField
              type="text"
              inputMode="numeric"
              value={targetPurchasePrice ? formatNumberWithCommas(targetPurchasePrice) : ''}
              data-tutorial="target-price"
              onChange={(e) => {
                const rawValue = parseNumberFromFormatted(e.target.value);
                // Remove leading zeros
                const cleanedValue = rawValue.replace(/^0+(?=\d)/, '');
                setTargetPurchasePrice(cleanedValue);
              }}
              size="small"
              placeholder="0"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{
                width: { xs: '100%', sm: '100%', md: '200px' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
            />
          </Grid>
          {profiles.length > 1 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <span>Income Weighted Average Age (IWAA): {formatIWAA(getCombinedValues(profiles).iwaa)} years</span>
                <InfoIconButton>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Income Weighted Average Age (IWAA)
                    </Typography>
                    <Typography variant="body1" component="div">
                      IWAA = Σ(Age × Assessable Income) / Σ(Assessable Income)
                    </Typography>
                    {(() => {
                      const iwaa = getCombinedValues(profiles).iwaa;
                      return iwaa.original !== iwaa.roundedUp ? (
                        <>
                          <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                            Calculated IWAA: {iwaa.original.toFixed(1)} years
                          </Typography>
                          <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            Rounded Up IWAA: {iwaa.roundedUp} years (used for loan calculations)
                          </Typography>
                          <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                            The IWAA is rounded UP to the next whole number for conservative age calculation, which results in fewer available loan years.
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                          IWAA: {iwaa.roundedUp} years (used for loan calculations)
                        </Typography>
                      );
                    })()}
                    <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                      Used for joint loan applications to determine loan tenure limits.
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                      Source: MAS Property Financing Guidelines
                    </Typography>
                    <Typography variant="body1" component="div">
                      <a href="https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                        View MAS Loan Tenure and LTV Limits
                      </a>
                    </Typography>
                  </Box>
                </InfoIconButton>
              </Typography>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Profile Details Section and Zoom Controls - Inline */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: 2, 
        mb: 2,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}>
        {/* Profile Details - Beautified */}
        {selectedProfile && (
          <Paper
            elevation={2}
            {...(profiles.length === 1 ? { 'data-tutorial': 'current-profile' } : {})}
            sx={{
              p: 1.5,
              px: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: 2,
              flex: 1,
              minWidth: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                <PersonIcon sx={{ fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {getSelectedName()}:
                </Typography>
                {profiles.map((profile, index) => {
                  const selectedProfiles = getSelectedProfiles();
                  const sourceProfile = selectedProfiles[index];
                  return (
                    <Box key={profile.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={getProfileTooltipContent(sourceProfile, profile)} arrow placement="bottom">
                        <Chip
                          label={sourceProfile?.Name || `Profile ${index + 1}`}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.8rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            fontWeight: 600,
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            cursor: 'help',
                          }}
                        />
                      </Tooltip>
                      <Typography variant="body2" sx={{ ml: 0.5 }}>
                        Age: {profile.currentAge} • Assessable Income: {formatCurrency(profile.monthlySalary)}
                      </Typography>
                      {index < profiles.length - 1 && (
                        <Typography variant="body2" sx={{ mx: 0.5, opacity: 0.8 }}>•</Typography>
                      )}
                    </Box>
                  );
                })}
                {profiles.length > 1 && (
                  <>
                    <Typography variant="body2" sx={{ mx: 0.5, opacity: 0.8 }}>•</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      IWAA: {formatIWAA(getCombinedValues(profiles).iwaa)} years
                      {getCombinedValues(profiles).iwaa.original !== getCombinedValues(profiles).iwaa.roundedUp && (
                        <span style={{ opacity: 0.9 }}> (↑{getCombinedValues(profiles).iwaa.roundedUp})</span>
                      )}
                    </Typography>
                  </>
                )}
              </Box>
              {profileManagerLauncher && selectedProfile && (
                <Tooltip
                  title={selectedProfile.type === 'group' ? 'Edit group' : 'Edit profile'}
                  placement="left"
                >
                  <IconButton
                    size="small"
                    aria-label={selectedProfile.type === 'group' ? 'Edit group' : 'Edit profile'}
                    onClick={() => {
                      if (selectedProfile.type === 'group') {
                        profileManagerLauncher.openEditGroup(selectedProfile.id);
                      } else {
                        profileManagerLauncher.openEditProfile(selectedProfile.id);
                      }
                    }}
                    sx={{
                      flexShrink: 0,
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Paper>
        )}
        
        {/* Zoom Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }} data-tutorial="zoom-controls">
        <Tooltip title="Zoom Out">
          <IconButton onClick={handleZoomOut} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="View Reset - Fit table to viewport">
          <Button
            variant="outlined"
            startIcon={<FitScreenIcon />}
            onClick={handleViewReset}
            size="small"
            sx={{ minWidth: 'auto' }}
          >
            View Reset
          </Button>
        </Tooltip>
        <Tooltip title="Zoom In">
          <IconButton onClick={handleZoomIn} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
          {Math.round(zoom * 100)}%
        </Typography>
        </Box>
        <Tooltip title="Export PDF Report">
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => setPdfExportDialogOpen(true)}
            sx={{ minWidth: 'auto', flexShrink: 0 }}
          >
            Export PDF
          </Button>
        </Tooltip>
      </Box>

      {/* Clear Planner Confirmation Dialog */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartAltIcon color="error" />
          Reset Purchase Planner?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will reset all Purchase Planner values to their original defaults and clear the selected example or history entry.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your saved history entries will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
          <Button onClick={clearPlanner} variant="contained" color="error">
            Clear &amp; Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Export Dialog */}
      <Dialog open={pdfExportDialogOpen} onClose={() => setPdfExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export PDF Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose which property types to include in the report:
          </Typography>
          <RadioGroup
            value={pdfExportScope}
            onChange={(e) => setPdfExportScope(e.target.value as 'current' | 'all')}
          >
            <FormControlLabel
              value="current"
              control={<Radio />}
              label={`Current property type only (${getPropertyTypeDisplay(propertyType)}${propertyType === 'HDB' ? ` — ${loanType === 'hdb' ? 'HDB Loan' : 'Bank Loan'}` : ''})`}
            />
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="All property types (Private, EC, HDB)"
            />
          </RadioGroup>
        </DialogContent>
        {/* Building the report takes a moment — show it running rather than a dead dialog. */}
        {pdfExporting && <LinearProgress sx={{ height: 4 }} />}
        <DialogActions>
          <Button onClick={() => setPdfExportDialogOpen(false)} disabled={pdfExporting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleExportPdf()}
            disabled={pdfExporting}
            startIcon={<PictureAsPdfIcon />}
          >
            {pdfExporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timeline Table */}
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        data-tutorial="timeline-table"
        sx={{
          maxHeight: tableContainerMaxHeight,
          overflow: 'auto',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'auto',
          overflowY: 'auto',
          mx: 0,
          position: 'relative',
          isolation: 'isolate',
          backgroundColor: 'background.default',
          borderRadius: 2,
          border: `1px solid ${tableColors.headerBorderColor}`,
          boxShadow: isDark
            ? '0 4px 24px rgba(0,0,0,0.4)'
            : '0 4px 24px rgba(0,0,0,0.08)',
          overscrollBehavior: 'contain',
          overscrollBehaviorX: 'contain',
          overscrollBehaviorY: 'contain',
          WebkitOverflowScrolling: 'touch',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
          '&::-webkit-scrollbar': {
            height: '6px',
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
            },
          },
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
            padding: tableBodyPadding,
            fontSize: tableBodyFontSize,
            borderBottom: `1px solid ${tableColors.headerBorderColor}`,
            transition: 'background-color 0.15s ease',
          },
          '& .MuiTableHead .MuiTableCell-root': {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflow: 'visible',
            lineHeight: 1.35,
            padding: tableHeaderPadding,
            fontSize: tableHeaderFontSize,
            fontWeight: 600,
            verticalAlign: 'middle',
            textAlign: 'center',
            letterSpacing: '0.02em',
          },
          '& .MuiTableBody .MuiTableRow-root': {
            position: 'relative',
            zIndex: 1,
          },
        }}
      >
          <Table
            ref={tableRef}
            sx={{
              minWidth: tableMinWidth,
              width: 'max-content',
              tableLayout: 'auto',
              zoom,
            }}
          >
          <TableHead>
            {/* Section Headers */}
            <TableRow
              ref={sectionHeaderRowRef}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 8,
                minHeight: { xs: 40, sm: 44, md: 48 },
                '& .MuiTableCell-root': {
                  py: { xs: 0.2, sm: 0.3, md: 0.4 },
                  borderBottom: `2px solid ${tableColors.sectionBorder}`,
                  verticalAlign: 'middle',
                },
              }}
            >
              <TableCell
                colSpan={1}
                sx={{
                  background: tableColors.timelineHeader,
                  color: 'white',
                  fontWeight: 700,
                  textAlign: 'center',
                  position: 'sticky',
                  left: 0,
                  zIndex: 20,
                  minWidth: timelineColumnWidths,
                  borderRight: `2px solid ${tableColors.sectionBorder}`,
                  fontSize: isLaptopViewport ? { xs: '0.74rem', sm: '0.8rem', md: '0.9rem' } : { xs: '0.72rem', sm: '0.78rem', md: '0.84rem' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Timeline
              </TableCell>
              {/* Profile sections - Each profile gets its own grouped columns */}
              {profiles.map((profile, profileIndex) => {
                const colorTheme = tableColors.buyerSectionColors[profileIndex] || tableColors.buyerSectionColors[2];
                const selectedProfiles = getSelectedProfiles();
                const sourceProfile = selectedProfiles[profileIndex];
                const profileName = sourceProfile?.Name || `Profile ${profileIndex + 1}`;
                const currentSingaporeYear = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' })).getFullYear();
                const birthYear =
                  sourceProfile?.BirthYear ??
                  (profile.currentAge ? currentSingaporeYear - profile.currentAge : null);
                
                return (
                <TableCell
                  key={`profile-header-${profile.id}`}
                  colSpan={4}
                  {...(profiles.length >= 2 && profileIndex === 1 ? { 'data-tutorial': 'add-profile' } : {})}
                  sx={{
                    backgroundColor: colorTheme.bg,
                    color: colorTheme.text,
                    fontWeight: 700,
                    textAlign: 'center',
                    fontSize: { xs: '0.72rem', sm: '0.78rem', md: '0.84rem' },
                    boxShadow: `inset 0 -1px 0 ${alpha(theme.palette.common.black, isDark ? 0.22 : 0.16)}`,
                  }}
                >
                  {profiles.length > 1 ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, width: '100%' }}
                      data-tutorial="current-profile"
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15, minWidth: 0 }}>
                        <Tooltip title={getProfileTooltipContent(sourceProfile, profile)} arrow placement="bottom">
                          <Typography sx={{ cursor: 'help', fontSize: { xs: '0.72rem', sm: '0.8rem', md: '0.88rem' }, lineHeight: 1.1, fontWeight: 700 }}>
                            {profileName}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" sx={{ mt: 0.1, fontSize: { xs: '0.62rem', sm: '0.66rem', md: '0.72rem' }, lineHeight: 1.1, opacity: 0.92 }}>
                          Age: {profile.currentAge}
                          {birthYear ? ` (${birthYear})` : ''}
                        </Typography>
                      </Box>
                      {profiles.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeProfile(profile.id)}
                          sx={{ color: colorTheme.text, '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.1) }, p: 0.25 }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, width: '100%' }}
                      {...(profiles.length === 1 && !selectedProfile ? { 'data-tutorial': 'current-profile' } : {})}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15, minWidth: 0 }}>
                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem', md: '0.88rem' }, lineHeight: 1.1, fontWeight: 700 }}>
                          Current Profile
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.1, fontSize: { xs: '0.62rem', sm: '0.66rem', md: '0.72rem' }, lineHeight: 1.1, opacity: 0.92 }}>
                          Age: {profile.currentAge}
                          {birthYear ? ` (${birthYear})` : ''}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={addProfile}
                        sx={{ color: colorTheme.text, '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.1) }, p: 0.5 }}
                        data-tutorial="add-profile"
                      >
                        <AddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </TableCell>
                );
              })}
              <TableCell
                colSpan={3}
                sx={{
                  background: tableColors.fundsHeader,
                  color: 'white',
                  fontWeight: 700,
                  textAlign: 'center',
                  fontSize: { xs: '0.72rem', sm: '0.78rem', md: '0.84rem' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              >
                <InfoInlineTrigger
                  infoContent={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {profiles.length > 1 ? 'Combined ' : ''}Funds (Accumulated)
                      </Typography>
                      <Typography variant="caption" component="div">
                        "Accumulated" means cumulative balances carried forward over time.
                      </Typography>
                      {profiles.length > 1 && (
                        <>
                          <Typography variant="caption" component="div">
                            Sum of all profiles' accumulated CPF OA and Cash Savings
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Calculated using Income Weighted Average Age (IWAA) for joint applications
                          </Typography>
                        </>
                      )}
                      <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                        Source: MAS Property Financing Guidelines
                      </Typography>
                    </Box>
                  }
                  iconSx={{ color: 'white' }}
                  layout="inline"
                >
                  <Box data-tutorial="combined-funds">
                    {profiles.length > 1 ? (
                      <>
                        <Box component="span" sx={{ fontWeight: 700, textDecoration: 'underline' }}>Combined</Box> Funds
                      </>
                    ) : (
                      <>Funds</>
                    )}
                  </Box>
                </InfoInlineTrigger>
              </TableCell>
              <TableCell
                colSpan={parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? 11 : 10}
                sx={{
                  background: tableColors.propertyHeader,
                  color: 'white',
                  fontWeight: 700,
                  textAlign: 'center',
                  fontSize: { xs: '0.72rem', sm: '0.78rem', md: '0.84rem' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              >
                Property Purchase ({getPropertyTypeDisplay(propertyType)})
              </TableCell>
            </TableRow>

            {/* Column Headers - Single Unified Row */}
            <TableRow
              sx={{
                position: 'sticky',
                top: `${sectionHeaderRowPx}px`,
                zIndex: 7,
                backgroundColor: tableColors.columnHeaderBg,
                '& .MuiTableCell-root': {
                  backgroundColor: 'inherit',
                  color: theme.palette.text.primary,
                  borderBottom: `2px solid ${tableColors.sectionBorder}`,
                  verticalAlign: 'middle',
                },
              }}
            >
              {/* Common Columns */}
              <TableCell
                sx={{
                  fontWeight: 700,
                  backgroundColor: `${tableColors.columnHeaderBg} !important`,
                  position: 'sticky',
                  left: 0,
                  zIndex: 19,
                  color: theme.palette.text.primary,
                  minWidth: timelineColumnWidths,
                  borderRight: `2px solid ${tableColors.sectionBorder}`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: isLaptopViewport ? { xs: '0.68rem', sm: '0.74rem', md: '0.84rem' } : { xs: '0.66rem', sm: '0.72rem', md: '0.78rem' },
                }}
              >
                Timeline
              </TableCell>
              {/* Profile Columns - Grouped by profile: Age, Assessable Income, Cash Savings Monthly, CPF OA Monthly */}
              {profiles.map((profile, profileIndex) => {
                const colorTheme = tableColors.buyerHeaderColors[profileIndex] || tableColors.buyerHeaderColors[2];
                const selectedProfiles = getSelectedProfiles();
                const sourceProfile = selectedProfiles[profileIndex];
                const profileName = sourceProfile?.Name || `Profile ${profileIndex + 1}`;

                return (
                <React.Fragment key={profile.id}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: colorTheme.bg,
                      color: colorTheme.text,
                      position: 'static',
                      minWidth: ageColumnWidths,
                      width: ageColumnWidths,
                      textAlign: 'center',
                    }}
                  >
                    <InfoInlineTrigger
                      infoContent={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Age ({profileName})
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            IWAA is used for loan calculations: {formatIWAA(getCombinedValues(profiles).iwaa)} years ({getCombinedValues(profiles).iwaa.roundedUp} used for calculations)
                          </Typography>
                        </Box>
                      }
                    >
                      <Box>Age</Box>
                    </InfoInlineTrigger>
                  </TableCell>
                  <HeaderInfoTableCell sx={{ backgroundColor: colorTheme.bg, color: theme.palette.text.primary, minWidth: responsiveColumnWidths(62, 70, 76, 82) }} infoContent={
                    sourceProfile?.SelfEmployedInd === 1
                      ? `Assessable monthly income${profiles.length > 1 ? ` (${profileName})` : ''} = Monthly Income × 70% + (Other Annual Income ÷ 12 × 70%). Self-employed: 30% haircut applied to base income per MAS rules.`
                      : `Assessable monthly income${profiles.length > 1 ? ` (${profileName})` : ''} = Monthly Income + (Other Annual Income ÷ 12 × 70%). Editable in the first row of the timeline table.`
                  }>
                    <>Assessable<br />Income{sourceProfile?.SelfEmployedInd === 1 ? <><br /><span style={{ fontSize: '0.6rem', color: '#D97706', fontWeight: 700 }}>Self Employed −30%</span></> : null}</>
                  </HeaderInfoTableCell>
                  <HeaderInfoTableCell sx={{ backgroundColor: colorTheme.bg, color: theme.palette.text.primary, minWidth: responsiveColumnWidths(64, 74, 80, 86) }} infoContent={`Monthly cash savings contribution${profiles.length > 1 ? ` (${profileName})` : ''}. CPF OA is calculated automatically.`}>
                    <>Cash Savings<br />(Monthly)</>
                  </HeaderInfoTableCell>
                  <HeaderInfoTableCell
                    sx={{ backgroundColor: colorTheme.bg, color: theme.palette.text.primary, minWidth: responsiveColumnWidths(64, 74, 80, 86), borderRight: `2px solid ${tableColors.sectionBorder}` }}
                    infoContent={
                      profile.selfEmployedInd === 1 ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            CPF OA (Monthly){profiles.length > 1 ? ` (${profileName})` : ''}
                          </Typography>
                          <Typography variant="caption" component="div">
                            Self-employed: statutory salary-based CPF may not apply. Each year row is editable — enter the monthly amount you use for planning (for example voluntary CPF or your typical OA inflow).
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Row 1 sets the default for new rows until you override a specific year. Accumulation uses each row’s monthly amount × 12.
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            CPF OA Monthly Contribution{profiles.length > 1 ? ` (${profileName})` : ''}
                          </Typography>
                          <Typography variant="caption" component="div">
                            = (Salary × Total CPF Rate) × OA Allocation Ratio
                          </Typography>
                          <Typography variant="caption" component="div">
                            Total CPF: ≤55 (37%), 55-60 (34%), 60-65 (25%), 65-70 (16.5%), &gt;70 (12.5%)
                          </Typography>
                          <Typography variant="caption" component="div">
                            OA ratios: ≤35 (62.17%), 35-45 (56.77%), 45-50 (51.36%), 50-55 (40.55%), 55-60 (35.30%), 60-65 (14.00%), 65-70 (6.07%), &gt;70 (8.00%)
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            Source: CPF Allocation Rates from 1 Jan 2026
                          </Typography>
                          <Typography variant="caption" component="div">
                            <a href="#" onClick={(e) => { e.preventDefault(); setSourceViewerOpen(true); setSourceViewerUrl('https://www.cpf.gov.sg/service/sfc/servlet.shepherd/document/download/069IW000005qFrJYAU'); }} style={{ color: '#90caf9', textDecoration: 'underline', cursor: 'pointer' }}>
                              View Official CPF Document
                            </a>
                          </Typography>
                        </Box>
                      )
                    }
                  >
                    <>
                      CPF OA<br />
                      (Monthly)
                      {profile.selfEmployedInd === 1 ? (
                        <span style={{ fontSize: '0.6rem', color: '#D97706', fontWeight: 700, display: 'block' }}>Self Employed — editable</span>
                      ) : null}
                    </>
                  </HeaderInfoTableCell>
                </React.Fragment>
                );
              })}

              {/* Funds Accumulated Columns */}
              <HeaderInfoTableCell
                sx={{ minWidth: responsiveColumnWidths(108, 118, 126, 134), px: isLaptopViewport ? 0.5 : 0.75 }}
                infoContent={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {profiles.length > 1 ? 'Combined ' : ''}CPF (OA + Refund)
                    </Typography>
                    <Typography variant="caption" component="div">
                      CPF available for purchase = CPF OA accumulated + CPF refund from sales.
                    </Typography>
                    {profiles.length > 1 && (
                      <Typography variant="caption" component="div">
                        Sum of all profiles' CPF OA balances plus refund.
                      </Typography>
                    )}
                    <Typography variant="caption" component="div">
                      Editable field (year 1) controls CPF OA base. Refund remains included in total.
                    </Typography>
                    {profiles.length > 1 && (
                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        Calculated using IWAA: {formatIWAA(getCombinedValues(profiles).iwaa)} years ({getCombinedValues(profiles).iwaa.roundedUp} used for calculations)
                      </Typography>
                    )}
                    <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                      Source: CPF.gov.sg{profiles.length > 1 ? ' & MAS Guidelines' : ''}
                    </Typography>
                    <Typography variant="caption" component="div">
                      <a href="#" onClick={(e) => { e.preventDefault(); setSourceViewerOpen(true); setSourceViewerUrl('https://www.cpf.gov.sg/service/sfc/servlet.shepherd/document/download/069IW000005qFrJYAU'); }} style={{ color: '#90caf9', textDecoration: 'underline', cursor: 'pointer' }}>
                        View CPF Allocation Rates
                      </a>
                    </Typography>
                  </Box>
                }
              >
                {profiles.length > 1 ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 700, textDecoration: 'underline' }}>Combined</Box><br />CPF (OA + Refund)
                  </>
                ) : (
                  <>CPF<br />(OA + Refund)</>
                )}
              </HeaderInfoTableCell>
              <HeaderInfoTableCell
                sx={{ minWidth: responsiveColumnWidths(108, 118, 126, 134), px: isLaptopViewport ? 0.5 : 0.75 }}
                infoContent={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {profiles.length > 1 ? 'Combined ' : ''}Cash (Savings + Proceed)
                    </Typography>
                    <Typography variant="caption" component="div">
                      Cash available for purchase = accumulated cash savings + cash proceeds from sale.
                    </Typography>
                    {profiles.length > 1 && (
                      <Typography variant="caption" component="div">
                        Sum of all profiles' cash savings balances plus proceeds.
                      </Typography>
                    )}
                    <Typography variant="caption" component="div">
                      Editable field. Cash savings portion is adjusted while proceeds remain included.
                    </Typography>
                    {profiles.length > 1 && (
                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        Calculated using IWAA: {formatIWAA(getCombinedValues(profiles).iwaa)} years ({getCombinedValues(profiles).iwaa.roundedUp} used for calculations)
                      </Typography>
                    )}
                  </Box>
                }
              >
                {profiles.length > 1 ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 700, textDecoration: 'underline' }}>Combined</Box><br />Cash (Savings + Proceed)
                  </>
                ) : (
                  <>Cash<br />(Savings + Proceed)</>
                )}
              </HeaderInfoTableCell>
              <HeaderInfoTableCell
                sx={{ minWidth: responsiveColumnWidths(108, 118, 126, 134), px: isLaptopViewport ? 0.5 : 0.75, borderRight: `2px solid ${tableColors.sectionBorder}` }}
                infoContent={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Total Capital
                    </Typography>
                    <Typography variant="caption" component="div">
                      = CPF OA Accumulated + Cash Savings Accumulated + CPF Refund From Sales + Cash Proceeds From Sale{parseFloat(cpfHousingGrant || '0') > 0 ? ` + CPF Housing Grant (${formatCurrency(parseFloat(cpfHousingGrant))})` : ''}
                    </Typography>
                  </Box>
                }
              >
                <>Total<br />Capital</>
              </HeaderInfoTableCell>

              {/* Property Purchase Columns */}
              {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                <HeaderInfoTableCell
                  sx={{ minWidth: responsiveColumnWidths(104, 116, 124, 132) }}
                  infoContent={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Target Purchase Price
                      </Typography>
                      <Typography variant="caption" component="div">
                        User-specified target property price
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        All calculations (downpayment, stamp duty, difference) are based on this target price.
                      </Typography>
                    </Box>
                  }
                >
                  <Box component="span" sx={{ fontWeight: 700, textDecoration: 'underline' }}>Target</Box><br />Purchase Price
                </HeaderInfoTableCell>
              ) : null}
              <HeaderInfoTableCell
                sx={{ minWidth: { xs: 130, md: 'auto' }, width: { md: 'auto' } }}
                infoContent={
                  <Box sx={{ maxWidth: 420 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Max Purchase Price (Base on Income)
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                      <strong>Formula:</strong> Max Purchase Price = Max Loan ÷ LTV Ratio (loan affordability). Use <strong>Include funds</strong> to add Total Capital to this figure for a combined view.
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                      This is the theoretical maximum property price you can afford based on your loan eligibility (TDSR/MSR limits).
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                      <strong>LTV Ratios:</strong>
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ pl: 2, mb: 1 }}>
                      • Private Property: 75% (25% downpayment)
                      <br />• Executive Condominium (EC): 75% (25% downpayment)
                      <br />• HDB Resale: 75% (25% downpayment) - MAS guidelines
                      <br />• Other HDB: 90% (10% downpayment)
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 1, fontWeight: 600, backgroundColor: 'rgba(255,255,255,0.1)', p: 0.5, borderRadius: 0.5 }}>
                      <strong>Example:</strong>
                      <br />Max Loan = $562,352, LTV = 75%
                      <br />Max Purchase Price = $562,352 ÷ 0.75 = $749,803
                      <br />
                      <br />
                      <em>Note: This is based on loan capacity. Check &quot;Difference&quot; column to see if you have sufficient capital for downpayment and stamp duty.</em>
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                      Sources:
                    </Typography>
                    <Typography variant="caption" component="div">
                      • <a href="https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                        MAS Loan Tenure and LTV Limits
                      </a>
                    </Typography>
                    <Typography variant="caption" component="div">
                      • <a href="https://www.mas.gov.sg/regulation/banking/notices-and-circulars" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                        MAS Banking Regulations
                      </a>
                    </Typography>
                  </Box>
                }
              >
                <>Max<br />Purchase Price</>
              </HeaderInfoTableCell>
              <HeaderInfoTableCell
                data-tutorial="max-loan-column"
                sx={{ minWidth: responsiveColumnWidths(88, 96, 104, 112) }}
                infoContent={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Maximum Loan Amount
                    </Typography>
                    <Typography variant="caption" component="div">
                      Private: TDSR 55% of monthly income
                    </Typography>
                    <Typography variant="caption" component="div">
                      HDB/EC: MSR 30% of monthly income
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      <strong>Stress Test Rate:</strong> {stressTestRate}%
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                      Sources:
                    </Typography>
                    <Typography variant="caption" component="div">
                      • TDSR/MSR: <a href="https://www.mas.gov.sg/regulation/banking/notices-and-circulars" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                        MAS Banking Regulations (Notice 645)
                      </a>
                    </Typography>
                    <Typography variant="caption" component="div">
                      • Stress Test Rate Source: {stressTestSourceLabel}
                    </Typography>
                  </Box>
                }
              >
                Max<br />Loan
              </HeaderInfoTableCell>
              {propertyType === 'HDB' && loanType === 'hdb' ? (
                // HDB Loan: Combined 25% downpayment column
                <HeaderInfoTableCell
                  data-tutorial="downpayment-column"
                  sx={{ minWidth: responsiveColumnWidths(108, 116, 124, 132) }}
                  infoContent={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        25% Downpayment (HDB Loan)
                      </Typography>
                      {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                        <>
                          <Typography variant="caption" component="div">
                            = Target Purchase Price - Max Loan
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            For HDB loans, the 25% downpayment can be paid using CPF OA or cash (no cash requirement).
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="caption" component="div">
                            = 25% × Max Purchase Price
                          </Typography>
                          <Typography variant="caption" component="div">
                            Can be paid using CPF OA or cash (no minimum cash requirement)
                          </Typography>
                        </>
                      )}
                      <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                        Source: CPF Board - Differences Between HDB Loan and Bank Loan
                      </Typography>
                      <Typography variant="caption" component="div">
                        <a href="https://www.cpf.gov.sg/member/infohub/educational-resources/3-differences-between-hdb-loan-and-bank-loan" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                          View CPF Board Guidelines
                        </a>
                      </Typography>
                    </Box>
                  }
                >
                  25% Down<br />(Cash/CPF)
                </HeaderInfoTableCell>
              ) : (
                // Bank Loan: Two separate columns (5% Cash + 20% Remaining)
                <>
                  <HeaderInfoTableCell
                    data-tutorial="downpayment-column"
                    sx={{ minWidth: responsiveColumnWidths(92, 100, 108, 116) }}
                    infoContent={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          5% Cash Downpayment
                        </Typography>
                        <Typography variant="caption" component="div">
                          = Purchase Price × 5% (must be paid in cash)
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                          Source: MAS Property Financing Rules
                        </Typography>
                        <Typography variant="caption" component="div">
                          <a href="https://www.mas.gov.sg/regulation/banking/notices-and-circulars" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                            View MAS Banking Regulations
                          </a>
                        </Typography>
                      </Box>
                    }
                  >
                    5% Down<br />(Cash)
                  </HeaderInfoTableCell>
                  <HeaderInfoTableCell
                    sx={{ minWidth: responsiveColumnWidths(108, 116, 124, 132) }}
                    infoContent={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Remaining Downpayment
                        </Typography>
                        {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                          <>
                            <Typography variant="caption" component="div">
                              = Target Purchase Price - Max Loan - 5% Downpayment
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                              When target price is specified, remaining downpayment is calculated as the difference between target price and loan amount (after deducting 5% cash downpayment).
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="caption" component="div">
                              = 20% × Max Purchase Price
                            </Typography>
                            <Typography variant="caption" component="div">
                              Can be paid using CPF OA or cash
                            </Typography>
                          </>
                        )}
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                          Source: MAS Property Financing Rules
                        </Typography>
                      </Box>
                    }
                  >
                    20% Balance<br />(CPF/Cash)
                  </HeaderInfoTableCell>
                </>
              )}
              <HeaderInfoTableCell
                data-tutorial="stamp-duty-column"
                sx={{ minWidth: responsiveColumnWidths(88, 96, 104, 112) }}
                infoContent={
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Stamp Duty
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
                      Buyer's Stamp Duty (BSD):
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                      • First $180,000: 1%
                      <br />• Next $180,000: 2%
                      <br />• Next $640,000: 3%
                      <br />• Next $500,000: 4%
                      <br />• Next $1,500,000: 5%
                      <br />• Remaining amount: 6%
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      Residential rates, on or after 15 Feb 2023. BSD rounded down to nearest dollar.
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                      ABSD is shown in its own column.
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                      Source: IRAS Buyer's Stamp Duty (BSD)
                    </Typography>
                    <Typography variant="body1" component="div">
                      <a href="https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/buyer's-stamp-duty-(bsd)" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                        View IRAS BSD rates
                      </a>
                    </Typography>
                  </Box>
                }
              >
                Stamp<br />Duty
              </HeaderInfoTableCell>
              <HeaderInfoTableCell
                sx={{ minWidth: responsiveColumnWidths(88, 96, 104, 112) }}
                infoContent={
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Additional Buyer's Stamp Duty (ABSD)
                    </Typography>
                    <Typography variant="body1" component="div">
                      = Purchase Price × ABSD Rate
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                      ABSD Rates (effective 27 Apr 2023):
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                      • Singapore Citizen: 1st 0% | 2nd 20% | 3rd+ 30%
                      <br />• Singapore PR: 1st 5% | 2nd 30% | 3rd+ 35%
                      <br />• Foreigner: 1st 60% | 2nd 60% | 3rd+ 60%
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      Rate is derived from the selected buyer profile(s) — citizenship (with FTA
                      remission) and number of properties owned — including IRAS married-couple
                      remission for a joint purchase. Tap a value for the full breakdown.
                    </Typography>
                    <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                      Source: IRAS Additional Buyer's Stamp Duty (ABSD)
                    </Typography>
                    <Typography variant="body1" component="div">
                      <a href="https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/additional-buyer's-stamp-duty-(absd)" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                        View IRAS ABSD rates
                      </a>
                    </Typography>
                  </Box>
                }
              >
                ABSD
              </HeaderInfoTableCell>
              <HeaderInfoTableCell
                sx={{ minWidth: responsiveColumnWidths(88, 96, 104, 112) }}
                infoContent={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Total Outlay
                    </Typography>
                    <Typography variant="caption" component="div">
                      = 5% Cash Downpayment + Remaining Downpayment + Stamp Duty + ABSD
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                      Source: MAS Property Financing Rules
                    </Typography>
                  </Box>
                }
              >
                Total<br />Outlay
              </HeaderInfoTableCell>
              <HeaderInfoTableCell
                sx={{ minWidth: responsiveColumnWidths(108, 116, 124, 132) }}
                infoContent={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Difference: Capital vs Outlay
                    </Typography>
                    <Typography variant="caption" component="div">
                      = Total Capital - Total Outlay
                    </Typography>
                    <Typography variant="caption" component="div">
                      Positive (green) = affordable, Negative (red) = shortfall
                    </Typography>
                  </Box>
                }
              >
                Diff: Cap<br />vs Outlay
              </HeaderInfoTableCell>
              <HeaderInfoTableCell
                  sx={{ minWidth: responsiveColumnWidths(100, 108, 116, 124) }}
                  infoContent={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Affordable Today
                      </Typography>
                      <Typography variant="caption" component="div">
                        Highest whole-dollar P satisfying all 3 conditions:
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        ① 0.05 × P ≤ Cash Savings (5% cash-only tranche)
                      </Typography>
                      <Typography variant="caption" component="div">
                        ② Downpayment(P) + BSD(P) + ABSD(P) ≤ Total Capital
                      </Typography>
                      <Typography variant="caption" component="div">
                        ③ P ≤ Max Loan + Total Capital
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        Downpayment = P − min(Max Loan, LTV × P); BSD = residential rates from 15 Feb 2023 (1–6%); ABSD = P × ABSD rate.
                      </Typography>
                    </Box>
                  }
                >
                  Affordable<br />Today
                </HeaderInfoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timelineData.map((row, index) => (
              <TableRow
                key={index}
                selected={selectedRowIndex === index}
                sx={{
                  backgroundColor:
                    selectedRowIndex === index
                      ? tableColors.rowSelected
                      : row.difference >= 0 && index === timelineData.length - 1
                      ? tableColors.positive
                      : index % 2 === 1
                      ? tableColors.rowAlternate
                      : 'transparent',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  borderLeft: selectedRowIndex === index
                    ? `3px solid ${theme.palette.primary.main}`
                    : '3px solid transparent',
                  '&:hover': {
                    backgroundColor: selectedRowIndex === index
                      ? alpha(theme.palette.primary.main, 0.14)
                      : row.difference >= 0 && index === timelineData.length - 1
                      ? tableColors.positive
                      : tableColors.rowHover,
                    '& .MuiTableCell-root': {
                      color: theme.palette.text.primary,
                    },
                  },
                }}
                onClick={() => {
                  setSelectedRowIndex(selectedRowIndex === index ? null : index);
                }}
              >
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: row.difference >= 0 && index === timelineData.length - 1
                      ? (isDark ? alpha('#4caf50', 0.12) : alpha('#4caf50', 0.06))
                      : theme.palette.background.paper,
                    borderRight: `2px solid ${tableColors.sectionBorder}`,
                    minWidth: timelineColumnWidths,
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    fontSize: isLaptopViewport ? { xs: '0.8rem', sm: '0.88rem', md: '1rem' } : { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                  }}
                >
                  {row.date}
                </TableCell>
                {/* Profile Columns - Grouped by profile: Age, Assessable Income, Cash Savings Monthly, CPF OA Monthly */}
                {profiles.map((profile, profileIndex) => {
                  // Calculate this profile's values for this timeline row
                  const profileAge = profile.currentAge + index;
                  const profileSalary = index === 0
                    ? profile.monthlySalary
                    : profile.monthlySalary * Math.pow(1 + settings.salaryIncrement / 100, index);
                  const profileCPFOAMonthly =
                    profile.selfEmployedInd === 1
                      ? (selfEmployedCpfOAMonthlyByRow[profile.id]?.[index] ?? profile.cpfOAMonthly)
                      : calculateCPFOAMonthly(profileSalary, profileAge);

                  const colorTheme = tableColors.buyerHeaderColors[profileIndex] || tableColors.buyerHeaderColors[2];
                  const selectedProfiles = getSelectedProfiles();
                  const sourceProfile = selectedProfiles[profileIndex];
                  const profileName = sourceProfile?.Name || `Profile ${profileIndex + 1}`;

                  // Use green background for last row if difference >= 0, otherwise use buyer color
                  const cellBgColor = row.difference >= 0 && index === timelineData.length - 1
                    ? tableColors.positive
                    : colorTheme.bg;
                  
                  return (
                    <React.Fragment key={profile.id}>
                      {/* Age */}
                      <TableCell
                        sx={{
                          position: 'static',
                          backgroundColor: cellBgColor,
                          color: colorTheme.text,
                          minWidth: ageColumnWidths,
                          width: ageColumnWidths,
                          textAlign: 'center',
                        }}
                      >
                        {index === 0 ? (
                          <TextField
                            type="text"
                            inputMode="numeric"
                            value={profile.currentAge === 0 ? '' : String(profile.currentAge)}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\D/g, '');
                              if (raw === '') {
                                updateProfile(profile.id, { currentAge: 0 });
                                return;
                              }
                              const parsed = parseInt(raw, 10);
                              if (!isNaN(parsed) && parsed <= 120) {
                                updateProfile(profile.id, { currentAge: parsed });
                              }
                            }}
                            onBlur={() => {
                              if (profile.currentAge === 0) {
                                updateProfile(profile.id, { currentAge: 21 });
                              }
                            }}
                            size="small"
                            sx={{
                              ...laptopAgeFieldSx,
                              '& .MuiOutlinedInput-root': {
                                padding: compactEditableInputPadding,
                                fontSize: editableInputFontSize,
                              },
                              '& input': {
                                padding: compactEditableInputPadding,
                                textAlign: 'center',
                                minWidth: 0,
                              },
                            }}
                            inputProps={{
                              style: { textAlign: 'center', minWidth: 0, fontSize: editableInputFontSize },
                              autoComplete: 'off',
                            }}
                          />
                        ) : (
                          profileAge
                        )}
                      </TableCell>
                      {/* Assessable Income — base amount editable on first row; later rows show projected values */}
                      <TableCell
                        sx={{
                          backgroundColor: cellBgColor,
                          color: colorTheme.text,
                          minWidth: responsiveColumnWidths(70, 80, 86, 92),
                          textAlign: 'right',
                        }}
                      >
                        {index === 0 ? (
                          <NumericFormat
                            customInput={TextField}
                            thousandSeparator=","
                            prefix="$"
                            decimalScale={0}
                            allowNegative={false}
                            value={profile.monthlySalary}
                            onValueChange={(values, sourceInfo) => {
                              if (!isUserNumericInput(sourceInfo)) return;
                              updateProfile(profile.id, { monthlySalary: values.floatValue ?? 0 });
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            size="small"
                            sx={{
                              ...laptopProfileCurrencyFieldSx,
                              '& .MuiOutlinedInput-root': {
                                padding: compactEditableInputPadding,
                                fontSize: editableInputFontSize,
                              },
                              '& input': {
                                padding: compactEditableInputPadding,
                                textAlign: 'right',
                                minWidth: 0,
                              },
                            }}
                            inputProps={{
                              style: { textAlign: 'right', minWidth: 0, fontSize: editableInputFontSize },
                              autoComplete: 'off',
                              inputMode: 'numeric',
                            }}
                          />
                        ) : (
                          formatCurrency(Math.round(profileSalary))
                        )}
                      </TableCell>
                      {/* Cash Savings Monthly */}
                      <TableCell
                        sx={{
                          backgroundColor: cellBgColor,
                          color: colorTheme.text,
                          minWidth: responsiveColumnWidths(64, 74, 80, 86),
                          textAlign: 'right',
                        }}
                      >
                        {index === 0 ? (
                          <NumericFormat
                            customInput={TextField}
                            thousandSeparator=","
                            prefix="$"
                            decimalScale={0}
                            allowNegative={false}
                            value={profile.cashSavingsMonthly}
                            onValueChange={(values, sourceInfo) => {
                              if (!isUserNumericInput(sourceInfo)) return;
                              updateProfile(profile.id, { cashSavingsMonthly: values.floatValue ?? 0 });
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            size="small"
                            sx={{
                              ...laptopProfileCurrencyFieldSx,
                              '& .MuiOutlinedInput-root': {
                                padding: compactEditableInputPadding,
                                fontSize: editableInputFontSize,
                              },
                              '& input': {
                                padding: compactEditableInputPadding,
                                textAlign: 'right',
                                minWidth: 0,
                              },
                            }}
                            inputProps={{
                              style: { textAlign: 'right', minWidth: 0, fontSize: editableInputFontSize },
                              autoComplete: 'off',
                              inputMode: 'numeric',
                            }}
                          />
                        ) : (
                          formatCurrency(profile.cashSavingsMonthly)
                        )}
                      </TableCell>
                      {/* CPF OA Monthly */}
                      <TableCell
                        sx={{
                          backgroundColor: cellBgColor,
                          color: colorTheme.text,
                          borderRight: `2px solid ${tableColors.sectionBorder}`,
                          minWidth: responsiveColumnWidths(64, 74, 80, 86),
                          textAlign: 'right',
                        }}
                      >
                        {profile.selfEmployedInd === 1 ? (
                          <NumericFormat
                            customInput={TextField}
                            thousandSeparator=","
                            prefix="$"
                            decimalScale={0}
                            allowNegative={false}
                            value={profileCPFOAMonthly}
                            onValueChange={(values, sourceInfo) => {
                              if (!isUserNumericInput(sourceInfo)) return;
                              const v = values.floatValue ?? 0;
                              if (index === 0) {
                                updateProfile(profile.id, { cpfOAMonthly: v });
                              } else {
                                setSelfEmployedCpfOAMonthlyByRow((prev) => ({
                                  ...prev,
                                  [profile.id]: { ...(prev[profile.id] ?? {}), [index]: v },
                                }));
                              }
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            size="small"
                            sx={{
                              ...laptopProfileCurrencyFieldSx,
                              '& .MuiOutlinedInput-root': {
                                padding: compactEditableInputPadding,
                                fontSize: editableInputFontSize,
                              },
                              '& input': {
                                padding: compactEditableInputPadding,
                                textAlign: 'right',
                                minWidth: 0,
                              },
                            }}
                            inputProps={{
                              style: { textAlign: 'right', minWidth: 0, fontSize: editableInputFontSize },
                              autoComplete: 'off',
                              inputMode: 'numeric',
                            }}
                          />
                        ) : (
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Monthly CPF OA Contribution {profiles.length > 1 ? `(${profileName})` : ''}
                                </Typography>
                                <Typography variant="caption" component="div">
                                  = Total CPF Contribution × OA Allocation Ratio
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                                  Total CPF Contribution = Salary (capped at $8,000) × Total CPF Rate
                                </Typography>
                                <Typography variant="caption" component="div">
                                  Age {profileAge}: Total CPF Rate = {profileAge <= 55 ? '37%' : profileAge <= 60 ? '34%' : profileAge <= 65 ? '25%' : profileAge <= 70 ? '16.5%' : '12.5%'}
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                                  Monthly CPF OA = {formatCurrency(profileCPFOAMonthly)}
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                                  <a href="#" onClick={(e) => { e.preventDefault(); setSourceViewerOpen(true); setSourceViewerUrl('https://www.cpf.gov.sg/service/sfc/servlet.shepherd/document/download/069IW000005qFrJYAU'); }} style={{ color: '#90caf9', textDecoration: 'underline', cursor: 'pointer' }}>
                                    View CPF Allocation Rates
                                  </a>
                                </Typography>
                              </Box>
                            }
                            arrow
                          >
                            <Box sx={{ display: 'block', width: '100%', textAlign: 'right', cursor: 'help' }}>
                              {formatCurrency(profileCPFOAMonthly)}
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                {/* Funds Accumulated - Combined Values */}
                <TableCell
                  sx={{
                    minWidth: isLaptopViewport ? { xs: 78, md: 84, lg: 88 } : { xs: 86, md: 98 },
                    px: isLaptopViewport ? 0.5 : 0.75,
                    textAlign: 'right',
                  }}
                >
                  {index === 0 ? (
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator=","
                      prefix="$"
                      decimalScale={0}
                      allowNegative={false}
                      value={
                        (() => {
                          const m = manualEdits[index]?.cpfOA;
                          const refund = row.cpfRefundFromSales ?? 0;
                          const combined = getCombinedValues(profiles).totalCurrentCPFOA ?? 0;
                          const combinedWithRefund = combined + refund;
                          if (m === '' || m === null || m === undefined) return combinedWithRefund;
                          return (typeof m === 'number' ? m : combined) + refund;
                        })()
                      }
                      onValueChange={(values, sourceInfo) => {
                        if (!isUserNumericInput(sourceInfo)) return;
                        const combinedInput = values.floatValue ?? 0;
                        const refund = row.cpfRefundFromSales ?? 0;
                        const newValue = Math.max(0, combinedInput - refund);
                        setManualEdits((prev) => ({
                          ...prev,
                          [index]: { ...prev[index], cpfOA: newValue },
                        }));
                        if (newValue === 0) {
                          profiles.forEach((p) => {
                            updateProfile(p.id, { currentCPFOA: 0 });
                          });
                          return;
                        }
                        if (profiles.length === 1) {
                          updateProfile(profiles[0].id, { currentCPFOA: newValue });
                        } else {
                          const currentTotal = getCombinedValues(profiles).totalCurrentCPFOA;
                          if (currentTotal > 0) {
                            const ratio = newValue / currentTotal;
                            profiles.forEach((p) => {
                              updateProfile(p.id, { currentCPFOA: Math.round(p.currentCPFOA * ratio) });
                            });
                          } else {
                            const perProfile = Math.round(newValue / profiles.length);
                            profiles.forEach((p, idx) => {
                              updateProfile(p.id, {
                                currentCPFOA: idx === 0 ? newValue - perProfile * (profiles.length - 1) : perProfile,
                              });
                            });
                          }
                        }
                      }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      size="small"
                      sx={{
                        ...laptopFundsCurrencyFieldSx,
                        '& .MuiOutlinedInput-root': {
                          padding: standardEditableInputPadding,
                          fontSize: editableInputFontSize,
                          minWidth: 0,
                        },
                        '& input': {
                          padding: standardEditableInputPadding,
                          textAlign: 'right',
                        },
                      }}
                      inputProps={{
                        style: { textAlign: 'right', fontSize: editableInputFontSize },
                        autoComplete: 'off',
                        inputMode: 'numeric',
                      }}
                    />
                  ) : (
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Combined CPF (OA + Refund)
                          </Typography>
                          <Typography variant="caption" component="div">
                            = CPF OA Accumulated + CPF Refund From Sales
                          </Typography>
                          {profiles.length > 1 && (
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                              Calculated using IWAA: {formatIWAA(getCombinedValues(profiles).iwaa)} years ({getCombinedValues(profiles).iwaa.roundedUp} used for calculations)
                            </Typography>
                          )}
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            CPF OA component = Previous Balance + 2.5% p.a. OA interest + this year's contributions (Combined Monthly CPF OA × 12)
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Monthly contributions also earn 2.5% p.a. interest from the month after each is received (CPF method: computed monthly, credited yearly)
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            = {formatCurrency(row.cpfOAAccumulated)} + {formatCurrency(row.cpfRefundFromSales)}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            = {formatCurrency(row.cpfOAAccumulated + row.cpfRefundFromSales)}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            Source: CPF.gov.sg & MAS Guidelines
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Box sx={{ display: 'block', width: '100%', textAlign: 'right', cursor: 'help' }}>
                        {formatCurrency(row.cpfOAAccumulated + row.cpfRefundFromSales)}
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    minWidth: isLaptopViewport ? { xs: 80, md: 86, lg: 90 } : { xs: 88, md: 102 },
                    px: isLaptopViewport ? 0.5 : 0.75,
                    textAlign: 'right',
                  }}
                >
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Combined Cash (Savings + Proceed)
                        </Typography>
                        <Typography variant="caption" component="div">
                          = Cash Savings Accumulated + Cash Proceeds From Sale
                        </Typography>
                        {profiles.length > 1 && (
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Calculated using IWAA: {formatIWAA(getCombinedValues(profiles).iwaa)} years ({getCombinedValues(profiles).iwaa.roundedUp} used for calculations)
                          </Typography>
                        )}
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          Cash savings component = Previous Combined Cash Savings + (Combined Monthly Cash Savings × 12)
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          = {formatCurrency(row.cashSavingsAccumulated)} + {formatCurrency(row.cashProceedsFromSale)}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                          = {formatCurrency(row.cashSavingsAccumulated + row.cashProceedsFromSale)}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          (Editable field)
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator=","
                      prefix="$"
                      decimalScale={0}
                      allowNegative={false}
                      value={row.cashSavingsAccumulated + row.cashProceedsFromSale}
                      onValueChange={(values, sourceInfo) => {
                        if (!isUserNumericInput(sourceInfo)) return;
                        const combinedInput = Math.round(values.floatValue ?? 0);
                        const v = Math.max(0, combinedInput - row.cashProceedsFromSale);
                        setManualEdits((prev) => ({
                          ...prev,
                          [index]: { ...prev[index], cashSavings: v },
                        }));
                      }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      size="small"
                      sx={{
                        ...laptopFundsCurrencyFieldSx,
                        '& .MuiOutlinedInput-root': {
                          padding: standardEditableInputPadding,
                          fontSize: editableInputFontSize,
                          minWidth: 0,
                        },
                        '& input': {
                          padding: standardEditableInputPadding,
                          textAlign: 'right',
                        },
                      }}
                      inputProps={{
                        style: { textAlign: 'right', fontSize: editableInputFontSize },
                        autoComplete: 'off',
                        inputMode: 'numeric',
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    minWidth: isLaptopViewport ? { xs: 84, md: 90, lg: 94 } : { xs: 92, md: 106 },
                    px: isLaptopViewport ? 0.5 : 0.75,
                    borderRight: `2px solid ${tableColors.sectionBorder}`,
                    textAlign: 'right',
                  }}
                >
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Total Capital
                        </Typography>
                        <Typography variant="caption" component="div">
                          = CPF OA Accumulated + Cash Savings Accumulated + CPF Refund From Sales + Cash Proceeds From Sale
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          = {formatCurrency(row.cpfOAAccumulated)} + {formatCurrency(row.cashSavingsAccumulated)} + {formatCurrency(row.cpfRefundFromSales)} + {formatCurrency(row.cashProceedsFromSale)}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                          = {formatCurrency(row.totalCapital)}
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Box sx={{ display: 'block', width: '100%', textAlign: 'right', cursor: 'help' }}>
                      {formatCurrency(row.totalCapital)}
                    </Box>
                  </Tooltip>
                </TableCell>
                {/* Property Purchase */}
                {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                  <TableCell sx={{ textAlign: 'right' }}>
                    <InfoValueTrigger
                      infoContent={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Target Purchase Price
                          </Typography>
                          <Typography variant="caption" component="div">
                            User-specified target property price
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            = {formatCurrency(row.targetPurchasePrice)}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            All calculations (downpayment, stamp duty, difference) are based on this target price.
                          </Typography>
                        </Box>
                      }
                    >
                      {formatCurrency(row.targetPurchasePrice)}
                    </InfoValueTrigger>
                  </TableCell>
                ) : null}
                <TableCell sx={{ minWidth: { xs: 130, md: 140 }, px: 1 }}>
                  <InfoValueTrigger
                    infoContent={
                      includeFundsInMaxPurchasePrice ? (
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            Max Purchase Price (Base on Income + Funds)
                          </Typography>
                          <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
                            = Max Purchase Price (Base on Income) + Total Capital
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>
                            1. Max Purchase Price (Base on Income)
                          </Typography>
                          <Typography variant="caption" component="div">
                            = Max Loan ÷ LTV Ratio
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Loan Tenure: {row.loanTenure} years · LTV: {(row.loanToValueRatio * 100).toFixed(0)}%{' '}
                            {row.loanTenure > 30 ? '(reduced due to tenure > 30 years)' : ''}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            = {formatCurrency(row.maxLoan)} ÷ {row.loanToValueRatio.toFixed(2)} ={' '}
                            <strong>{formatCurrency(row.maxPurchasePrice)}</strong>
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
                            2. Total Capital (funds)
                          </Typography>
                          <Typography variant="caption" component="div">
                            = CPF OA Accumulated + Cash Savings Accumulated + CPF Refund From Sales + Cash Proceeds From Sale
                            {parseFloat(cpfHousingGrant || '0') > 0
                              ? ` + CPF Housing Grant (${formatCurrency(parseFloat(cpfHousingGrant))})`
                              : ''}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            = {formatCurrency(row.cpfOAAccumulated)} + {formatCurrency(row.cashSavingsAccumulated)} +{' '}
                            {formatCurrency(row.cpfRefundFromSales)} + {formatCurrency(row.cashProceedsFromSale)}
                            {parseFloat(cpfHousingGrant || '0') > 0
                              ? ` + ${formatCurrency(parseFloat(cpfHousingGrant))}`
                              : ''}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            = {formatCurrency(row.totalCapital)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, mt: 1.5 }}>
                            Sum
                          </Typography>
                          <Typography variant="body2" component="div">
                            = {formatCurrency(row.maxPurchasePrice)} + {formatCurrency(row.totalCapital)} ={' '}
                            <strong>{formatCurrency(row.purchasePriceForCalculation)}</strong>
                          </Typography>
                          {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                            <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                              Note: Downpayment and outlay columns use your target price (
                              {formatCurrency(row.targetPurchasePrice)}); this column shows income-based max plus funds.
                            </Typography>
                          ) : (
                            includeFundsInMaxPurchasePrice && (
                              <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                                Downpayment, stamp duty, and total outlay use this combined purchase price (income max +
                                funds).
                              </Typography>
                            )
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Max Purchase Price (Base on Income)
                          </Typography>
                          <Typography variant="caption" component="div">
                            = Max Loan ÷ LTV Ratio
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Loan Tenure: {row.loanTenure} years
                          </Typography>
                          <Typography variant="caption" component="div">
                            LTV Ratio: {(row.loanToValueRatio * 100).toFixed(0)}% {row.loanTenure > 30 ? '(reduced due to tenure > 30 years)' : ''}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            = {formatCurrency(row.maxLoan)} ÷ {row.loanToValueRatio.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            = {formatCurrency(row.maxPurchasePrice)}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            <a href="https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline', cursor: 'pointer' }}>
                              Source: MAS Loan Tenure and LTV Limits
                            </a>
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                            Turn on <strong>Include funds</strong> in the column header to add Total Capital to this figure.
                          </Typography>
                        </Box>
                      )
                    }
                  >
                    {formatCurrency(
                      parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0
                        ? includeFundsInMaxPurchasePrice
                          ? row.maxPurchasePrice + row.totalCapital
                          : row.maxPurchasePrice
                        : includeFundsInMaxPurchasePrice
                          ? row.purchasePriceForCalculation
                          : row.maxPurchasePrice
                    )}
                  </InfoValueTrigger>
                </TableCell>
                <TableCell>
                  <InfoValueTrigger
                    infoContent={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          Max Loan Amount
                        </Typography>
                        <Typography variant="body1" component="div">
                          Max Monthly Payment = {(() => { const base = getBasePropertyType(propertyType); return base === 'PRIVATE' ? '55%' : '30%'; })()} × Monthly Salary
                        </Typography>
                        <Typography variant="body1" component="div">
                          = {(() => { const base = getBasePropertyType(propertyType); return base === 'PRIVATE' ? '0.55' : '0.30'; })()} × <strong>{formatCurrency(row.monthlySalary)}</strong>
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                          = <strong>{formatCurrency(((() => { const base = getBasePropertyType(propertyType); return base === 'PRIVATE' ? 0.55 : 0.30; })()) * row.monthlySalary)}</strong> per month
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                          Max Loan = Max Monthly Payment × [(1+r)^n - 1] / [r × (1+r)^n]
                        </Typography>
                        <Typography variant="body1" component="div">
                          where r = {stressTestRate}% ÷ 12 = {(stressTestRate / 12).toFixed(4)}% monthly
                        </Typography>
                        <Typography variant="body1" component="div">
                          n = {row.loanTenure} years × 12 = {row.loanTenure * 12} months
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          (Capped at {(() => {
                            if (propertyType === 'HDB') {
                              return '25';
                            }
                            const base = getBasePropertyType(propertyType);
                            return base === 'PRIVATE' ? '30' : base === 'EC' ? '30' : '30';
                          })()} years maximum tenure, or until age 65, whichever is shorter)
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                          LTV Ratio: {(row.loanToValueRatio * 100).toFixed(0)}% {row.loanTenure > 30 ? '(reduced due to tenure > 30 years per MAS regulations)' : ''}
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                          = <strong>{formatCurrency(row.maxLoan)}</strong> (income-based max)
                        </Typography>
                        {row.loanAtPurchasePrice !== row.maxLoan && (
                          <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                            {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) <= 0 &&
                            includeFundsInMaxPurchasePrice ? (
                              <>
                                Loan at this purchase price = Purchase Price × LTV ({(row.loanToValueRatio * 100).toFixed(0)}
                                %): <strong>{formatCurrency(row.loanAtPurchasePrice)}</strong>
                                <Typography variant="caption" component="div" sx={{ mt: 0.5, display: 'block' }}>
                                  With Include funds, this follows the combined purchase price (not capped by the
                                  income-based max loan above).
                                </Typography>
                              </>
                            ) : (
                              <>
                                Loan at this property price (LTV cap, limited by income max):{' '}
                                <strong>{formatCurrency(row.loanAtPurchasePrice)}</strong>
                              </>
                            )}
                          </Typography>
                        )}
                        <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                          Stress Test Rate: {stressTestRate}%
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                          Source: {stressTestSourceLabel}
                        </Typography>
                      </Box>
                    }
                  >
                    {formatCurrency(row.loanAtPurchasePrice)}
                  </InfoValueTrigger>
                </TableCell>
                {propertyType === 'HDB' && loanType === 'hdb' ? (
                  // HDB Loan: Combined 25% downpayment cell (no red highlighting since CPF/Cash can be used)
                  <TableCell sx={{ textAlign: 'right' }}>
                    <InfoValueTrigger
                      infoContent={
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            25% Downpayment (HDB Loan)
                          </Typography>
                          {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                            <>
                              <Typography variant="body1" component="div">
                                = Target Purchase Price - Loan (at LTV cap)
                              </Typography>
                              <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                                = <strong>{formatCurrency(row.targetPurchasePrice)}</strong> - <strong>{formatCurrency(row.loanAtPurchasePrice)}</strong>
                              </Typography>
                              <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                                = <strong>{formatCurrency(row.downpayment5Cash)}</strong>
                              </Typography>
                            </>
                          ) : includeFundsInMaxPurchasePrice ? (
                            <>
                              <Typography variant="body1" component="div">
                                = Purchase Price (income + funds) - Loan (at LTV cap)
                              </Typography>
                              <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                                = <strong>{formatCurrency(row.purchasePriceForCalculation)}</strong> - <strong>{formatCurrency(row.loanAtPurchasePrice)}</strong>
                              </Typography>
                              <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                                = <strong>{formatCurrency(row.downpayment5Cash)}</strong>
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body1" component="div">
                                = 25% × Max Purchase Price
                              </Typography>
                              <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                                = 25% × <strong>{formatCurrency(row.maxPurchasePrice)}</strong>
                              </Typography>
                              <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                                = <strong>{formatCurrency(row.downpayment5Cash)}</strong>
                              </Typography>
                            </>
                          )}
                          <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                            For HDB loans, the 25% downpayment can be paid using CPF OA or cash (no minimum cash requirement).
                          </Typography>
                          <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            Source: CPF Board - Differences Between HDB Loan and Bank Loan
                          </Typography>
                        </Box>
                      }
                    >
                      {formatCurrency(row.downpayment5Cash)}
                    </InfoValueTrigger>
                  </TableCell>
                ) : (
                  // Bank Loan: Two separate cells (5% Cash + 20% Remaining)
                  <>
                    <TableCell
                      sx={{
                        backgroundColor: row.downpayment5Cash > 0 ? tableColors.cashWarning : 'transparent',
                        color: row.downpayment5Cash > 0 ? tableColors.cashWarningText : 'inherit',
                        fontWeight: row.downpayment5Cash > 0 ? 600 : 400,
                        textAlign: 'right',
                      }}
                    >
                      <InfoValueTrigger
                        infoContent={
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                              5% Cash Downpayment
                            </Typography>
                            <Typography variant="body1" component="div">
                              = 5% × Purchase Price
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                              = 5% ×{' '}
                              {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0
                                ? formatCurrency(row.targetPurchasePrice)
                                : includeFundsInMaxPurchasePrice
                                  ? formatCurrency(row.purchasePriceForCalculation)
                                  : formatCurrency(row.maxPurchasePrice)}
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                              = <strong>{formatCurrency(row.downpayment5Cash)}</strong>
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                              This 5% must be paid in cash (cannot use CPF).
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                              Source: MAS Property Financing Rules
                            </Typography>
                          </Box>
                        }
                      >
                        {formatCurrency(row.downpayment5Cash)}
                      </InfoValueTrigger>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <InfoValueTrigger
                        infoContent={
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                              Remaining Downpayment
                            </Typography>
                            {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                              <>
                                <Typography variant="body1" component="div">
                                  = Target Purchase Price - Loan (at LTV cap) - 5% Downpayment
                                </Typography>
                                <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                                  = <strong>{formatCurrency(row.targetPurchasePrice)}</strong> - <strong>{formatCurrency(row.loanAtPurchasePrice)}</strong> - <strong>{formatCurrency(row.downpayment5Cash)}</strong>
                                </Typography>
                                <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                                  = <strong>{formatCurrency(row.remainingDownpayment)}</strong>
                                </Typography>
                              </>
                            ) : includeFundsInMaxPurchasePrice ? (
                              <>
                                <Typography variant="body1" component="div">
                                  = Purchase Price - Loan (at LTV cap) - 5% Cash Downpayment
                                </Typography>
                                <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                                  = <strong>{formatCurrency(row.purchasePriceForCalculation)}</strong> - <strong>{formatCurrency(row.loanAtPurchasePrice)}</strong> - <strong>{formatCurrency(row.downpayment5Cash)}</strong>
                                </Typography>
                                <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                                  = <strong>{formatCurrency(row.remainingDownpayment)}</strong>
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="body1" component="div">
                                  = 20% × Max Purchase Price
                                </Typography>
                                <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                                  = 20% × <strong>{formatCurrency(row.maxPurchasePrice)}</strong>
                                </Typography>
                                <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                                  = <strong>{formatCurrency(row.remainingDownpayment)}</strong>
                                </Typography>
                              </>
                            )}
                            <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                              Can be paid using CPF OA or cash
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                              Source: MAS Property Financing Rules
                            </Typography>
                            <Typography variant="body1" component="div">
                              <a href="https://www.mas.gov.sg/regulation/banking/notices-and-circulars" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                                View MAS Banking Regulations (Notice 645)
                              </a>
                            </Typography>
                          </Box>
                        }
                      >
                        {formatCurrency(row.remainingDownpayment)}
                      </InfoValueTrigger>
                    </TableCell>
                  </>
                )}
                <TableCell sx={{ textAlign: 'right' }}>
                  <InfoValueTrigger
                    infoContent={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          Stamp Duty
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
                          Buyer's Stamp Duty (BSD):
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 0.75 }}>
                          Progressive residential rates applied to the purchase price of{' '}
                          <strong>{formatCurrency(row.purchasePriceForCalculation)}</strong>
                        </Typography>
                        {/* Per-bracket working: only the slice of the price that falls in each
                            bracket is taxed, so the last bracket is usually a partial slice. */}
                        <Box sx={{ pl: 1 }}>
                          {getBsdBreakdown(row.purchasePriceForCalculation)
                            .filter((b) => b.taxable > 0)
                            .map((b, i) => (
                              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                <Typography variant="body2" component="span">
                                  {b.bracket.split(' × ')[0]} · {formatCurrency(b.taxable)} ×{' '}
                                  {Math.round(b.rate * 100)}%
                                </Typography>
                                <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
                                  {formatCurrency(b.duty)}
                                </Typography>
                              </Box>
                            ))}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 700 }}>
                              = BSD
                            </Typography>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {formatCurrency(row.stampDuty)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" component="div" sx={{ mt: 0.75 }}>
                          Residential rates, on or after 15 Feb 2023. Rounded to the nearest dollar.
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                          Source: IRAS Buyer's Stamp Duty (BSD)
                        </Typography>
                        <Typography variant="body1" component="div">
                          <a href="https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/buyer's-stamp-duty-(bsd)" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                            View IRAS BSD rates
                          </a>
                        </Typography>
                      </Box>
                    }
                  >
                    {formatCurrency(row.stampDuty)}
                  </InfoValueTrigger>
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <InfoValueTrigger
                    infoContent={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          Additional Buyer's Stamp Duty (ABSD)
                        </Typography>
                        {absdInfo.manual ? (
                          <Typography variant="body1" component="div">
                            Manual ABSD rate of <strong>{absdInfo.rate}%</strong> (set via Settings).
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
                              ABSD Rates (effective 27 Apr 2023):
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                              • Singapore Citizen: 1st 0% | 2nd 20% | 3rd+ 30%
                              <br />• Singapore PR: 1st 5% | 2nd 30% | 3rd+ 35%
                              <br />• Foreigner: 1st 60% | 2nd 60% | 3rd+ 60%
                            </Typography>
                            {absdInfo.borrowers.length > 0 ? (
                              <Box sx={{ mt: 1 }}>
                                {absdInfo.borrowers.map((b, i) => (
                                  <Typography key={i} variant="body1" component="div">
                                    {b.label}: {b.citizenshipLabel}{b.fta ? ' (FTA → SC rates)' : ''}, {propertyCountLabel(b.propertyCount)} → {b.rate}%
                                  </Typography>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" component="div" sx={{ mt: 1, display: 'block' }}>
                                No buyer profile selected — defaults to Singapore Citizen, 1st property (0%).
                              </Typography>
                            )}
                            {absdInfo.remissionApplied && (
                              <Typography variant="body1" component="div" sx={{ mt: 1, color: 'success.main' }}>
                                Married-couple remission applied — rate reduced from {absdInfo.rateBeforeRemission}% to {absdInfo.rate}% (saving {formatCurrency(Math.round(row.purchasePriceForCalculation * (absdInfo.rateBeforeRemission - absdInfo.rate) / 100))}).
                              </Typography>
                            )}
                            {!absdInfo.remissionApplied && absdInfo.borrowers.length > 1 && (
                              <Typography variant="caption" component="div" sx={{ mt: 1, display: 'block' }}>
                                Joint purchase — ABSD uses the highest applicable rate.
                              </Typography>
                            )}
                          </>
                        )}
                        <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 700 }}>
                          = {formatCurrency(row.purchasePriceForCalculation)} × {absdInfo.rate}% = <strong>{formatCurrency(row.absd)}</strong>
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                          Source: IRAS Additional Buyer's Stamp Duty (ABSD)
                        </Typography>
                        <Typography variant="body1" component="div">
                          <a href="https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/additional-buyer's-stamp-duty-(absd)" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', textDecoration: 'underline' }}>
                            View IRAS ABSD rates
                          </a>
                        </Typography>
                      </Box>
                    }
                  >
                    {formatCurrency(row.absd)}
                  </InfoValueTrigger>
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <InfoValueTrigger
                    infoContent={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          Total Outlay
                        </Typography>
                        {propertyType === 'HDB' && loanType === 'hdb' ? (
                          <>
                            <Typography variant="body1" component="div">
                              = 25% Downpayment (Cash/CPF) + Stamp Duty (BSD) + ABSD
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                              = <strong>{formatCurrency(row.downpayment5Cash)}</strong> + <strong>{formatCurrency(row.stampDuty)}</strong> + <strong>{formatCurrency(row.absd)}</strong>
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="body1" component="div">
                              = 5% Cash Downpayment + Remaining Downpayment + Stamp Duty (BSD) + ABSD
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                              = <strong>{formatCurrency(row.downpayment5Cash)}</strong> + <strong>{formatCurrency(row.remainingDownpayment)}</strong> + <strong>{formatCurrency(row.stampDuty)}</strong> + <strong>{formatCurrency(row.absd)}</strong>
                            </Typography>
                          </>
                        )}
                        <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                          = <strong>{formatCurrency(row.totalOutlay)}</strong>
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                          This is the total amount you need to pay upfront for the property purchase.
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                          Source: MAS Property Financing Rules
                        </Typography>
                      </Box>
                    }
                  >
                    {formatCurrency(row.totalOutlay)}
                  </InfoValueTrigger>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: row.difference >= 0 ? tableColors.positive : tableColors.negative,
                    color: row.difference >= 0 ? tableColors.positiveText : tableColors.negativeText,
                    fontWeight: 600,
                    borderRadius: 0,
                    textAlign: 'right',
                  }}
                >
                  <InfoValueTrigger
                    infoContent={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          Capital Adequacy (Difference)
                        </Typography>
                        {parseFloat(parseNumberFromFormatted(targetPurchasePrice)) > 0 ? (
                          <>
                            <Typography variant="body1" component="div">
                              Based on Target Purchase Price: <strong>{formatCurrency(row.targetPurchasePrice)}</strong>
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                              = Total Capital - Total Outlay (calculated from target price)
                            </Typography>
                          </>
                        ) : includeFundsInMaxPurchasePrice ? (
                          <Typography variant="body1" component="div">
                            = Total Capital - Total Outlay (calculated from income max + funds purchase price)
                          </Typography>
                        ) : (
                          <Typography variant="body1" component="div">
                            = Total Capital - Total Outlay (calculated from max purchase price)
                          </Typography>
                        )}
                        <Typography variant="body1" component="div" sx={{ mt: 0.5 }}>
                          = <strong>{formatCurrency(row.totalCapital)}</strong> - <strong>{formatCurrency(row.totalOutlay)}</strong>
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>
                          = <strong>{formatCurrency(row.difference)}</strong>
                        </Typography>
                        {row.difference >= 0 ? (
                          <Typography variant="body1" component="div" sx={{ mt: 1, color: 'success.main', fontWeight: 600 }}>
                            ✓ Sufficient capital
                          </Typography>
                        ) : (
                          <Typography variant="body1" component="div" sx={{ mt: 1, color: 'error.main', fontWeight: 600 }}>
                            ⚠️ Insufficient capital
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    {formatCurrency(row.difference)}
                  </InfoValueTrigger>
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <InfoValueTrigger
                    infoContent={(() => {
                      const P = row.affordableToday;
                      const loanAmount = Math.min(row.maxLoan, row.loanToValueRatio * P);
                      const cash5pct = Math.round(P * 0.05);
                      const remaining20pct = Math.round(P - loanAmount) - cash5pct;
                      const bsd = calculateBsd(P);
                      const absd = Math.round(P * (absdInfo.rate / 100));
                      const cashAvail = row.cashSavingsAccumulated + row.cashProceedsFromSale;
                      const totalUpfront = cash5pct + remaining20pct + bsd + absd;

                      // Compute each constraint's individual ceiling to show derivation
                      const cashCeiling = cashAvail > 0 ? Math.floor(cashAvail / 0.05) : 0;
                      // Capital ceiling: highest P where downpayment+BSD+ABSD <= totalCapital (no cash constraint)
                      let lo = 0, hi = row.maxLoan + row.totalCapital;
                      while (lo < hi) {
                        const mid = Math.floor((lo + hi + 1) / 2);
                        const la = Math.min(row.maxLoan, row.loanToValueRatio * mid);
                        const dp = mid - la;
                        if (dp + calculateBsd(mid) + Math.round(mid * (absdInfo.rate / 100)) <= row.totalCapital) lo = mid; else hi = mid - 1;
                      }
                      const capitalCeiling = lo;
                      const loanCeiling = Math.round(row.maxLoan / row.loanToValueRatio);
                      const bindingLabel = (p: number): string => {
                        if (p === capitalCeiling && p < cashCeiling && p < loanCeiling) return ' ← binding (capital)';
                        if (p === cashCeiling && p <= capitalCeiling && p < loanCeiling) return ' ← binding (cash)';
                        if (p === loanCeiling) return ' ← binding (loan)';
                        return '';
                      };

                      return (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Affordable Today: {formatCurrency(P)}
                          </Typography>

                          {/* Derivation */}
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            How it's derived:
                          </Typography>
                          <Typography variant="caption" component="div">
                            Cash limit (0.05 × P ≤ {formatCurrency(cashAvail)}): P ≤ {formatCurrency(cashCeiling)}{P === Math.min(cashCeiling, capitalCeiling, loanCeiling) && cashCeiling <= capitalCeiling && cashCeiling <= loanCeiling ? ' ← binding' : ''}
                          </Typography>
                          <Typography variant="caption" component="div">
                            Capital limit (down + BSD + ABSD ≤ {formatCurrency(row.totalCapital)}): P ≤ {formatCurrency(capitalCeiling)}{capitalCeiling <= cashCeiling && capitalCeiling <= loanCeiling ? ' ← binding' : ''}
                          </Typography>
                          <Typography variant="caption" component="div">
                            Loan limit (LTV {Math.round(row.loanToValueRatio * 100)}%): P ≤ {formatCurrency(loanCeiling)}{loanCeiling < cashCeiling && loanCeiling < capitalCeiling ? ' ← binding' : ''}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            = min({formatCurrency(cashCeiling)}, {formatCurrency(capitalCeiling)}, {formatCurrency(loanCeiling)}) = {formatCurrency(P)}
                          </Typography>

                          {/* Verification */}
                          <Typography variant="caption" component="div" sx={{ mt: 1, fontWeight: 600 }}>
                            Verification:
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            ① 5% Cash Downpayment (cash only)
                          </Typography>
                          <Typography variant="caption" component="div">
                            {formatCurrency(cash5pct)} ≤ Cash {formatCurrency(cashAvail)}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            ② Total Upfront ≤ Total Capital
                          </Typography>
                          <Typography variant="caption" component="div">
                            5% cash downpayment: {formatCurrency(cash5pct)}
                          </Typography>
                          <Typography variant="caption" component="div">
                            20% remaining downpayment (cash/CPF): {formatCurrency(remaining20pct)}
                          </Typography>
                          <Typography variant="caption" component="div">
                            BSD (cash/CPF): {formatCurrency(bsd)}
                          </Typography>
                          <Typography variant="caption" component="div">
                            ABSD (cash/CPF): {formatCurrency(absd)}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }}>
                            {formatCurrency(cash5pct)} + {formatCurrency(remaining20pct)} + {formatCurrency(bsd)} + {formatCurrency(absd)} = {formatCurrency(totalUpfront)} ≤ {formatCurrency(row.totalCapital)}
                          </Typography>
                        </Box>
                      );
                    })()}
                  >
                    {formatCurrency(row.affordableToday)}
                  </InfoValueTrigger>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
      </TableContainer>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Salary Increment (%)"
              type="number"
              inputMode="decimal"
              value={settings.salaryIncrement || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Remove leading zeros
                const cleanedValue = value.replace(/^0+(?=\d)/, '');
                const numValue = cleanedValue === '' ? 0 : parseFloat(cleanedValue);
                setSettings({ ...settings, salaryIncrement: isNaN(numValue) ? 0 : numValue });
              }}
              fullWidth
              sx={{ mb: 3 }}
              helperText="Annual salary increment percentage"
            />
            <Alert severity="info" sx={{ mb: 3 }}>
              Stress test rate is now managed globally from the left navigation under <strong>Settings</strong>. Current shared rate: {stressTestRate}%.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body1">Additional Buyer Stamp Duty</Typography>
                <Typography variant="body2" color="text.secondary">
                  {settings.absdRate}%
                </Typography>
              </Box>
              <Chip
                label={settings.absdEnabled ? 'ON' : 'OFF'}
                color={settings.absdEnabled ? 'primary' : 'default'}
                onClick={() =>
                  setSettings({ ...settings, absdEnabled: !settings.absdEnabled })
                }
                sx={{ cursor: 'pointer' }}
              />
            </Box>
            {settings.absdEnabled && (
              <TextField
                label="ABSD Rate (%)"
                type="number"
                inputMode="decimal"
                value={settings.absdRate || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Remove leading zeros
                  const cleanedValue = value.replace(/^0+(?=\d)/, '');
                  const numValue = cleanedValue === '' ? 0 : parseFloat(cleanedValue);
                  setSettings({ ...settings, absdRate: isNaN(numValue) ? 0 : numValue });
                }}
                fullWidth
                sx={{ mt: 2 }}
                helperText="Additional Buyer Stamp Duty rate"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button onClick={() => setSettingsOpen(false)} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Information Dialog */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1 }} />
            Formulas & Logic Reference
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" gutterBottom>
              CPF Calculations
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>CPF Contribution Rate:</strong> Total CPF rate by age, applied up to the Ordinary Wage ceiling of $8,000
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>CPF OA Allocation Rates (by age):</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              • 35 & below: 62.17% of CPF contribution
              <br />• Above 35-45: 56.77%
              <br />• Above 45-50: 51.36%
              <br />• Above 50-55: 40.55%
              <br />• Above 55-60: 35.30%
              <br />• Above 60-65: 14.00%
              <br />• Above 65-70: 6.07%
              <br />• Above 70: 8.00%
            </Typography>
            <Typography variant="body2" paragraph sx={{ mt: 1 }}>
              <strong>CPF OA Monthly:</strong> (Salary capped at $8,000 × Total CPF Rate × OA Ratio) based on age
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>CPF OA Accumulated:</strong> Previous Balance + 2.5% p.a. OA interest + (Monthly CPF OA × 12). Following CPF's method, interest is computed monthly and credited yearly, and each monthly contribution earns interest from the month after it is received.
            </Typography>
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                Source: CPF Allocation Rates from 1 January 2026
              </Typography>
              <Typography variant="caption" component="div">
                <a href="#" onClick={(e) => { e.preventDefault(); setSourceViewerOpen(true); setSourceViewerUrl('https://www.cpf.gov.sg/service/sfc/servlet.shepherd/document/download/069IW000005qFrJYAU'); }} style={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}>
                  View Official CPF Document
                </a>
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Loan Calculations
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Max Monthly Payment:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              • Private Property: Monthly Income × 55% (TDSR limit)
              <br />• EC/HDB: Monthly Income × 30% (MSR limit)
            </Typography>
            <Typography variant="body2" paragraph sx={{ mt: 1 }}>
              <strong>Max Loan Amount:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2, fontFamily: 'monospace' }}>
              Max Loan = Max Monthly Payment × [(1+r)^n - 1] / [r × (1+r)^n]
              <br />
              where: r = monthly interest rate (assumed 3.5% p.a. = 0.2917% monthly)
              <br />
              n = number of monthly payments (loan tenure × 12)
            </Typography>
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                Sources: MAS Guidelines
              </Typography>
              <Typography variant="caption" component="div">
                <a href="https://www.mas.gov.sg/regulation/banking/notices-and-circulars" target="_blank" rel="noopener noreferrer" style={{ color: 'primary.main', textDecoration: 'underline' }}>
                  MAS Banking Regulations (Notice 645 - Property Market Measures)
                </a>
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Property Price Calculations
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Loan-to-Value (LTV) Ratios:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              • Private Property: 75% (≤30 years), 55% (&gt;30 years)
              <br />• Executive Condominium (EC): 75% (≤30 years), 55% (&gt;30 years)
              <br />• HDB Resale: 75% (≤30 years), 55% (&gt;30 years) - MAS guidelines
              <br />• Other HDB: 90% (≤30 years), 80% (&gt;30 years)
              <br />
              <br /><strong>Note:</strong> LTV ratios are reduced when loan tenure exceeds 30 years per MAS regulations.
            </Typography>
            <Typography variant="body2" paragraph sx={{ mt: 1 }}>
              <strong>Max Purchase Price:</strong> Max Loan ÷ LTV Ratio
            </Typography>
            <Typography variant="body2" paragraph>
              This is the theoretical maximum property price based on your loan eligibility (TDSR/MSR limits). It represents what you can borrow, not necessarily what you can afford with your current capital.
            </Typography>
            <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                Source: MAS Loan Tenure and LTV Limits
              </Typography>
              <Typography variant="caption" component="div">
                <a href="https://www.mas.gov.sg/regulation/explainers/new-housing-loans/loan-tenure-and-loan-to-value-limits" target="_blank" rel="noopener noreferrer" style={{ color: 'primary.main', textDecoration: 'underline' }}>
                  MAS Loan Tenure and LTV Limits
                </a>
              </Typography>
            </Box>
            <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                Example Calculation:
              </Typography>
              <Typography variant="caption" component="div">
                Given: Max Loan = $562,352, LTV = 75%
                <br />
                <br />
                <strong>Calculation:</strong>
                <br />Max Purchase Price = $562,352 ÷ 0.75 = $749,803
                <br />
                <br />
                <em>Note: This shows your loan capacity. To check if you have sufficient capital for the downpayment (5% cash + 20% remaining) and stamp duty, refer to the "Difference" column. A positive difference means you can afford it; negative means you need more capital.</em>
              </Typography>
            </Box>
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              <strong>5% Cash Downpayment:</strong> Purchase Price × 5% (must be paid in cash)
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Remaining Downpayment:</strong> Max Purchase Price × 20%
            </Typography>
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                Source: MAS Property Financing Rules
              </Typography>
              <Typography variant="caption" component="div">
                <a href="https://www.mas.gov.sg/regulation/banking/notices-and-circulars" target="_blank" rel="noopener noreferrer" style={{ color: 'primary.main', textDecoration: 'underline' }}>
                  View MAS Banking Regulations (Notice 645)
                </a>
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Stamp Duty Calculations
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Buyer's Stamp Duty (BSD):</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              • First $180,000: 1%
              <br />• Next $180,000: 2%
              <br />• Next $640,000: 3%
              <br />• Next $500,000: 4%
              <br />• Next $1,500,000: 5%
              <br />• Remaining amount: 6%
            </Typography>
            <Typography variant="body2" paragraph sx={{ mt: 1 }}>
              Residential rates on or after 15 Feb 2023. BSD is rounded down to the nearest dollar (minimum $1).
            </Typography>
            <Typography variant="body2" paragraph sx={{ mt: 1 }}>
              <strong>Additional Buyer Stamp Duty (ABSD):</strong> Purchase Price × ABSD Rate, shown
              in its own column. The rate is derived from the selected buyer profile(s) — citizenship
              (with FTA remission) and number of properties owned — including IRAS married-couple
              remission for a joint purchase. A manual rate set in Settings overrides the derived rate.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Total Stamp Duty:</strong> BSD + ABSD (both added to Total Outlay)
            </Typography>
            <Box sx={{ mt: 1, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                Source: IRAS Buyer's Stamp Duty (BSD)
              </Typography>
              <Typography variant="caption" component="div">
                <a href="https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/buyer's-stamp-duty-(bsd)" target="_blank" rel="noopener noreferrer" style={{ color: 'primary.main', textDecoration: 'underline' }}>
                  View IRAS BSD rates
                </a>
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Affordability Calculations
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Total Capital:</strong> CPF OA Accumulated + Cash Savings Accumulated + CPF Refund From Sales + Cash Proceeds From Sale
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Total Outlay:</strong> 5% Cash Downpayment + Remaining Downpayment + Stamp
              Duty (BSD) + ABSD
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Difference:</strong> Total Capital - Total Outlay
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Affordable Today:</strong> The highest whole-dollar property price P
              satisfying all three conditions: (1) 0.05 × P ≤ Cash Savings — the mandatory 5%
              cash-only downpayment tranche (bank loans); (2) Downpayment(P) + BSD(P) + ABSD(P) ≤ Total
              Capital — where Downpayment(P) = P − min(Max Loan, LTV × P), BSD uses residential
              rates from 15 Feb 2023 (1–6%, rounded down), and ABSD = P × ABSD rate; (3) P ≤ Max Loan
              + Total Capital. For HDB loans, condition (1) is waived as the full downpayment may be
              funded with CPF.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Other Notes & Assumptions
            </Typography>
            <Typography variant="body2" paragraph>
              • Loan tenure is capped at maximum tenure: {propertyType === 'HDB'
                  ? '25 years for HDB Resale (conservative cap, applies to both HDB loans and bank loans)'
                  : '30 years for Private/EC'
                } or until age 65, whichever is shorter
            </Typography>
            <Typography variant="body2" paragraph>
              • Interest rate assumption: 3.5% per annum (standard mortgage rate)
            </Typography>
            <Typography variant="body2" paragraph>
              • Salary increment is applied annually at the specified percentage
            </Typography>
            <Typography variant="body2" paragraph>
              • CPF Ordinary Wage ceiling: $8,000 per month from 1 January 2026
            </Typography>
            <Typography variant="body2" paragraph>
              • CPF OA earns 2.5% p.a. interest following the CPF method: interest is computed monthly
                and credited (compounded) yearly. The accumulated balance earns a full year of interest,
                and each monthly contribution earns interest from the month after it is received.
            </Typography>
            <Typography variant="body2" paragraph>
              • Red highlighting indicates cash components that must be paid in cash
            </Typography>
            <Typography variant="body2">
              • Green highlighting indicates affordability (positive difference)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Source Viewer Dialog */}
      <Dialog 
        open={sourceViewerOpen} 
        onClose={() => setSourceViewerOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Source Document</Typography>
            <Box>
              <Button
                href={sourceViewerUrl}
                download
                target="_blank"
                variant="contained"
                size="small"
                sx={{ mr: 1 }}
              >
                Download
              </Button>
              <IconButton onClick={() => setSourceViewerOpen(false)} size="small">
                <Typography>✕</Typography>
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
            <iframe
              src={sourceViewerUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: 'calc(90vh - 100px)',
              }}
              title="Source Document Viewer"
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Tutorial Dialog */}
      <Dialog 
        open={tutorialOpen} 
        onClose={() => {
          setTutorialOpen(false);
          setDialogMinimized(false);
          setPointerVisible(false);
          setPointerTarget(null);
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            maxHeight: '90vh',
            transition: 'all 0.3s ease-in-out',
            opacity: dialogMinimized ? 0.3 : 1,
            transform: dialogMinimized ? 'scale(0.85) translateY(-20px)' : 'scale(1) translateY(0)',
            pointerEvents: dialogMinimized ? 'none' : 'auto',
          }
        }}
        BackdropProps={{
          sx: {
            transition: 'all 0.3s ease-in-out',
            opacity: dialogMinimized ? 0.1 : 0.5,
            backgroundColor: dialogMinimized ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="div">
              Super Calculator Tutorial
            </Typography>
            <Box>
              <Tooltip title="Download User Manual">
                <IconButton 
                  onClick={downloadUserManual} 
                  color="primary"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setTutorialOpen(false)} size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={tutorialStep} orientation="vertical">
            {tutorialSteps.map((step, index) => (
              <Step 
                key={index}
                completed={completedSteps.has(index)}
                sx={{
                  '& .MuiStepLabel-root .Mui-completed': {
                    color: 'success.main',
                  },
                }}
              >
                <StepLabel
                  StepIconComponent={() => (
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {completedSteps.has(index) ? (
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                      ) : index === tutorialStep ? (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'primary.main',
                            backgroundColor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'grey.400',
                            backgroundColor: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Box>
                      )}
                    </Box>
                  )}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {step.title}
                    {completedSteps.has(index) && (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, ml: 0.5 }} />
                    )}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-line',
                      mb: 2,
                      lineHeight: 1.8
                    }}
                  >
                    {step.content}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {step.targetElement && index === tutorialStep && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleShowArrow}
                        size="small"
                        startIcon={<ZoomInIcon />}
                        sx={{ 
                          borderStyle: 'dashed',
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        Show on Page
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (index < tutorialSteps.length - 1) {
                          setTutorialStep(index + 1);
                          updateTutorialProgress(index + 1, false);
                        } else {
                          updateTutorialProgress(tutorialSteps.length, true); // Mark as completed
                          setTutorialOpen(false);
                        }
                      }}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    >
                      {index < tutorialSteps.length - 1 ? 'Next' : 'Finish'}
                    </Button>
                    {index > 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setTutorialStep(index - 1);
                          updateTutorialProgress(index - 1, false);
                        }}
                        size="small"
                        sx={{ flexShrink: 0 }}
                      >
                        Previous
                      </Button>
                    )}
                    <Button
                      variant="text"
                      onClick={() => setTutorialOpen(false)}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    >
                      Skip Tutorial
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
          {tutorialStep === tutorialSteps.length && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                🎉 Congratulations!
              </Typography>
              <Typography variant="body1" paragraph>
                You've completed the tutorial! You're now ready to use Super Calculator 
                to plan your property purchase.
              </Typography>
              <Typography variant="body2" paragraph>
                Don't forget: You can download the complete User Manual anytime using 
                the download button in the tutorial header.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  updateTutorialProgress(tutorialSteps.length, true); // Mark as completed
                  setTutorialOpen(false);
                  setTutorialStep(0);
                }}
                sx={{ mt: 1 }}
              >
                Get Started
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              Step {tutorialStep + 1} of {tutorialSteps.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                onClick={handleRestartTutorial}
                startIcon={<ReplayIcon />}
                size="small"
                sx={{ flexShrink: 0 }}
                color="secondary"
              >
                Restart
              </Button>
              <Button
                onClick={() => {
                  const newStep = Math.max(0, tutorialStep - 1);
                  setTutorialStep(newStep);
                  updateTutorialProgress(newStep, false);
                }}
                disabled={tutorialStep === 0}
                size="small"
                sx={{ flexShrink: 0 }}
              >
                Previous
              </Button>
              <Button
                onClick={() => {
                  if (tutorialStep < tutorialSteps.length - 1) {
                    const newStep = tutorialStep + 1;
                    setTutorialStep(newStep);
                    updateTutorialProgress(newStep, false);
                  } else {
                    updateTutorialProgress(tutorialSteps.length, true); // Mark as completed
                    setTutorialOpen(false);
                    setTutorialStep(0);
                  }
                }}
                variant="contained"
                size="small"
                sx={{ flexShrink: 0 }}
              >
                {tutorialStep < tutorialSteps.length - 1 ? 'Next' : 'Finish'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Tutorial Pointer Overlay */}
      {pointerTarget && pointerVisible && (
        <Portal>
          <TutorialPointer
            selector={pointerTarget.selector}
            position={pointerTarget.position}
          />
        </Portal>
      )}

      <Snackbar
        open={historyToast.open}
        autoHideDuration={historyToast.severity === 'error' ? 8000 : 2500}
        onClose={() => setHistoryToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setHistoryToast((t) => ({ ...t, open: false }))}
          severity={historyToast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {historyToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Tutorial Pointer Component
interface TutorialPointerProps {
  selector: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const TutorialPointer: React.FC<TutorialPointerProps> = ({ selector, position }) => {
  const [coords, setCoords] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  useEffect(() => {
    const updatePosition = () => {
      const element = document.querySelector(selector);
      if (!element) {
        setCoords(null);
        return;
      }
      element.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      const rect = element.getBoundingClientRect();
      setCoords({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        width: rect.width,
        height: rect.height,
      });
    };

    updatePosition();
    const raf = requestAnimationFrame(() => updatePosition());
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [selector]);

  if (!coords) return null;

  const highlightStyle: React.CSSProperties = {
    left: `${coords.x - coords.width / 2}px`,
    top: `${coords.y - coords.height / 2}px`,
    width: `${coords.width}px`,
    height: `${coords.height}px`,
  };

  return (
    <>
      {/* Highlight overlay */}
      <Box
        sx={{
          position: 'fixed',
          ...highlightStyle,
          border: '3px solid #6366F1',
          borderRadius: '8px',
          backgroundColor: 'rgba(99, 102, 241, 0.15)',
          pointerEvents: 'none',
          zIndex: 9998,
          animation: 'pulse 2s ease-in-out infinite, fadeIn 0.3s ease-in',
          '@keyframes pulse': {
            '0%, 100%': {
              boxShadow: '0 0 0 0 rgba(99, 102, 241, 0.7)',
            },
            '50%': {
              boxShadow: '0 0 0 15px rgba(99, 102, 241, 0)',
            },
          },
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      />
    </>
  );
};

export default PurchasePlanner;
